/**
 * Dynamic Interface Language & Translation Engine
 * Supports seamless real-time language switching with fallback to English.
 */

export type SupportedLanguage = 'en' | 'hi' | 'gu' | 'mr' | 'es' | 'fr' | 'de' | 'ar';

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

export const LANGUAGE_STORAGE_KEY = 'flipbook_user_language';
export const LANGUAGE_CHANGE_EVENT = 'flipbook:language-change';

export const SUPPORTED_LANGUAGES: Array<{
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  dir: 'ltr' | 'rtl';
}> = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', dir: 'ltr' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी', dir: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', dir: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', dir: 'ltr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
];

export const translations: Record<SupportedLanguage, Record<string, string>> = {
  en: {
    // Navigation & Page Titles
    'nav.dashboard': 'Dashboard',
    'nav.flipbook_library': 'Flipbook Library',
    'nav.access_history': 'Access History',
    'nav.books_approval': 'Books Approval',
    'nav.organizations': 'Organizations',
    'nav.user_management': 'User Management',
    'nav.access_logs': 'Access Logs',
    'nav.masters': 'Masters',
    'nav.subject_master': 'Subject Master',
    'nav.grade_master': 'Grade Master',
    'nav.settings': 'Settings',

    // Page Subtitles
    'library.subtitle': 'Manage and view all digital books',
    'dashboard.subtitle': 'Overview of digital books, reader engagement, and analytics',
    'access_history.subtitle': 'View reader session logs and access events',
    'books_approval.subtitle': 'Review and process submitted book approvals',
    'organizations.subtitle': 'Manage organizations, themes, and permissions',
    'user_management.subtitle': 'Manage user accounts, roles, and assignments',
    'access_logs.subtitle': 'Monitor and audit user activity across organizations',
    'masters.subjects_subtitle': 'Manage academic subjects for flipbooks',
    'masters.grades_subtitle': 'Manage academic grades and standards',
    'masters.add_subject': 'Add Subject',
    'masters.edit_subject': 'Edit Subject',
    'masters.create_subject': 'Create Subject',
    'masters.update_subject': 'Update Subject',
    'masters.delete_subject_title': 'Delete Subject?',
    'masters.delete_subject_desc': 'This will permanently delete',
    'masters.delete_subject_hint': 'Subjects assigned to books cannot be deleted; deactivate them instead.',
    'masters.subject_name': 'Subject Name',
    'masters.enter_subject_name': 'Enter subject name',
    'masters.subject_active_desc': 'Available when selecting a subject for a book',
    'masters.subject_inactive_desc': 'Hidden from book subject selection',
    'masters.no_subjects_found': 'No subjects found',
    'masters.loading_subjects': 'Loading subjects...',
    'masters.try_different_search': 'Try a different search term.',
    'masters.add_first_subject': 'Add your first subject to get started.',
    'masters.subject_name_required': 'Subject name is required',
    'masters.subject_created_success': 'Subject created successfully',
    'masters.subject_updated_success': 'Subject updated successfully',
    'masters.subject_deleted_success': 'Subject deleted successfully',
    'masters.failed_load_subjects': 'Failed to load subjects',
    'masters.failed_save_subject': 'Failed to save subject',
    'masters.failed_delete_subject': 'Failed to delete subject',
    'masters.add_grade': 'Add Grade',
    'masters.edit_grade': 'Edit Grade',
    'masters.create_grade': 'Create Grade',
    'masters.update_grade': 'Update Grade',
    'masters.delete_grade_title': 'Delete Grade?',
    'masters.delete_grade_desc': 'This will permanently delete',
    'masters.delete_grade_hint': 'Grades assigned to books cannot be deleted; deactivate them instead.',
    'masters.grade_name': 'Grade Name',
    'masters.enter_grade_name': 'Enter grade name',
    'masters.grade_active_desc': 'Available when selecting a grade for a book',
    'masters.grade_inactive_desc': 'Hidden from book grade selection',
    'masters.no_grades_found': 'No grades found',
    'masters.loading_grades': 'Loading grades...',
    'masters.add_first_grade': 'Add your first grade to get started.',
    'masters.grade_name_required': 'Grade name is required',
    'masters.grade_created_success': 'Grade created successfully',
    'masters.grade_updated_success': 'Grade updated successfully',
    'masters.grade_deleted_success': 'Grade deleted successfully',
    'masters.failed_load_grades': 'Failed to load grades',
    'masters.failed_save_grade': 'Failed to save grade',
    'masters.failed_delete_grade': 'Failed to delete grade',
    'masters.order_already_used': 'Order is already used. Please choose a different order.',
    'filter.search_subjects': 'Search subjects...',
    'filter.search_grades': 'Search grades...',
    'action.deleting': 'Deleting...',

    // Roles
    'role.super_admin': 'Super Admin',
    'role.admin': 'Admin',
    'role.admins': 'Admins',
    'role.school_admin': 'School Admin',
    'role.approver_l1': 'Approver L1',
    'role.approver_l2': 'Approver L2',
    'role.publish_approver_l1': 'Publish Approver Level 1',
    'role.publish_approver_l2': 'Publish Approver Level 2',
    'role.delete_approver_l1': 'Delete Approver Level 1',
    'role.delete_approver_l2': 'Delete Approver Level 2',
    'role.content_creator': 'Content Creator',
    'role.reader': 'Reader',
    'role.student': 'Student',
    'role.teacher': 'Teacher',

    // User Management Modals & Tables
    'user_management.admins': 'Admins',
    'user_management.delete_user': 'Delete User',
    'user_management.delete_user_desc': 'Are you sure you want to delete',
    'user_management.cannot_undone': 'This action cannot be undone.',
    'user_management.add_new_user': 'Add New User',
    'user_management.edit_user': 'Edit User',
    'user_management.create_user_desc': 'Create a new user account and assign module access',
    'user_management.edit_user_desc': 'Update user details and module permissions',
    'user_management.basic_info': 'Basic Information',
    'user_management.full_name': 'Full Name',
    'user_management.email_address': 'Email Address',
    'user_management.password': 'Password',
    'user_management.new_password_keep': 'New Password (leave blank to keep)',
    'user_management.grade_optional': 'Grade / Class (optional)',
    'user_management.not_applicable': 'Not Applicable',
    'user_management.organizations_assignment': 'Organizations Assignment',
    'user_management.module_access': 'Module Access',
    'user_management.module_access_desc': 'Defaults set by role — customise as needed',
    'user_management.force_login': 'Force Login',
    'user_management.otp_history': 'OTP History',
    'user_management.all_orgs': 'All Orgs',
    'user_management.none': 'None',

    // Access Log Actions & Groups
    'log.group_user': 'User',
    'log.group_books': 'Books',
    'log.group_other': 'Other',
    'log.no_logs_found': 'No logs found.',
    'log.force_login_by': 'Force Login by',
    'log.performed_by': 'Performed By',
    'log.target': 'Target',
    'log.filter_by_action': 'Filter by action',
    'log.action_login': 'Login',
    'log.action_logout': 'Logout',
    'log.action_generate_login_otp': 'Generate Login OTP',
    'log.action_verify_login_otp': 'Verify Login OTP',
    'log.action_generate_reset_otp': 'Generate Reset OTP',
    'log.action_verify_reset_otp': 'Verify Reset OTP',
    'log.action_reset_password': 'Reset Password',
    'log.action_view_book': 'View Book',
    'log.action_generate_otp': 'Generate OTP',
    'log.action_verify_otp': 'Verify OTP',
    'log.action_upload_book': 'Upload Book',
    'log.action_update_book': 'Update Book',
    'log.action_publish_book': 'Publish Book',
    'log.action_delete_book': 'Delete Book',
    'log.action_toggle_otp': 'Toggle OTP',
    'log.action_add_access_user': 'Add Access User',
    'log.action_remove_access_user': 'Remove Access User',
    'log.action_create_user': 'Create User',
    'log.action_update_user': 'Update User',
    'log.action_delete_user': 'Delete User',
    'log.action_activate_version': 'Activate Version',
    'log.action_create_qr': 'Create QR',
    'log.action_toggle_qr': 'Toggle QR',
    'log.action_delete_qr': 'Delete QR',
    'log.action_scan_qr': 'Scan QR',

    // Global Header & User Menu
    'header.search_placeholder': 'Search books, subjects, grades...',
    'header.switch_organization': 'Switch Organization',
    'header.all_organizations': 'All Organizations',
    'header.global_overview': 'Global Overview',
    'header.all_assigned_orgs': 'All Assigned Organizations',
    'header.profile': 'My Profile',
    'header.logout': 'Sign Out',
    'header.super_admin': 'Super Admin',
    'header.admin': 'Admin',
    'header.notifications': 'Notifications',
    'notifications.new': 'New',
    'notifications.all': 'All',
    'notifications.unread': 'Unread',
    'notifications.approvals': 'Approvals',
    'notifications.books': 'Books',
    'notifications.alerts': 'Alerts',
    'notifications.mark_all_read': 'Mark all read',
    'notifications.clear_read': 'Clear read',
    'notifications.view_approvals': 'View all approvals',
    'notifications.empty': 'No notifications found',
    'notifications.caught_up': "You're all caught up!",
    'notifications.loading': 'Loading notifications...',

    // Actions & Buttons
    'action.search': 'Search',
    'action.filter': 'Filter',
    'action.reset': 'Reset',
    'action.clear_all': 'Clear All',
    'action.clear_filters': 'Clear filters',
    'action.save': 'Save Changes',
    'action.saving': 'Saving...',
    'action.cancel': 'Cancel',
    'action.edit': 'Edit',
    'action.delete': 'Delete',
    'action.actions': 'Actions',
    'action.upload': 'Upload',
    'action.upload_book': 'Upload Book',
    'action.add_user': 'Add User',
    'action.add_organization': 'Add Organization',
    'action.create_organization': 'Create Organization',
    'action.add_subject': 'Add Subject',
    'action.add_grade': 'Add Grade',
    'action.create': 'Create',
    'action.status': 'Status',
    'action.back': 'Back',
    'action.confirm': 'Confirm',
    'action.close': 'Close',
    'action.send': 'Send',
    'action.sending': 'Sending...',
    'action.copy_link': 'Copy Link',
    'action.copy': 'Copy',
    'action.copied': 'Copied!',
    'action.view': 'View',
    'action.preview': 'Preview',
    'action.approve': 'Approve',
    'action.approving': 'Approving...',
    'action.send_back': 'Send Back',
    'action.reject': 'Reject',
    'action.history': 'History',
    'action.versions': 'Versions',
    'action.version_history': 'Version History',
    'action.replace': 'Replace',
    'action.replace_book': 'Replace Book',
    'action.qr_code': 'QR Code',
    'action.download_qr': 'Download QR Code',
    'action.download_pdf': 'Download PDF',
    'action.refresh': 'Refresh',
    'action.export': 'Export',
    'action.export_csv': 'Export CSV',
    'action.import': 'Import',
    'action.view_all': 'View All',
    'action.show_more': 'Show More',
    'action.collapse': 'Collapse',
    'action.expand': 'Expand',
    'action.submit_l2': 'Submit for Level 2',
    'action.publish': 'Publish',
    'action.publishing': 'Publishing...',
    'action.activate': 'Activate',
    'action.deactivate': 'Deactivate',
    'action.archive': 'Archive',
    'action.edit_book': 'Edit Book',
    'action.delete_book': 'Delete Book',
    'action.request_delete': 'Request Delete',
    'action.no_books_found': 'No books found',
    'action.try_adjusting_filters': 'Try adjusting your search or filter criteria',
    'action.upload_first_book': 'Upload your first book',
    'action.select_all': 'Select all',
    'action.clear_selection': 'Clear selection',
    'action.by': 'by',

    // Pagination
    'pagination.showing': 'Showing',
    'pagination.of': 'of',
    'pagination.rows_per_page': 'Rows per page',
    'pagination.previous_page': 'Previous page',
    'pagination.next_page': 'Next page',
    'pagination.books': 'books',
    'pagination.logs': 'logs',
    'pagination.records': 'records',
    'pagination.entries': 'entries',
    'pagination.items': 'items',
    'pagination.prev': 'Prev',
    'pagination.next': 'Next',

    // Filter labels
    'filter.all_statuses': 'All Statuses',
    'filter.all_grades': 'All Grades',
    'filter.all_subjects': 'All Subjects',
    'filter.all_roles': 'All Roles',
    'filter.all_actions': 'All Actions',
    'filter.search_books': 'Search title, subject, uploader...',
    'filter.search_users': 'Search name, email, role...',
    'filter.search_logs': 'Search logs, users, actions...',
    'filter.search_organizations': 'Search organizations...',
    'filter.search_reviewers': 'Search reviewers...',
    'filter.search_approvals': 'Search title, requester, grade, subject...',

    // Statuses
    'status.active': 'Active',
    'status.inactive': 'Inactive',
    'status.draft': 'Draft',
    'status.published': 'Published',
    'status.processing': 'Processing',
    'status.pending_approval': 'Pending Approval',
    'status.pending_l1': 'Pending Approval L1',
    'status.pending': 'Pending',
    'status.ready_for_l2': 'Ready for L2',
    'status.awaiting_l2': 'Awaiting L2',
    'status.pending_l2': 'Pending Approval L2',
    'status.approved': 'Approved',
    'status.pending_publish': 'Pending Publish',
    'status.archived': 'Archived',
    'status.expired': 'Expired',
    'status.rejected': 'Sent Back',
    'status.cancelled': 'Cancelled',
    'status.success': 'Success',
    'status.failed': 'Failed',
    'status.all': 'All',

    // Approvals, Reviews & Reviewers
    'approval.total_requests': 'Total Requests',
    'approval.book_approval': 'Book Approval',
    'approval.delete_requests': 'Delete Requests',
    'approval.level_1': 'Level 1',
    'approval.level_2': 'Level 2',
    'approval.approval': 'Approval',
    'approval.reviewers_l1': 'Level 1 reviewers',
    'approval.reviewers_l2': 'Level 2 reviewers',
    'approval.delete_reviewers_l1': 'Level 1 delete reviewers',
    'approval.delete_reviewers_l2': 'Level 2 delete reviewers',
    'approval.replacement_reviewers_l1': 'Level 1 replacement reviewers',
    'approval.replacement_reviewers_l2': 'Level 2 replacement reviewers',
    'approval.publishers': 'Publishers',
    'approval.responsible_persons': 'Responsible persons',
    'approval.selected': 'selected',
    'approval.no_reviewers_found': 'No reviewers found',
    'approval.no_matches': 'No matches',
    'approval.select_l1_desc': 'Select Level 1 approvers. Any one approval moves this book to Level 2.',
    'approval.select_l2_desc': 'Select Level 2 approvers. Any one approval publishes this book automatically.',
    'approval.sent_back_for_improvement': 'Sent back for improvement',
    'approval.no_feedback_note': 'No feedback note was provided. Replace the PDF or edit details, then it will be reassigned automatically.',
    'approval.delete_pending': 'Delete pending',
    'approval.ready_for_l2_delete': 'Ready for L2 delete',
    'approval.replacement_pending': 'Replacement pending',
    'approval.replacement': 'Replacement',
    'approval.no_approvals': 'No pending approvals',
    'approval.no_delete_requests': 'No delete requests',
    'approval.review_book': 'Review Book',
    'approval.review_book_subtitle': 'View the flipbook, then approve or reject',
    'approval.loading_book': 'Loading book...',
    'approval.request_not_found': 'Approval request not found',
    'approval.why_approved': 'Why approved',
    'approval.send_back_for_improvement': 'Send back for improvement',
    'approval.pdf_not_available': 'PDF not available',
    'approval.pdf_not_available_desc': 'This book does not have a viewable PDF yet.',
    'approval.book_preview': 'Book Preview',
    'approval.replacement_preview_hint': 'Replacement PDF — current published version stays live until approved',
    'approval.loading_preview': 'Loading preview...',
    'approval.book_not_found': 'Book not found',
    'approval.note_from_sender': 'Note from sender',
    'approval.publish_now_desc': 'This book is already Level 2 approved. Publishing will make it available in the library immediately.',
    'action.send_delete_l2': 'Send for Level 2 Delete',
    'action.send_for': 'Send for',
    'action.back_to_list': 'Back to list',

    // Grades & Standards
    'grade.nursery': 'Nursery',
    'grade.lkg': 'LKG',
    'grade.ukg': 'UKG',
    'grade.grade_prefix': 'Grade',

    // Common Subjects
    'subject.mathematics': 'Mathematics',
    'subject.economics': 'Economics',
    'subject.science': 'Science',
    'subject.english': 'English',
    'subject.hindi': 'Hindi',
    'subject.gujarati': 'Gujarati',
    'subject.marathi': 'Marathi',
    'subject.physics': 'Physics',
    'subject.chemistry': 'Chemistry',
    'subject.biology': 'Biology',
    'subject.history': 'History',
    'subject.geography': 'Geography',
    'subject.computer_science': 'Computer Science',

    // Organizations Modals & Actions
    'org.create_organization': 'Create New Organization',
    'org.edit_organization': 'Edit Organization',
    'org.configure_desc': 'Configure organization details, colors, and branding logo',
    'org.activate_organization': 'Activate Organization',
    'org.deactivate_organization': 'Deactivate Organization',

    // Book Upload & Edit
    'action.submit_for_approval': 'Submit for Approval',
    'action.save_changes': 'Save Changes',
    'upload.page_title': 'Upload New Book',
    'upload.edit_page_title': 'Edit Book',
    'upload.subtitle': 'Convert a PDF into an interactive HTML5 flipbook',
    'upload.edit_subtitle': 'Update book details or replace the PDF flipbook source',
    'upload.pdf_source_file': 'PDF Source File',
    'upload.optional_replace': 'Optional — upload only to replace',
    'upload.drop_pdf': 'Drop your PDF here, or',
    'upload.browse': 'browse',
    'upload.pdf_limit': 'PDF files only — max {size} MB per upload',
    'upload.thumbnail_title': 'Book Thumbnail',
    'upload.drop_image': 'Drop your image here, or',
    'upload.image_limit': 'JPG, PNG, or WebP — max 2 MB · leave empty to use PDF page 1',
    'upload.current_thumbnail': 'Current thumbnail on file',
    'upload.replace_image': 'Replace image',
    'upload.replace_pdf': 'Replace PDF',
    'upload.org_title': 'Organization',
    'upload.org_edit_desc': 'Organization cannot be changed after the book is created.',
    'upload.org_desc': 'Select which organization this book belongs to.',
    'upload.book_info': 'Book Information',
    'upload.book_name': 'Book Name',
    'upload.book_name_desc': 'Use the full official title as it will appear in the flipbook viewer',
    'upload.book_name_placeholder': 'e.g. Mathematics Foundations — Grade 5, Term 1',
    'upload.publisher': 'Publisher',
    'upload.publisher_desc': 'Publishing house or content source for this book',
    'upload.grade': 'Grade',
    'upload.grade_desc': 'The target grade for this book',
    'upload.subject': 'Subject',
    'upload.subject_desc': 'Academic subject category',
    'upload.expiry_date': 'Expiry Date',
    'upload.expiry_date_desc': 'Flipbook becomes inaccessible after this date.',
    'upload.description': 'Description',
    'upload.description_desc': 'Brief summary shown to prospective parents and schools before they request access',
    'upload.description_placeholder': 'Describe the book content, target audience, and key topics covered...',
    'upload.access_control': 'Access Control',
    'upload.access_notes': 'Access Notes',
    'upload.access_notes_desc': 'Internal notes about who should have access to this book (not shown to public users)',
    'upload.approval_banner_title': '2-Level Approval Required',
    'upload.approval_banner_desc': 'Submit for approval sends this book to all Level 1 approvers. After any one Level 1 approval, it is sent automatically to all Level 2 approvers. Once Level 2 approves, the book is published. If a book is sent back for improvement, replace or edit it — it is reassigned automatically.',
    'upload.submitted_notice': 'Submitted books go to all Level 1 approvers, then Level 2',
    'upload.live_notice': 'The current published version stays live until Level 2 approves the new PDF',
    'upload.save_notice': 'Save metadata anytime. Replacing the PDF on a published book requires approval.',

    // Common Table Columns
    'col.book_title': 'Book Title',
    'col.grade': 'Grade',
    'col.subject': 'Subject',
    'col.status': 'Status',
    'col.pages': 'Pages',
    'col.uploaded': 'Uploaded',
    'col.uploaded_by': 'Uploaded By',
    'col.expiry': 'Expiry',
    'col.views': 'Views',
    'col.link': 'Link',
    'col.name': 'Name',
    'col.email': 'Email',
    'col.role': 'Role',
    'col.organization': 'Organization',
    'col.permissions': 'Permissions',
    'col.created_date': 'Created Date',
    'col.last_login': 'Last Login',
    'col.ip_address': 'IP Address',
    'col.device': 'Device',
    'col.action': 'Action',
    'col.resource': 'Resource',
    'col.timestamp': 'Timestamp',
    'col.details': 'Details',
    'col.order': 'Order',
    'col.requested_by': 'Requested By',
    'col.sent_at': 'Sent At',
    'col.level': 'Level',
    'col.approval': 'Approval',

    // Dashboard & Metrics
    'dashboard.title': 'Dashboard',
    'dashboard.total_books': 'Total Books',
    'dashboard.total_users': 'Total Users',
    'dashboard.total_views': 'Total Book Views',
    'dashboard.active_users': 'Active Users',
    'dashboard.avg_read_time': 'Avg. Read Time',
    'dashboard.active_flipbooks': 'Active Flipbooks',
    'dashboard.expired_today': 'Expired Today',
    'dashboard.otp_generated': 'OTP Requests (24h)',
    'dashboard.processing_queue': 'Processing Queue',
    'dashboard.storage_used': 'PDF Storage Used',
    'dashboard.published_books': 'Published Books',
    'dashboard.draft_books': 'Draft Books',
    'dashboard.expired_books': 'Expired Books',
    'dashboard.recent_activity': 'Recent Activity',
    'dashboard.top_performing': 'Top Performing Books',
    'dashboard.views_trend': 'Views Trend',
    'dashboard.books_by_grade': 'Books by Grade',
    'dashboard.books_by_subject': 'Books by Subject',
    'dashboard.trends_distribution': 'Trends & Distribution',
    'dashboard.otp_trend': 'OTP Request Trend',
    'dashboard.otp_trend_sub': 'Last 14 days · published flipbook access',
    'dashboard.books_by_grade_sub': 'Distribution across all grades',
    'dashboard.latest_events': 'Latest events across the library',
    'dashboard.today': 'Today',
    'dashboard.distribution_subjects': 'Distribution across subjects',
    'dashboard.analytics_heading': 'Analytics — Book views, flipbook-access OTPs & user insights',
    'dashboard.monthly_activity_trend': 'Monthly Activity Trend',
    'dashboard.views_by_subject': 'Views by Subject',
    'dashboard.books_by_grade_group': 'Books by Grade Group',
    'dashboard.unique_otp_users': 'Unique OTP Users',
    'dashboard.this_month': 'this month',
    'dashboard.no_recent_activity': 'No recent activity yet',
    'dashboard.no_subject_data': 'No subject view data yet',
    'dashboard.no_grade_data': 'No grade data yet',
    'dashboard.no_books_yet': 'No books yet',
    'dashboard.page_title': 'Dashboard & Analytics',
    'dashboard.main_subtitle': 'Library overview, activity metrics, and usage analytics',
    'dashboard.needs_renewal': 'Needs renewal',
    'dashboard.none_today': 'None today',
    'dashboard.vs_previous_24h': 'vs previous 24h',
    'dashboard.pdf_processing_pending': 'PDF processing pending',
    'dashboard.queue_clear': 'Queue clear',
    'dashboard.from_uploaded_books': 'From uploaded book files',
    'dashboard.all_time': 'All time',
    'dashboard.last_7_days': 'Last 7 days',
    'dashboard.per_session': 'Per session',
    'dashboard.monthly_activity_sub': 'Book views, flipbook-access OTPs, and active users',
    'dashboard.grades_count': 'grades',
    'dashboard.grade_single': 'grade',
    'dashboard.otp_requests': 'OTP Requests',
    'dashboard.books_count': 'Books',
    'dashboard.chart_views': 'Views',
    'dashboard.chart_otps': 'OTPs',
    'dashboard.chart_users': 'Users',
    'dashboard.chart_share': 'Share',
    'dashboard.action_required': 'Action',
    'dashboard.activity_upload': 'Book Uploaded',
    'dashboard.activity_otp': 'Access OTP',
    'dashboard.activity_expired': 'Book Expired',
    'dashboard.activity_view': 'Book View',
    'header.global_overview_desc': 'Global overview & aggregated data',
    'header.all_assigned_orgs_desc': 'All assigned organizations & aggregated data',
    'date.just_now': 'Just now',
    'date.yesterday': 'Yesterday',
    'date.m_ago': 'm ago',
    'date.h_ago': 'h ago',
    'date.d_ago': 'd ago',


    // Access History
    'history.visitors': 'Visitors',
    'history.view_events': 'View Events',
    'history.read_sessions': 'Read Sessions',
    'history.otp_history': 'OTP History',
    'history.reader_user': 'Reader / User',
    'history.total_read_time': 'Total Read Time',
    'history.first_access': 'First Access',
    'history.last_access': 'Last Access',
    'history.latest_ip': 'Latest IP',
    'history.books_accessed': 'Books Accessed',

    // Settings
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage your personal preferences and system configuration',
    'settings.tab_general': 'General Settings',
    'settings.tab_notifications': 'Notification Preferences',
    'settings.tab_security': 'Security Settings',
    'settings.tab_flipbook': 'Flipbook Settings',
    'settings.tab_email': 'Email Settings',
    'settings.interface_language': 'Interface Language',
    'settings.interface_language_desc': 'Select the display language for the application interface',
    'settings.timezone': 'Timezone',
    'settings.timezone_desc': 'Timestamps and dates will display in your selected timezone',
    'settings.admin_email': 'Admin Email',
    'settings.admin_email_desc': 'Primary contact email for system notifications',
    'settings.organization_name': 'Organization Name',
    'settings.save_success': 'Settings saved successfully',

    // Flipbook Viewer & Reader
    'reader.loading_flipbook': 'Loading flipbook…',
    'reader.loading_page': 'Loading page...',
    'reader.read_book': 'Read Book',
    'reader.thumbnails': 'Thumbnails',
    'reader.search': 'Search in book',
    'reader.zoom_in': 'Zoom In',
    'reader.zoom_out': 'Zoom Out',
    'reader.fullscreen': 'Fullscreen',
    'reader.exit_fullscreen': 'Exit Fullscreen',
    'reader.sound_on': 'Sound On',
    'reader.sound_off': 'Sound Off',
    'reader.auto_play': 'Auto Play',
    'reader.pause': 'Pause',
    'reader.download': 'Download PDF',
    'reader.bookmarks': 'Bookmarks',
    'reader.add_bookmark': 'Bookmark this page',
    'reader.remove_bookmark': 'Remove bookmark',
    'reader.page_of': 'of',
    'reader.protected_document': 'Protected Document',

    // Session Expiry
    'session.expired_title': 'Session Expired',
    'session.expired_desc': 'Your session has expired due to inactivity. Please sign in again to continue.',
    'session.sign_in_again': 'Sign In Again',
  },
  hi: {
    // Navigation & Page Titles
    'nav.dashboard': 'डैशबोर्ड',
    'nav.flipbook_library': 'फ्लिपबुक लाइब्रेरी',
    'nav.access_history': 'एक्सेस इतिहास',
    'nav.books_approval': 'किताब अनुमोदन',
    'nav.organizations': 'संगठन',
    'nav.user_management': 'उपयोगकर्ता प्रबंधन',
    'nav.access_logs': 'एक्सेस लॉग्स',
    'nav.masters': 'मास्टर्स',
    'nav.subject_master': 'विषय मास्टर',
    'nav.grade_master': 'कक्षा मास्टर',
    'nav.settings': 'सेटिंग्स',

    // Page Subtitles
    'library.subtitle': 'सभी डिजिटल पुस्तकों का प्रबंधन और अवलोकन करें',
    'dashboard.subtitle': 'डिजिटल पुस्तकों, पाठकों और विश्लेषण का अवलोकन',
    'access_history.subtitle': 'पाठक सत्र लॉग और एक्सेस ईवेंट देखें',
    'books_approval.subtitle': 'प्रस्तुत पुस्तक अनुमोदनों की समीक्षा और कार्रवाई करें',
    'organizations.subtitle': 'संगठन, थीम और अनुमतियां प्रबंधित करें',
    'user_management.subtitle': 'उपयोगकर्ता खाते, भूमिकाएं और कार्यभार प्रबंधित करें',
    'access_logs.subtitle': 'संगठनों में उपयोगकर्ता गतिविधियों की निगरानी और ऑडिट करें',
    'masters.subjects_subtitle': 'फ्लिपबुक्स के लिए शैक्षणिक विषय प्रबंधित करें',
    'masters.grades_subtitle': 'शैक्षणिक कक्षाएं और मानक प्रबंधित करें',
    'masters.add_subject': 'विषय जोड़ें',
    'masters.edit_subject': 'विषय संपादित करें',
    'masters.create_subject': 'विषय बनाएं',
    'masters.update_subject': 'विषय अपडेट करें',
    'masters.delete_subject_title': 'विषय हटाएं?',
    'masters.delete_subject_desc': 'यह स्थायी रूप से हटा देगा',
    'masters.delete_subject_hint': 'पुस्तकों को सौंपे गए विषयों को हटाया नहीं जा सकता; इसके बजाय उन्हें निष्क्रिय करें।',
    'masters.subject_name': 'विषय का नाम',
    'masters.enter_subject_name': 'विषय का नाम दर्ज करें',
    'masters.subject_active_desc': 'पुस्तक के लिए विषय चुनते समय उपलब्ध',
    'masters.subject_inactive_desc': 'पुस्तक विषय चयन से छिपा हुआ',
    'masters.no_subjects_found': 'कोई विषय नहीं मिला',
    'masters.loading_subjects': 'विषय लोड हो रहे हैं...',
    'masters.try_different_search': 'कृपया दूसरा खोज शब्द आज़माएं।',
    'masters.add_first_subject': 'शुरू करने के लिए अपना पहला विषय जोड़ें।',
    'masters.subject_name_required': 'विषय का नाम आवश्यक है',
    'masters.subject_created_success': 'विषय सफलतापूर्वक बनाया गया',
    'masters.subject_updated_success': 'विषय सफलतापूर्वक अपडेट किया गया',
    'masters.subject_deleted_success': 'विषय सफलतापूर्वक हटाया गया',
    'masters.failed_load_subjects': 'विषय लोड करने में विफल',
    'masters.failed_save_subject': 'विषय सहेजने में विफल',
    'masters.failed_delete_subject': 'विषय हटाने में विफल',
    'masters.add_grade': 'कक्षा जोड़ें',
    'masters.edit_grade': 'कक्षा संपादित करें',
    'masters.create_grade': 'कक्षा बनाएं',
    'masters.update_grade': 'कक्षा अपडेट करें',
    'masters.delete_grade_title': 'कक्षा हटाएं?',
    'masters.delete_grade_desc': 'यह स्थायी रूप से हटा देगा',
    'masters.delete_grade_hint': 'पुस्तकों को सौंपी गई कक्षाओं को हटाया नहीं जा सकता; इसके बजाय उन्हें निष्क्रिय करें।',
    'masters.grade_name': 'कक्षा का नाम',
    'masters.enter_grade_name': 'कक्षा का नाम दर्ज करें',
    'masters.grade_active_desc': 'पुस्तक के लिए कक्षा चुनते समय उपलब्ध',
    'masters.grade_inactive_desc': 'पुस्तक कक्षा चयन से छिपा हुआ',
    'masters.no_grades_found': 'कोई कक्षा नहीं मिली',
    'masters.loading_grades': 'कक्षाएं लोड हो रही हैं...',
    'masters.add_first_grade': 'शुरू करने के लिए अपनी पहली कक्षा जोड़ें।',
    'masters.grade_name_required': 'कक्षा का नाम आवश्यक है',
    'masters.grade_created_success': 'कक्षा सफलतापूर्वक बनाई गई',
    'masters.grade_updated_success': 'कक्षा सफलतापूर्वक अपडेट की गई',
    'masters.grade_deleted_success': 'कक्षा सफलतापूर्वक हटाई गई',
    'masters.failed_load_grades': 'कक्षाएं लोड करने में विफल',
    'masters.failed_save_grade': 'कक्षा सहेजने में विफल',
    'masters.failed_delete_grade': 'कक्षा हटाने में विफल',
    'masters.order_already_used': 'क्रम पहले से उपयोग में है। कृपया कोई दूसरा क्रम चुनें।',
    'filter.search_subjects': 'विषय खोजें...',
    'filter.search_grades': 'कक्षा खोजें...',
    'action.deleting': 'हटाया जा रहा है...',

    // Roles
    'role.super_admin': 'सुपर एडमिन',
    'role.admin': 'एडमिन',
    'role.admins': 'एडमिन',
    'role.school_admin': 'स्कूल एडमिन',
    'role.approver_l1': 'अनुमोदक L1',
    'role.approver_l2': 'अनुमोदक L2',
    'role.publish_approver_l1': 'प्रकाशन अनुमोदक स्तर 1',
    'role.publish_approver_l2': 'प्रकाशन अनुमोदक स्तर 2',
    'role.delete_approver_l1': 'हटाने का अनुमोदक स्तर 1',
    'role.delete_approver_l2': 'हटाने का अनुमोदक स्तर 2',
    'role.content_creator': 'कंटेंट क्रिएटर',
    'role.reader': 'पाठक',
    'role.student': 'छात्र',
    'role.teacher': 'शिक्षक',

    // User Management Modals & Tables
    'user_management.admins': 'एडमिन',
    'user_management.delete_user': 'उपयोगकर्ता हटाएं',
    'user_management.delete_user_desc': 'क्या आप वाकई हटाना चाहते हैं',
    'user_management.cannot_undone': 'यह क्रिया पूर्ववत नहीं की जा सकती।',
    'user_management.add_new_user': 'नया उपयोगकर्ता जोड़ें',
    'user_management.edit_user': 'उपयोगकर्ता संपादित करें',
    'user_management.create_user_desc': 'एक नया खाता बनाएं और मॉड्यूल एक्सेस सौंपें',
    'user_management.edit_user_desc': 'उपयोगकर्ता विवरण और मॉड्यूल अनुमतियां अपडेट करें',
    'user_management.basic_info': 'बुनियादी जानकारी',
    'user_management.full_name': 'पूरा नाम',
    'user_management.email_address': 'ईमेल पता',
    'user_management.password': 'पासवर्ड',
    'user_management.new_password_keep': 'नया पासवर्ड (रखने के लिए खाली छोड़ें)',
    'user_management.grade_optional': 'कक्षा / ग्रेड (वैकल्पिक)',
    'user_management.not_applicable': 'लागू नहीं',
    'user_management.organizations_assignment': 'संगठन असाइनमेंट',
    'user_management.module_access': 'मॉड्यूल एक्सेस',
    'user_management.module_access_desc': 'भूमिका द्वारा डिफ़ॉल्ट सेट — आवश्यकतानुसार अनुकूलित करें',
    'user_management.force_login': 'फ़ोर्स लॉगिन',
    'user_management.otp_history': 'OTP इतिहास',
    'user_management.all_orgs': 'सभी संगठन',
    'user_management.none': 'कोई नहीं',

    // Access Log Actions & Groups
    'log.group_user': 'उपयोगकर्ता',
    'log.group_books': 'पुस्तकें',
    'log.group_other': 'अन्य',
    'log.no_logs_found': 'कोई लॉग नहीं मिला।',
    'log.force_login_by': 'द्वारा फ़ोर्स लॉगिन',
    'log.performed_by': 'द्वारा किया गया',
    'log.target': 'लक्ष्य',
    'log.filter_by_action': 'क्रिया द्वारा फ़िल्टर करें',
    'log.action_login': 'लॉगिन',
    'log.action_logout': 'लॉग आउट',
    'log.action_generate_login_otp': 'लॉगिन OTP जनरेट करें',
    'log.action_verify_login_otp': 'लॉगिन OTP सत्यापित करें',
    'log.action_generate_reset_otp': 'रीसेट OTP जनरेट करें',
    'log.action_verify_reset_otp': 'रीसेट OTP सत्यापित करें',
    'log.action_reset_password': 'पासवर्ड रीसेट करें',
    'log.action_view_book': 'किताब देखें',
    'log.action_generate_otp': 'OTP जनरेट करें',
    'log.action_verify_otp': 'OTP सत्यापित करें',
    'log.action_upload_book': 'किताब अपलोड करें',
    'log.action_update_book': 'किताब अपडेट करें',
    'log.action_publish_book': 'किताब प्रकाशित करें',
    'log.action_delete_book': 'किताब हटाएं',
    'log.action_toggle_otp': 'OTP टॉगल करें',
    'log.action_add_access_user': 'एक्सेस उपयोगकर्ता जोड़ें',
    'log.action_remove_access_user': 'एक्सेस उपयोगकर्ता हटाएं',
    'log.action_create_user': 'उपयोगकर्ता बनाएं',
    'log.action_update_user': 'उपयोगकर्ता अपडेट करें',
    'log.action_delete_user': 'उपयोगकर्ता हटाएं',
    'log.action_activate_version': 'संस्करण सक्रिय करें',
    'log.action_create_qr': 'QR बनाएं',
    'log.action_toggle_qr': 'QR टॉगल करें',
    'log.action_delete_qr': 'QR हटाएं',
    'log.action_scan_qr': 'QR स्कैन करें',

    // Global Header & User Menu
    'header.search_placeholder': 'किताबें, विषय, कक्षा खोजें...',
    'header.switch_organization': 'संगठन बदलें',
    'header.all_organizations': 'सभी संगठन',
    'header.global_overview': 'ग्लोबल अवलोकन',
    'header.all_assigned_orgs': 'सभी सौंपे गए संगठन',
    'header.profile': 'मेरी प्रोफाइल',
    'header.logout': 'लॉग आउट',
    'header.super_admin': 'सुपर एडमिन',
    'header.admin': 'एडमिन',
    'header.notifications': 'सूचनाएं',
    'notifications.new': 'नई',
    'notifications.all': 'सभी',
    'notifications.unread': 'अपठित',
    'notifications.approvals': 'अनुमोदन',
    'notifications.books': 'पुस्तकें',
    'notifications.alerts': 'चेतावनी',
    'notifications.mark_all_read': 'सभी को पढ़ा हुआ चिह्नित करें',
    'notifications.clear_read': 'पढ़े हुए साफ़ करें',
    'notifications.view_approvals': 'सभी अनुमोदन देखें',
    'notifications.empty': 'कोई सूचना नहीं मिली',
    'notifications.caught_up': 'सब कुछ अद्यतित है!',
    'notifications.loading': 'सूचनाएं लोड हो रही हैं...',

    // Actions & Buttons
    'action.search': 'खोजें',
    'action.filter': 'फ़िल्टर',
    'action.reset': 'रीसेट',
    'action.clear_all': 'सभी हटाएं',
    'action.clear_filters': 'फ़िल्टर हटाएं',
    'action.save': 'परिवर्तन सहेजें',
    'action.saving': 'सहेजा जा रहा है...',
    'action.cancel': 'रद्द करें',
    'action.edit': 'संपादित करें',
    'action.delete': 'हटाएं',
    'action.actions': 'कार्रवाई',
    'action.upload': 'अपलोड',
    'action.upload_book': 'किताब अपलोड करें',
    'action.add_user': 'उपयोगकर्ता जोड़ें',
    'action.add_organization': 'संगठन जोड़ें',
    'action.create_organization': 'संगठन बनाएं',
    'action.add_subject': 'विषय जोड़ें',
    'action.add_grade': 'कक्षा जोड़ें',
    'action.create': 'बनाएं',
    'action.status': 'स्थिति',
    'action.back': 'पीछे',
    'action.confirm': 'पुष्टि करें',
    'action.close': 'बंद करें',
    'action.send': 'भेजें',
    'action.sending': 'भेजा जा रहा है...',
    'action.copy_link': 'लिंक कॉपी करें',
    'action.copy': 'कॉपी',
    'action.copied': 'कॉपी हो गया!',
    'action.view': 'देखें',
    'action.preview': 'पूर्वावलोकन',
    'action.approve': 'स्वीकार करें',
    'action.approving': 'स्वीकार किया जा रहा है...',
    'action.send_back': 'वापस भेजें',
    'action.reject': 'अस्वीकार करें',
    'action.history': 'इतिहास',
    'action.versions': 'संस्करण',
    'action.version_history': 'संस्करण इतिहास',
    'action.replace': 'बदलें',
    'action.replace_book': 'किताब बदलें',
    'action.qr_code': 'क्यूआर कोड',
    'action.download_qr': 'क्यूआर कोड डाउनलोड करें',
    'action.download_pdf': 'पीडीएफ डाउनलोड करें',
    'action.refresh': 'रीफ्रेश',
    'action.export': 'निर्यात करें',
    'action.export_csv': 'सीएसवी निर्यात करें',
    'action.import': 'आयात करें',
    'action.view_all': 'सभी देखें',
    'action.show_more': 'और देखें',
    'action.collapse': 'छोटा करें',
    'action.expand': 'बड़ा करें',
    'action.submit_l2': 'स्तर 2 के लिए सबमिट करें',
    'action.publish': 'प्रकाशित करें',
    'action.publishing': 'प्रकाशित हो रहा है...',
    'action.activate': 'सक्रिय करें',
    'action.deactivate': 'निष्क्रिय करें',
    'action.archive': 'संग्रहित करें',
    'action.edit_book': 'किताब संपादित करें',
    'action.delete_book': 'किताब हटाएं',
    'action.request_delete': 'हटाने का अनुरोध करें',
    'action.no_books_found': 'कोई किताब नहीं मिली',
    'action.try_adjusting_filters': 'कृपया अपनी खोज या फ़िल्टर बदलें',
    'action.upload_first_book': 'अपनी पहली किताब अपलोड करें',
    'action.select_all': 'सभी चुनें',
    'action.clear_selection': 'चयन हटाएं',
    'action.by': 'द्वारा',

    // Pagination
    'pagination.showing': 'दिखा रहे हैं',
    'pagination.of': 'में से',
    'pagination.rows_per_page': 'प्रति पृष्ठ पंक्तियाँ',
    'pagination.previous_page': 'पिछला पृष्ठ',
    'pagination.next_page': 'अगला पृष्ठ',
    'pagination.books': 'पुस्तकें',
    'pagination.logs': 'लॉग',
    'pagination.records': 'रिकॉर्ड',
    'pagination.entries': 'प्रविष्टियाँ',
    'pagination.items': 'आइटम',
    'pagination.prev': 'पिछला',
    'pagination.next': 'अगला',

    // Filter labels
    'filter.all_statuses': 'सभी स्थितियां',
    'filter.all_grades': 'सभी कक्षाएं',
    'filter.all_subjects': 'सभी विषय',
    'filter.all_roles': 'सभी भूमिकाएं',
    'filter.all_actions': 'सभी क्रियाएं',
    'filter.search_books': 'शीर्षक, विषय, अपलोडर खोजें...',
    'filter.search_users': 'नाम, ईमेल, भूमिका खोजें...',
    'filter.search_logs': 'लॉग, उपयोगकर्ता, कार्रवाई खोजें...',
    'filter.search_organizations': 'संगठन खोजें...',
    'filter.search_reviewers': 'समीक्षक खोजें...',
    'filter.search_approvals': 'शीर्षक, अनुरोधकर्ता, कक्षा खोजें...',

    // Statuses
    'status.active': 'सक्रिय',
    'status.inactive': 'निष्क्रिय',
    'status.draft': 'ड्राफ्ट',
    'status.published': 'प्रकाशित',
    'status.processing': 'प्रक्रिया जारी',
    'status.pending_approval': 'अनुमोदन लंबित',
    'status.pending_l1': 'L1 अनुमोदन लंबित',
    'status.pending': 'लंबित',
    'status.ready_for_l2': 'L2 के लिए तैयार',
    'status.awaiting_l2': 'L2 अनुमोदन प्रतीक्षारत',
    'status.pending_l2': 'L2 अनुमोदन लंबित',
    'status.approved': 'स्वीकृत',
    'status.pending_publish': 'प्रकाशन लंबित',
    'status.archived': 'संग्रहीत',
    'status.expired': 'समाप्त',
    'status.rejected': 'वापस भेजा गया',
    'status.cancelled': 'रद्द किया गया',
    'status.success': 'सफल',
    'status.failed': 'असफल',
    'status.all': 'सभी',

    // Approvals, Reviews & Reviewers
    'approval.total_requests': 'कुल अनुरोध',
    'approval.book_approval': 'पुस्तक अनुमोदन',
    'approval.delete_requests': 'विलोपन अनुरोध',
    'approval.level_1': 'स्तर 1',
    'approval.level_2': 'स्तर 2',
    'approval.approval': 'अनुमोदन',
    'approval.reviewers_l1': 'स्तर 1 समीक्षक',
    'approval.reviewers_l2': 'स्तर 2 समीक्षक',
    'approval.delete_reviewers_l1': 'स्तर 1 विलोपन समीक्षक',
    'approval.delete_reviewers_l2': 'स्तर 2 विलोपन समीक्षक',
    'approval.replacement_reviewers_l1': 'स्तर 1 प्रतिस्थापन समीक्षक',
    'approval.replacement_reviewers_l2': 'स्तर 2 प्रतिस्थापन समीक्षक',
    'approval.publishers': 'प्रकाशक',
    'approval.responsible_persons': 'जिम्मेदार व्यक्ति',
    'approval.selected': 'चयनित',
    'approval.no_reviewers_found': 'कोई समीक्षक नहीं मिला',
    'approval.no_matches': 'कोई परिणाम नहीं',
    'approval.select_l1_desc': 'स्तर 1 के अनुमोदकों का चयन करें। कोई भी एक अनुमोदन इस पुस्तक को स्तर 2 पर ले जाता है।',
    'approval.select_l2_desc': 'स्तर 2 के अनुमोदकों का चयन करें। कोई भी एक अनुमोदन इस पुस्तक को स्वचालित रूप से प्रकाशित करता है।',
    'approval.sent_back_for_improvement': 'सुधार के लिए वापस भेजा गया',
    'approval.no_feedback_note': 'कोई फीडबैक नोट प्रदान नहीं किया गया था। पीडीएफ बदलें या विवरण संपादित करें, फिर इसे स्वचालित रूप से पुन: सौंपा जाएगा।',
    'approval.delete_pending': 'हटाना लंबित',
    'approval.ready_for_l2_delete': 'L2 विलोपन के लिए तैयार',
    'approval.replacement_pending': 'प्रतिस्थापन लंबित',
    'approval.replacement': 'प्रतिस्थापन',
    'approval.no_approvals': 'कोई लंबित अनुमोदन नहीं',
    'approval.no_delete_requests': 'कोई विलोपन अनुरोध नहीं',
    'approval.review_book': 'पुस्तक की समीक्षा करें',
    'approval.review_book_subtitle': 'फ्लिपबुक देखें, फिर स्वीकृत या अस्वीकृत करें',
    'approval.loading_book': 'पुस्तक लोड हो रही है...',
    'approval.request_not_found': 'अनुमोदन अनुरोध नहीं मिला',
    'approval.why_approved': 'स्वीकृति का कारण',
    'approval.send_back_for_improvement': 'सुधार के लिए वापस भेजें',
    'approval.pdf_not_available': 'पीडीएफ उपलब्ध नहीं है',
    'approval.pdf_not_available_desc': 'इस पुस्तक में अभी तक देखने योग्य पीडीएफ नहीं है।',
    'approval.book_preview': 'पुस्तक पूर्वावलोकन',
    'approval.replacement_preview_hint': 'प्रतिस्थापन पीडीएफ — वर्तमान प्रकाशित संस्करण स्वीकृत होने तक लाइव रहेगा',
    'approval.loading_preview': 'पूर्वावलोकन लोड हो रहा है...',
    'approval.book_not_found': 'पुस्तक नहीं मिली',
    'approval.note_from_sender': 'प्रेषक का नोट',
    'approval.publish_now_desc': 'यह पुस्तक पहले से ही स्तर 2 अनुमोदित है। प्रकाशित करने से यह तुरंत लाइब्रेरी में उपलब्ध हो जाएगी।',
    'action.send_delete_l2': 'स्तर 2 विलोपन के लिए भेजें',
    'action.send_for': 'के लिए भेजें',
    'action.back_to_list': 'सूची पर वापस जाएं',

    // Grades & Standards
    'grade.nursery': 'नर्सरी',
    'grade.lkg': 'एलकेजी',
    'grade.ukg': 'यूकेजी',
    'grade.grade_prefix': 'कक्षा',

    // Common Subjects
    'subject.mathematics': 'गणित',
    'subject.economics': 'अर्थशास्त्र',
    'subject.science': 'विज्ञान',
    'subject.english': 'अंग्रेज़ी',
    'subject.hindi': 'हिन्दी',
    'subject.gujarati': 'गुजराती',
    'subject.marathi': 'मराठी',
    'subject.physics': 'भौतिकी',
    'subject.chemistry': 'रसायन विज्ञान',
    'subject.biology': 'जीव विज्ञान',
    'subject.history': 'इतिहास',
    'subject.geography': 'भूगोल',
    'subject.computer_science': 'कंप्यूटर साइंस',

    // Organizations Modals & Actions
    'org.create_organization': 'नया संगठन बनाएं',
    'org.edit_organization': 'संगठन संपादित करें',
    'org.configure_desc': 'संगठन विवरण, रंग और लोगो कॉन्फ़िगर करें',
    'org.activate_organization': 'संगठन सक्रिय करें',
    'org.deactivate_organization': 'संगठन निष्क्रिय करें',

    // Book Upload & Edit
    'action.submit_for_approval': 'मंजूरी के लिए सबमिट करें',
    'action.save_changes': 'बदलाव सहेजें',
    'upload.page_title': 'नई पुस्तक अपलोड करें',
    'upload.edit_page_title': 'पुस्तक संपादित करें',
    'upload.subtitle': 'PDF को इंटरएक्टिव HTML5 फ्लिपबुक में बदलें',
    'upload.edit_subtitle': 'पुस्तक विवरण अपडेट करें या PDF फ्लिपबुक स्रोत बदलें',
    'upload.pdf_source_file': 'PDF स्रोत फ़ाइल',
    'upload.optional_replace': 'वैकल्पिक — केवल बदलने के लिए अपलोड करें',
    'upload.drop_pdf': 'अपनी PDF फ़ाइल यहाँ खींचें, या',
    'upload.browse': 'ब्राउज़ करें',
    'upload.pdf_limit': 'केवल PDF फ़ाइलें — अधिकतम {size} MB प्रति अपलोड',
    'upload.thumbnail_title': 'पुस्तक थंबनेल',
    'upload.drop_image': 'अपनी छवि यहाँ खींचें, या',
    'upload.image_limit': 'JPG, PNG, या WebP — अधिकतम 2 MB · PDF का पहला पृष्ठ उपयोग करने के लिए खाली छोड़ें',
    'upload.current_thumbnail': 'फ़ाइल में वर्तमान थंबनेल',
    'upload.replace_image': 'छवि बदलें',
    'upload.replace_pdf': 'PDF बदलें',
    'upload.org_title': 'संगठन',
    'upload.org_edit_desc': 'पुस्तक बनने के बाद संगठन बदला नहीं जा सकता।',
    'upload.org_desc': 'चुनें कि यह पुस्तक किस संगठन की है।',
    'upload.book_info': 'पुस्तक की जानकारी',
    'upload.book_name': 'पुस्तक का नाम',
    'upload.book_name_desc': 'पूर्ण आधिकारिक शीर्षक का उपयोग करें जैसा कि यह फ्लिपबुक व्यूअर में दिखाई देगा',
    'upload.book_name_placeholder': 'उदा. Mathematics Foundations — Grade 5, Term 1',
    'upload.publisher': 'प्रकाशक',
    'upload.publisher_desc': 'इस पुस्तक के लिए प्रकाशन गृह या सामग्री स्रोत',
    'upload.grade': 'कक्षा',
    'upload.grade_desc': 'इस पुस्तक के लिए लक्षित कक्षा',
    'upload.subject': 'विषय',
    'upload.subject_desc': 'शैक्षणिक विषय श्रेणी',
    'upload.expiry_date': 'समाप्ति तिथि',
    'upload.expiry_date_desc': 'इस तिथि के बाद फ्लिपबुक अनुपलब्ध हो जाएगी।',
    'upload.description': 'विवरण',
    'upload.description_desc': 'संक्षिप्त सारांश जो संभावित माता-पिता और स्कूलों को एक्सेस मांगने से पहले दिखाया जाता है',
    'upload.description_placeholder': 'पुस्तक सामग्री, लक्षित पाठक और मुख्य विषयों का वर्णन करें...',
    'upload.access_control': 'एक्सेस नियंत्रण',
    'upload.access_notes': 'एक्सेस नोट्स',
    'upload.access_notes_desc': 'आंतरिक नोट्स कि इस पुस्तक तक किसकी पहुंच होनी चाहिए (सार्वजनिक उपयोगकर्ताओं को नहीं दिखाया गया)',
    'upload.approval_banner_title': '2-स्तरीय मंजूरी आवश्यक',
    'upload.approval_banner_desc': 'अनुमोदन के लिए सबमिट करने पर यह पुस्तक सभी स्तर 1 के अनुमोदनकर्ताओं को भेजी जाती है। किसी भी एक स्तर 1 की मंजूरी के बाद, यह स्वचालित रूप से सभी स्तर 2 के अनुमोदनकर्ताओं को भेजी जाती है। स्तर 2 की मंजूरी के बाद, पुस्तक प्रकाशित हो जाती है।',
    'upload.submitted_notice': 'सबमिट की गई पुस्तकें सभी स्तर 1 के अनुमोदनकर्ताओं के पास जाती हैं, फिर स्तर 2 के पास',
    'upload.live_notice': 'वर्तमान प्रकाशित संस्करण तब तक लाइव रहता है जब तक स्तर 2 नई PDF को मंजूरी नहीं दे देता',
    'upload.save_notice': 'मेटाडेटा कभी भी सहेजें। प्रकाशित पुस्तक पर PDF बदलने के लिए मंजूरी आवश्यक है।',

    // Common Table Columns
    'col.book_title': 'किताब का शीर्षक',
    'col.grade': 'कक्षा',
    'col.subject': 'विषय',
    'col.status': 'स्थिति',
    'col.pages': 'पृष्ठ',
    'col.uploaded': 'अपलोड तिथि',
    'col.uploaded_by': 'द्वारा अपलोड',
    'col.expiry': 'समाप्ति तिथि',
    'col.views': 'दृश्य',
    'col.link': 'लिंक',
    'col.name': 'नाम',
    'col.email': 'ईमेल',
    'col.role': 'भूमिका',
    'col.organization': 'संगठन',
    'col.permissions': 'अनुमतियां',
    'col.created_date': 'निर्माण तिथि',
    'col.last_login': 'अंतिम लॉगिन',
    'col.ip_address': 'आईपी पता',
    'col.device': 'डिवाइस',
    'col.action': 'कार्रवाई',
    'col.resource': 'संसाधन',
    'col.timestamp': 'समय',
    'col.details': 'विवरण',
    'col.order': 'क्रम',
    'col.requested_by': 'अनुरोधकर्ता',
    'col.sent_at': 'भेजा गया समय',
    'col.level': 'स्तर',
    'col.approval': 'अनुमोदन',

    // Dashboard & Metrics
    'dashboard.title': 'डैशबोर्ड',
    'dashboard.total_books': 'कुल पुस्तकें',
    'dashboard.total_users': 'कुल उपयोगकर्ता',
    'dashboard.total_views': 'कुल पुस्तक दृश्य',
    'dashboard.active_users': 'सक्रिय उपयोगकर्ता',
    'dashboard.avg_read_time': 'औसत पठन समय',
    'dashboard.active_flipbooks': 'सक्रिय फ्लिपबुक्स',
    'dashboard.expired_today': 'आज समाप्त',
    'dashboard.otp_generated': 'ओटीपी अनुरोध (24 घंटे)',
    'dashboard.processing_queue': 'प्रसंस्करण कतार',
    'dashboard.storage_used': 'उपयोग किया गया पीडीएफ स्टोरेज',
    'dashboard.published_books': 'प्रकाशित पुस्तकें',
    'dashboard.draft_books': 'ड्राफ्ट पुस्तकें',
    'dashboard.expired_books': 'समाप्त पुस्तकें',
    'dashboard.recent_activity': 'हाल की गतिविधि',
    'dashboard.top_performing': 'शीर्ष प्रदर्शन पुस्तकें',
    'dashboard.views_trend': 'दृश्य रुझान',
    'dashboard.books_by_grade': 'कक्षा अनुसार पुस्तकें',
    'dashboard.books_by_subject': 'विषय अनुसार पुस्तकें',
    'dashboard.trends_distribution': 'रुझान और वितरण',
    'dashboard.otp_trend': 'ओटीपी अनुरोध रुझान',
    'dashboard.otp_trend_sub': 'पिछले 14 दिन · प्रकाशित फ्लिपबुक एक्सेस',
    'dashboard.books_by_grade_sub': 'सभी कक्षाओं में वितरण',
    'dashboard.latest_events': 'लाइब्रेरी के नवीनतम कार्यक्रम',
    'dashboard.today': 'आज',
    'dashboard.distribution_subjects': 'विषयों में वितरण',
    'dashboard.analytics_heading': 'एनालिटिक्स — पुस्तक दृश्य, एक्सेस ओटीपी और उपयोगकर्ता जानकारी',
    'dashboard.monthly_activity_trend': 'मासिक गतिविधि रुझान',
    'dashboard.views_by_subject': 'विषय अनुसार दृश्य',
    'dashboard.books_by_grade_group': 'कक्षा समूह अनुसार पुस्तकें',
    'dashboard.unique_otp_users': 'विशिष्ट ओटीपी उपयोगकर्ता',
    'dashboard.this_month': 'इस महीने',
    'dashboard.no_recent_activity': 'अभी कोई हाल की गतिविधि नहीं है',
    'dashboard.no_subject_data': 'कोई विषय डेटा उपलब्ध नहीं है',
    'dashboard.no_grade_data': 'कोई कक्षा डेटा उपलब्ध नहीं है',
    'dashboard.no_books_yet': 'अभी कोई किताब नहीं है',
    'dashboard.page_title': 'डैशबोर्ड और एनालिटिक्स',
    'dashboard.main_subtitle': 'पुस्तकालय अवलोकन, गतिविधि मेट्रिक्स और उपयोग विश्लेषण',
    'dashboard.needs_renewal': 'नवीनीकरण आवश्यक',
    'dashboard.none_today': 'आज कोई नहीं',
    'dashboard.vs_previous_24h': 'पिछले 24 घंटों की तुलना में',
    'dashboard.pdf_processing_pending': 'पीडीएफ प्रसंस्करण लंबित',
    'dashboard.queue_clear': 'कतार खाली',
    'dashboard.from_uploaded_books': 'अपलोड की गई पुस्तक फ़ाइलों से',
    'dashboard.all_time': 'कुल समय',
    'dashboard.last_7_days': 'पिछले 7 दिन',
    'dashboard.per_session': 'प्रति सत्र',
    'dashboard.monthly_activity_sub': 'पुस्तक दृश्य, एक्सेस ओटीपी और सक्रिय उपयोगकर्ता',
    'dashboard.grades_count': 'कक्षाएं',
    'dashboard.grade_single': 'कक्षा',
    'dashboard.otp_requests': 'ओटीपी अनुरोध',
    'dashboard.books_count': 'पुस्तकें',
    'dashboard.chart_views': 'दृश्य',
    'dashboard.chart_otps': 'ओटीपी',
    'dashboard.chart_users': 'उपयोगकर्ता',
    'dashboard.chart_share': 'हिस्सा',
    'dashboard.action_required': 'कार्रवाई',
    'dashboard.activity_upload': 'पुस्तक अपलोड की गई',
    'dashboard.activity_otp': 'एक्सेस ओटीपी',
    'dashboard.activity_expired': 'पुस्तक समाप्त',
    'dashboard.activity_view': 'पुस्तक दृश्य',
    'header.global_overview_desc': 'ग्लोबल अवलोकन और समग्र डेटा',
    'header.all_assigned_orgs_desc': 'सभी सौंपे गए संगठन और समग्र डेटा',
    'date.just_now': 'अभी',
    'date.yesterday': 'कल',
    'date.m_ago': 'मिनट पहले',
    'date.h_ago': 'घंटे पहले',
    'date.d_ago': 'दिन पहले',


    // Access History
    'history.visitors': 'आगंतुक',
    'history.view_events': 'दृश्य ईवेंट्स',
    'history.read_sessions': 'पठन सत्र',
    'history.otp_history': 'ओटीपी इतिहास',
    'history.reader_user': 'पाठक / उपयोगकर्ता',
    'history.total_read_time': 'कुल पठन समय',
    'history.first_access': 'पहला एक्सेस',
    'history.last_access': 'अंतिम एक्सेस',
    'history.latest_ip': 'नवीनतम आईपी',
    'history.books_accessed': 'एक्सेस की गई पुस्तकें',

    // Settings
    'settings.title': 'सेटिंग्स',
    'settings.subtitle': 'अपनी व्यक्तिगत प्राथमिकताएं और सिस्टम कॉन्फ़िगरेशन प्रबंधित करें',
    'settings.tab_general': 'सामान्य सेटिंग्स',
    'settings.tab_notifications': 'सूचना प्राथमिकताएं',
    'settings.tab_security': 'सुरक्षा सेटिंग्स',
    'settings.tab_flipbook': 'फ्लिपबुक सेटिंग्स',
    'settings.tab_email': 'ईमेल सेटिंग्स',
    'settings.interface_language': 'इंटरफ़ेस भाषा',
    'settings.interface_language_desc': 'एप्लिकेशन इंटरफ़ेस के लिए भाषा चुनें',
    'settings.timezone': 'समय क्षेत्र (टाइमज़ोन)',
    'settings.timezone_desc': 'समय और तारीखें आपके चुने गए समय क्षेत्र में दिखाई देंगी',
    'settings.admin_email': 'व्यवस्थापक ईमेल',
    'settings.admin_email_desc': 'सिस्टम सूचनाओं के लिए प्राथमिक संपर्क ईमेल',
    'settings.organization_name': 'संगठन का नाम',
    'settings.save_success': 'सेटिंग्स सफलतापूर्वक सहेजी गईं',

    // Flipbook Viewer & Reader
    'reader.loading_flipbook': 'फ्लिपबुक लोड हो रही है…',
    'reader.loading_page': 'पृष्ठ लोड हो रहा है...',
    'reader.read_book': 'किताब पढ़ें',
    'reader.thumbnails': 'थंबनेल',
    'reader.search': 'किताब में खोजें',
    'reader.zoom_in': 'ज़ूम इन',
    'reader.zoom_out': 'ज़ूम आउट',
    'reader.fullscreen': 'फ़ुलस्क्रीन',
    'reader.exit_fullscreen': 'फ़ुलस्क्रीन से बाहर निकलें',
    'reader.sound_on': 'ध्वनि चालू',
    'reader.sound_off': 'ध्वनि बंद',
    'reader.auto_play': 'ऑटो प्ले',
    'reader.pause': 'रोकें',
    'reader.bookmarks': 'बुकमार्क',
    'reader.add_bookmark': 'यह पृष्ठ बुकमार्क करें',
    'reader.remove_bookmark': 'बुकमार्क हटाएं',
    'reader.page_of': 'का',
    'reader.protected_document': 'सुरक्षित दस्तावेज़',

    // Session Expiry
    'session.expired_title': 'सत्र समाप्त हो गया',
    'session.expired_desc': 'निष्क्रियता के कारण आपका सत्र समाप्त हो गया है। कृपया जारी रखने के लिए पुनः साइन इन करें।',
    'session.sign_in_again': 'पुनः साइन इन करें',
  },
  gu: {
    'nav.dashboard': 'ડેશબોર્ડ',
    'nav.flipbook_library': 'ફ્લિપબુક લાઇબ્રેરી',
    'nav.access_history': 'એક્સેસ ઇતિહાસ',
    'nav.books_approval': 'પુસ્તક મંજૂરી',
    'nav.organizations': 'સંસ્થાઓ',
    'nav.user_management': 'વપરાશકર્તા સંચાલન',
    'nav.access_logs': 'એક્સેસ લૉગ્સ',
    'nav.masters': 'માસ્ટર્સ',
    'nav.subject_master': 'વિષય માસ્ટર',
    'nav.grade_master': 'ધોરણ માસ્ટર',
    'nav.settings': 'સેટિંગ્સ',

    'library.subtitle': 'બધા ડિજિટલ પુસ્તકોનું સંચાલન અને અવલોકન કરો',
    'dashboard.subtitle': 'ડિજિટલ પુસ્તકો, વાંચકો અને વિશ્લેષણનું વિહંગાવલોકન',
    'access_history.subtitle': 'વાંચક સત્ર લૉગ્સ અને એક્સેસ ઇવેન્ટ્સ જુઓ',
    'books_approval.subtitle': 'સબમિટ કરેલ પુસ્તક મંજૂરીઓની સમીક્ષા કરો',
    'organizations.subtitle': 'સંસ્થાઓ, થીમ્સ અને પરવાનગીઓ સંચાલિત કરો',
    'user_management.subtitle': 'વપરાશકર્તા ખાતાઓ અને ભૂમિકાઓનું સંચાલન કરો',
    'access_logs.subtitle': 'સંસ્થાઓમાં વપરાશકર્તા પ્રવૃત્તિઓનું નિરીક્ષણ કરો',
    'masters.subjects_subtitle': 'ફ્લિપબુક્સ માટે શૈક્ષણિક વિષયો સંચાલિત કરો',
    'masters.grades_subtitle': 'શૈક્ષણિક ધોરણો અને વર્ગો સંચાલિત કરો',
    'masters.add_subject': 'વિષય ઉમેરો',
    'masters.edit_subject': 'વિષય સંપાદિત કરો',
    'masters.create_subject': 'વિષય બનાવો',
    'masters.update_subject': 'વિષય અપડેટ કરો',
    'masters.delete_subject_title': 'વિષય કાઢી નાખો?',
    'masters.delete_subject_desc': 'આ કાયમી ધોરણે કાઢી નાખશે',
    'masters.delete_subject_hint': 'પુસ્તકો સાથે જોડાયેલા વિષયો કાઢી શકાતા નથી; તેના બદલે તેને નિષ્ક્રિય કરો.',
    'masters.subject_name': 'વિષયનું નામ',
    'masters.enter_subject_name': 'વિષયનું નામ દાખલ કરો',
    'masters.subject_active_desc': 'પુસ્તક માટે વિષય પસંદ કરતી વખતે ઉપલબ્ધ',
    'masters.subject_inactive_desc': 'પુસ્તક વિષય પસંદગીમાંથી છુપાયેલ',
    'masters.no_subjects_found': 'કોઈ વિષયો મળ્યા નથી',
    'masters.loading_subjects': 'વિષયો લોડ થઈ રહ્યા છે...',
    'masters.try_different_search': 'કૃપા કરીને અલગ શોધ શબ્દ અજમાવો.',
    'masters.add_first_subject': 'શરૂ કરવા માટે તમારો પ્રથમ વિષય ઉમેરો.',
    'masters.subject_name_required': 'વિષયનું નામ જરૂરી છે',
    'masters.subject_created_success': 'વિષય સફળતાપૂર્વક બનાવવામાં આવ્યો',
    'masters.subject_updated_success': 'વિષય સફળતાપૂર્વક અપડેટ થયો',
    'masters.subject_deleted_success': 'વિષય સફળતાપૂર્વક કાઢી નાખવામાં આવ્યો',
    'masters.failed_load_subjects': 'વિષયો લોડ કરવામાં નિષ્ફળ',
    'masters.failed_save_subject': 'વિષય સાચવવામાં નિષ્ફળ',
    'masters.failed_delete_subject': 'વિષય કાઢી નાખવામાં નિષ્ફળ',
    'masters.add_grade': 'ધોરણ ઉમેરો',
    'masters.edit_grade': 'ધોરણ સંપાદિત કરો',
    'masters.create_grade': 'ધોરણ બનાવો',
    'masters.update_grade': 'ધોરણ અપડેટ કરો',
    'masters.delete_grade_title': 'ધોરણ કાઢી નાખો?',
    'masters.delete_grade_desc': 'આ કાયમી ધોરણે કાઢી નાખશે',
    'masters.delete_grade_hint': 'પુસ્તકો સાથે જોડાયેલા ધોરણ કાઢી શકાતા નથી; તેના બદલે તેને નિષ્ક્રિય કરો.',
    'masters.grade_name': 'ધોરણનું નામ',
    'masters.enter_grade_name': 'ધોરણનું નામ દાખલ કરો',
    'masters.grade_active_desc': 'પુસ્તક માટે ધોરણ પસંદ કરતી વખતે ઉપલબ્ધ',
    'masters.grade_inactive_desc': 'પુસ્તક ધોરણ પસંદગીમાંથી છુપાયેલ',
    'masters.no_grades_found': 'કોઈ ધોરણ મળ્યા નથી',
    'masters.loading_grades': 'ધોરણ લોડ થઈ રહ્યા છે...',
    'masters.add_first_grade': 'શરૂ કરવા માટે તમારું પ્રથમ ધોરણ ઉમેરો.',
    'masters.grade_name_required': 'ધોરણનું નામ જરૂરી છે',
    'masters.grade_created_success': 'ધોરણ સફળતાપૂર્વક બનાવવામાં આવ્યું',
    'masters.grade_updated_success': 'ધોરણ સફળતાપૂર્વક અપડેટ થયું',
    'masters.grade_deleted_success': 'ધોરણ સફળતાપૂર્વક કાઢી નાખવામાં આવ્યું',
    'masters.failed_load_grades': 'ધોરણ લોડ કરવામાં નિષ્ફળ',
    'masters.failed_save_grade': 'ધોરણ સાચવવામાં નિષ્ફળ',
    'masters.failed_delete_grade': 'ધોરણ કાઢી નાખવામાં નિષ્ફળ',
    'masters.order_already_used': 'ક્રમ પહેલાથી વપરાયેલ છે. કૃપા કરીને અલગ ક્રમ પસંદ કરો.',
    'filter.search_subjects': 'વિષયો શોધો...',
    'filter.search_grades': 'ધોરણ શોધો...',
    'action.deleting': 'કાઢી રહ્યા છીએ...',

    // Roles
    'role.super_admin': 'સુપર એડમિન',
    'role.admin': 'એડમિન',
    'role.admins': 'એડમિન',
    'role.school_admin': 'સ્કૂલ એડમિન',
    'role.approver_l1': 'મંજૂરકર્તા L1',
    'role.approver_l2': 'મંજૂરકર્તા L2',
    'role.publish_approver_l1': 'પ્રકાશન મંજૂરકર્તા લેવલ 1',
    'role.publish_approver_l2': 'પ્રકાશન મંજૂરકર્તા લેવલ 2',
    'role.delete_approver_l1': 'ડિલીટ મંજૂરકર્તા લેવલ 1',
    'role.delete_approver_l2': 'ડિલીટ મંજૂરકર્તા લેવલ 2',
    'role.content_creator': 'કન્ટેન્ટ ક્રિએટર',
    'role.reader': 'વાંચક',
    'role.student': 'વિદ્યાર્થી',
    'role.teacher': 'શિક્ષક',

    // User Management Modals & Tables
    'user_management.admins': 'એડમિન',
    'user_management.delete_user': 'વપરાશકર્તા કાઢી નાખો',
    'user_management.delete_user_desc': 'શું તમે ખરેખર કાઢી નાખવા માંગો છો',
    'user_management.cannot_undone': 'આ ક્રિયા પૂર્વવત્ કરી શકાતી નથી.',
    'user_management.add_new_user': 'નવો વપરાશકર્તા ઉમેરો',
    'user_management.edit_user': 'વપરાશકર્તા સંપાદિત કરો',
    'user_management.create_user_desc': 'નવું એકાઉન્ટ બનાવો અને મોડ્યુલ એક્સેસ સોંપો',
    'user_management.edit_user_desc': 'વપરાશકર્તા વિગતો અને પરવાનગીઓ અપડેટ કરો',
    'user_management.basic_info': 'મૂળભૂત માહિતી',
    'user_management.full_name': 'પૂરું નામ',
    'user_management.email_address': 'ઇમેઇલ સરનામું',
    'user_management.password': 'પાસવર્ડ',
    'user_management.new_password_keep': 'નવો પાસવર્ડ (રાખવા માટે ખાલી રાખો)',
    'user_management.grade_optional': 'ધોરણ / વર્ગ (વૈકલ્પિક)',
    'user_management.not_applicable': 'લાગુ નથી',
    'user_management.organizations_assignment': 'સંસ્થાઓની સોંપણી',
    'user_management.module_access': 'મોડ્યુલ એક્સેસ',
    'user_management.module_access_desc': 'ભૂમિકા દ્વારા ડિફૉલ્ટ સેટ — જરૂર મુજબ બદલો',
    'user_management.force_login': 'ફોર્સ લૉગિન',
    'user_management.otp_history': 'OTP ઇતિહાસ',
    'user_management.all_orgs': 'બધી સંસ્થાઓ',
    'user_management.none': 'કોઈ નહીં',

    // Access Log Actions & Groups
    'log.group_user': 'વપરાશકર્તા',
    'log.group_books': 'પુસ્તકો',
    'log.group_other': 'અન્ય',
    'log.no_logs_found': 'કોઈ લૉગ્સ મળ્યા નથી.',
    'log.force_login_by': 'દ્વારા ફોર્સ લૉગિન',
    'log.performed_by': 'દ્વારા કરવામાં આવ્યું',
    'log.target': 'લક્ષ્ય',
    'log.filter_by_action': 'ક્રિયા દ્વારા ફિલ્ટર કરો',
    'log.action_login': 'લૉગિન',
    'log.action_logout': 'લૉગ આઉટ',
    'log.action_generate_login_otp': 'લૉગિન OTP જનરેટ કરો',
    'log.action_verify_login_otp': 'લૉગિન OTP ચકાસો',
    'log.action_generate_reset_otp': 'રીસેટ OTP જનરેટ કરો',
    'log.action_verify_reset_otp': 'રીસેટ OTP ચકાસો',
    'log.action_reset_password': 'પાસવર્ડ રીસેટ કરો',
    'log.action_view_book': 'પુસ્તક જુઓ',
    'log.action_generate_otp': 'OTP જનરેટ કરો',
    'log.action_verify_otp': 'OTP ચકાસો',
    'log.action_upload_book': 'પુસ્તક અપલોડ કરો',
    'log.action_update_book': 'પુસ્તક અપડેટ કરો',
    'log.action_publish_book': 'પુસ્તક પ્રકાશિત કરો',
    'log.action_delete_book': 'પુસ્તક કાઢી નાખો',
    'log.action_toggle_otp': 'OTP ટૉગલ કરો',
    'log.action_add_access_user': 'એક્સેસ વપરાશકર્તા ઉમેરો',
    'log.action_remove_access_user': 'એક્સેસ વપરાશકર્તા દૂર કરો',
    'log.action_create_user': 'વપરાશકર્તા બનાવો',
    'log.action_update_user': 'વપરાશકર્તા અપડેટ કરો',
    'log.action_delete_user': 'વપરાશકર્તા કાઢી નાખો',
    'log.action_activate_version': 'આવૃત્તિ સક્રિય કરો',
    'log.action_create_qr': 'QR બનાવો',
    'log.action_toggle_qr': 'QR ટૉગલ કરો',
    'log.action_delete_qr': 'QR કાઢી નાખો',
    'log.action_scan_qr': 'QR સ્કેન કરો',

    'header.search_placeholder': 'પુસ્તકો, વિષય, ધોરણ શોધો...',
    'header.switch_organization': 'સંસ્થા બદલો',
    'header.all_organizations': 'બધી સંસ્થાઓ',
    'header.global_overview': 'ગ્લોબલ વિહંગાવલોકન',
    'header.all_assigned_orgs': 'બધી સોંપાયેલ સંસ્થાઓ',
    'header.profile': 'મારી પ્રોફાઇલ',
    'header.logout': 'લૉગ આઉટ',
    'header.super_admin': 'સુપર એડમિન',
    'header.admin': 'એડમિન',
    'header.notifications': 'સૂચનાઓ',
    'notifications.new': 'નવી',
    'notifications.all': 'બધી',
    'notifications.unread': 'ન વંચાયેલ',
    'notifications.approvals': 'મંજૂરીઓ',
    'notifications.books': 'પુસ્તકો',
    'notifications.alerts': 'ચેતવણીઓ',
    'notifications.mark_all_read': 'બધી વંચાયેલ તરીકે ચિહ્નિત કરો',
    'notifications.clear_read': 'વંચાયેલ સાફ કરો',
    'notifications.view_approvals': 'બધી મંજૂરીઓ જુઓ',
    'notifications.empty': 'કોઈ સૂચના મળી નથી',
    'notifications.caught_up': 'બધું અદ્યતન છે!',
    'notifications.loading': 'સૂચનાઓ લોડ થઈ રહી છે...',

    'action.search': 'શોધો',
    'action.filter': 'ફિલ્ટર',
    'action.reset': 'રીસેટ',
    'action.clear_all': 'બધું સાફ કરો',
    'action.clear_filters': 'ફિલ્ટર્સ સાફ કરો',
    'action.save': 'ફેરફારો સાચવો',
    'action.saving': 'સાચવી રહ્યું છે...',
    'action.cancel': 'રદ કરો',
    'action.edit': 'સંપાદિત કરો',
    'action.delete': 'કાઢી નાખો',
    'action.actions': 'ક્રિયાઓ',
    'action.upload': 'અપલોડ',
    'action.upload_book': 'પુસ્તક અપલોડ કરો',
    'action.add_user': 'વપરાશકર્તા ઉમેરો',
    'action.add_organization': 'સંસ્થા ઉમેરો',
    'action.create_organization': 'સંસ્થા બનાવો',
    'action.add_subject': 'વિષય ઉમેરો',
    'action.add_grade': 'ધોરણ ઉમેરો',
    'action.create': 'બનાવો',
    'action.status': 'સ્થિતિ',
    'action.back': 'પાછળ',
    'action.confirm': 'પુષ્ટિ કરો',
    'action.close': 'બંધ કરો',
    'action.send': 'મોકલો',
    'action.sending': 'મોકલી રહ્યું છે...',
    'action.copy_link': 'લિંક કૉપિ કરો',
    'action.copy': 'કૉપિ',
    'action.copied': 'કૉપિ થઈ ગયું!',
    'action.view': 'જુઓ',
    'action.preview': 'પૂર્વાવલોકન',
    'action.approve': 'મંજૂર કરો',
    'action.approving': 'મંજૂર કરી રહ્યું છે...',
    'action.send_back': 'પાછું મોકલો',
    'action.reject': 'અસ્વીકાર કરો',
    'action.history': 'ઇતિહાસ',
    'action.versions': 'આવૃત્તિઓ',
    'action.version_history': 'આવૃત્તિ ઇતિહાસ',
    'action.replace': 'બદલો',
    'action.replace_book': 'પુસ્તક બદલો',
    'action.qr_code': 'ક્યૂઆર કોડ',
    'action.download_qr': 'ક્યૂઆર કોડ ડાઉનલોડ કરો',
    'action.download_pdf': 'પીડીએફ ડાઉનલોડ કરો',
    'action.refresh': 'રીફ્રેશ',
    'action.export': 'નિકાસ કરો',
    'action.export_csv': 'સીએસવી નિકાસ કરો',
    'action.import': 'આયાત કરો',
    'action.view_all': 'બધા જુઓ',
    'action.show_more': 'વધુ જુઓ',
    'action.collapse': 'સંકોચો',
    'action.expand': 'વિસ્તૃત કરો',
    'action.submit_l2': 'સ્તર 2 માટે સબમિટ કરો',
    'action.publish': 'પ્રકાશિત કરો',
    'action.publishing': 'પ્રકાશિત થઈ રહ્યું છે...',
    'action.activate': 'સક્રિય કરો',
    'action.deactivate': 'નિષ્ક્રિય કરો',
    'action.archive': 'સંગ્રહિત કરો',
    'action.edit_book': 'પુસ્તક સંપાદિત કરો',
    'action.delete_book': 'પુસ્તક કાઢી નાખો',
    'action.request_delete': 'કાઢી નાખવાની વિનંતી કરો',
    'action.no_books_found': 'કોઈ પુસ્તકો મળ્યા નથી',
    'action.try_adjusting_filters': 'કૃપા કરીને તમારી શોધ અથવા ફિલ્ટર બદલો',
    'action.upload_first_book': 'તમારું પ્રથમ પુસ્તક અપલોડ કરો',
    'action.select_all': 'બધા પસંદ કરો',
    'action.clear_selection': 'પસંદગી સાફ કરો',
    'action.by': 'દ્વારા',

    'pagination.showing': 'દર્શાવી રહ્યા છીએ',
    'pagination.of': 'માંથી',
    'pagination.rows_per_page': 'પ્રતિ પૃષ્ઠ પંક્તિઓ',
    'pagination.previous_page': 'પાછલું પૃષ્ઠ',
    'pagination.next_page': 'આગલું પૃષ્ઠ',
    'pagination.books': 'પુસ્તકો',
    'pagination.logs': 'લૉગ્સ',
    'pagination.records': 'રેકોર્ડ્સ',
    'pagination.entries': 'એન્ટ્રીઓ',
    'pagination.items': 'આઇટમ્સ',
    'pagination.prev': 'પાછલું',
    'pagination.next': 'આગળનું',

    'filter.all_statuses': 'બધી સ્થિતિઓ',
    'filter.all_grades': 'બધા ધોરણ',
    'filter.all_subjects': 'બધા વિષયો',
    'filter.all_roles': 'બધી ભૂમિકાઓ',
    'filter.all_actions': 'બધી ક્રિયાઓ',
    'filter.search_books': 'શીર્ષક, વિષય, અપલોડ કરનાર શોધો...',
    'filter.search_users': 'નામ, ઇમેઇલ, ભૂમિકા શોધો...',
    'filter.search_logs': 'લૉગ્સ, વપરાશકર્તાઓ શોધો...',
    'filter.search_organizations': 'સંસ્થાઓ શોધો...',
    'filter.search_reviewers': 'સમીક્ષકો શોધો...',
    'filter.search_approvals': 'શીર્ષક, વિનંતીકર્તા, ધોરણ શોધો...',

    'status.active': 'સક્રિય',
    'status.inactive': 'નિષ્ક્રિય',
    'status.draft': 'ડ્રાફ્ટ',
    'status.published': 'પ્રકાશિત',
    'status.processing': 'પ્રક્રિયા હેઠળ',
    'status.pending_approval': 'મંજૂરી બાકી',
    'status.pending_l1': 'L1 મંજૂરી બાકી',
    'status.pending': 'બાકી',
    'status.ready_for_l2': 'L2 માટે તૈયાર',
    'status.awaiting_l2': 'L2 મંજૂરી બાકી',
    'status.pending_l2': 'L2 મંજૂરી બાકી',
    'status.approved': 'મંજૂર',
    'status.pending_publish': 'પ્રકાશન બાકી',
    'status.archived': 'સંગ્રહિત',
    'status.expired': 'સમાપ્ત',
    'status.rejected': 'પાછું મોકલેલ',
    'status.cancelled': 'રદ થયેલ',
    'status.success': 'સફળ',
    'status.failed': 'નિષ્ફળ',
    'status.all': 'બધા',

    // Approvals, Reviews & Reviewers
    'approval.total_requests': 'કુલ વિનંતીઓ',
    'approval.book_approval': 'પુસ્તક મંજૂરી',
    'approval.delete_requests': 'ડિલીટ વિનંતીઓ',
    'approval.level_1': 'લેવલ 1',
    'approval.level_2': 'લેવલ 2',
    'approval.approval': 'મંજૂરી',
    'approval.reviewers_l1': 'લેવલ 1 સમીક્ષકો',
    'approval.reviewers_l2': 'લેવલ 2 સમીક્ષકો',
    'approval.delete_reviewers_l1': 'લેવલ 1 ડિલીટ સમીક્ષકો',
    'approval.delete_reviewers_l2': 'લેવલ 2 ડિલીટ સમીક્ષકો',
    'approval.replacement_reviewers_l1': 'લેવલ 1 બદલાવ સમીક્ષકો',
    'approval.replacement_reviewers_l2': 'લેવલ 2 બદલાવ સમીક્ષકો',
    'approval.publishers': 'પ્રકાશકો',
    'approval.responsible_persons': 'જવાબદાર વ્યક્તિઓ',
    'approval.selected': 'પસંદ કરેલ',
    'approval.no_reviewers_found': 'કોઈ સમીક્ષક મળ્યા નથી',
    'approval.no_matches': 'કોઈ મેળ મળ્યો નથી',
    'approval.select_l1_desc': 'લેવલ 1 મંજૂરકર્તાઓ પસંદ કરો. કોઈપણ એક મંજૂરી આ પુસ્તકને લેવલ 2 પર લઈ જશે.',
    'approval.select_l2_desc': 'લેવલ 2 મંજૂરકર્તાઓ પસંદ કરો. કોઈપણ એક મંજૂરી આ પુસ્તકને આપમેળે પ્રકાશિત કરશે.',
    'approval.sent_back_for_improvement': 'સુધારણા માટે પાછું મોકલેલ',
    'approval.no_feedback_note': 'કોઈ પ્રતિસાદ નોંધ આપવામાં આવી નથી. PDF બદલો અથવા વિગતો સંપાદિત કરો, પછી તે આપમેળે ફરીથી સોંપવામાં આવશે.',
    'approval.delete_pending': 'ડિલીટ બાકી',
    'approval.ready_for_l2_delete': 'L2 ડિલીટ માટે તૈયાર',
    'approval.replacement_pending': 'બદલાવ બાકી',
    'approval.replacement': 'બદલાવ',
    'approval.no_approvals': 'કોઈ પેન્ડિંગ મંજૂરી નથી',
    'approval.no_delete_requests': 'કોઈ ડિલીટ વિનંતી નથી',
    'approval.review_book': 'પુસ્તકની સમીક્ષા કરો',
    'approval.review_book_subtitle': 'ફ્લિપબુક જુઓ, પછી મંજૂર અથવા અસ્વીકાર કરો',
    'approval.loading_book': 'પુસ્તક લોડ થઈ રહ્યું છે...',
    'approval.request_not_found': 'મંજૂરી વિનંતી મળી નથી',
    'approval.why_approved': 'મંજૂરીનું કારણ',
    'approval.send_back_for_improvement': 'સુધારણા માટે પાછું મોકલો',
    'approval.pdf_not_available': 'PDF ઉપલબ્ધ નથી',
    'approval.pdf_not_available_desc': 'આ પુસ્તકમાં હજી સુધી જોવાલાયક PDF નથી.',
    'approval.book_preview': 'પુસ્તક પૂર્વાવલોકન',
    'approval.replacement_preview_hint': 'બદલાવ PDF — વર્તમાન પ્રકાશિત સંસ્કરણ મંજૂર ન થાય ત્યાં સુધી લાઇવ રહેશે',
    'approval.loading_preview': 'પૂર્વાવલોકન લોડ થઈ રહ્યું છે...',
    'approval.book_not_found': 'પુસ્તક મળ્યું નથી',
    'approval.note_from_sender': 'મોકલનારની નોંધ',
    'approval.publish_now_desc': 'આ પુસ્તક પહેલેથી જ લેવલ 2 મંજૂર છે. પ્રકાશિત કરવાથી તે તરત જ લાઇબ્રેરીમાં ઉપલબ્ધ થઈ જશે.',
    'action.send_delete_l2': 'લેવલ 2 ડિલીટ માટે મોકલો',
    'action.send_for': 'માટે મોકલો',
    'action.back_to_list': 'યાદી પર પાછા જાઓ',

    // Grades & Standards
    'grade.nursery': 'નર્સરી',
    'grade.lkg': 'એલકેજી',
    'grade.ukg': 'યુકેજી',
    'grade.grade_prefix': 'ધોરણ',

    // Common Subjects
    'subject.mathematics': 'ગણિત',
    'subject.economics': 'અર્થશાસ્ત્ર',
    'subject.science': 'વિજ્ઞાન',
    'subject.english': 'અંગ્રેજી',
    'subject.hindi': 'હિન્દી',
    'subject.gujarati': 'ગુજરાતી',
    'subject.marathi': 'મરાઠી',
    'subject.physics': 'ભૌતિકશાસ્ત્ર',
    'subject.chemistry': 'રસાયણશાસ્ત્ર',
    'subject.biology': 'જીવવિજ્ઞાન',
    'subject.history': 'ઇતિહાસ',
    'subject.geography': 'ભૂગોળ',
    'subject.computer_science': 'કોમ્પ્યુટર સાયન્સ',

    // Organizations Modals & Actions
    'org.create_organization': 'નવી સંસ્થા બનાવો',
    'org.edit_organization': 'સંસ્થા સંપાદિત કરો',
    'org.configure_desc': 'સંસ્થાની વિગતો, રંગો અને લોગો ગોઠવો',
    'org.activate_organization': 'સંસ્થા સક્રિય કરો',
    'org.deactivate_organization': 'સંસ્થા નિષ્ક્રિય કરો',

    // Book Upload & Edit
    'action.submit_for_approval': 'મંજૂરી માટે સબમિટ કરો',
    'action.save_changes': 'ફેરફારો સાચવો',
    'upload.page_title': 'નવું પુસ્તક અપલોડ કરો',
    'upload.edit_page_title': 'પુસ્તક સંપાદિત કરો',
    'upload.subtitle': 'PDF ને ઇન્ટરેક્ટિવ HTML5 ફ્લિપબુકમાં કન્વર્ટ કરો',
    'upload.edit_subtitle': 'પુસ્તકની વિગતો અપડેટ કરો અથવા PDF ફ્લિપબુક સ્ત્રોત બદલો',
    'upload.pdf_source_file': 'PDF સ્રોત ફાઇલ',
    'upload.optional_replace': 'વૈકલ્પિક — ફક્ત બદલવા માટે અપલોડ કરો',
    'upload.drop_pdf': 'તમારી PDF અહીં ડ્રોપ કરો, અથવા',
    'upload.browse': 'બ્રાઉઝ કરો',
    'upload.pdf_limit': 'ફક્ત PDF ફાઇલો — મહત્તમ {size} MB પ્રતિ અપલોડ',
    'upload.thumbnail_title': 'પુસ્તક થંબનેલ',
    'upload.drop_image': 'તમારી છબી અહીં ડ્રોપ કરો, અથવા',
    'upload.image_limit': 'JPG, PNG, અથવા WebP — મહત્તમ 2 MB · PDF પૃષ્ઠ 1 નો ઉપયોગ કરવા માટે ખાલી છોડો',
    'upload.current_thumbnail': 'ફાઇલમાં વર્તમાન થંબનેલ',
    'upload.replace_image': 'છબી બદલો',
    'upload.replace_pdf': 'PDF બદલો',
    'upload.org_title': 'સંસ્થા',
    'upload.org_edit_desc': 'પુસ્તક બન્યા પછી સંસ્થા બદલી શકાતી નથી.',
    'upload.org_desc': 'આ પુસ્તક કઈ સંસ્થાનું છે તે પસંદ કરો.',
    'upload.book_info': 'પુસ્તકની માહિતી',
    'upload.book_name': 'પુસ્તકનું નામ',
    'upload.book_name_desc': 'સંપૂર્ણ સત્તાવાર શીર્ષકનો ઉપયોગ કરો જેમ કે તે ફ્લિપબુક વ્યુઅરમાં દેખાશે',
    'upload.book_name_placeholder': 'દા.ત. Mathematics Foundations — Grade 5, Term 1',
    'upload.publisher': 'પ્રકાશક',
    'upload.publisher_desc': 'આ પુસ્તક માટે પ્રકાશન ગૃહ અથવા સામગ્રી સ્ત્રોત',
    'upload.grade': 'ધોરણ',
    'upload.grade_desc': 'આ પુસ્તક માટે લક્ષિત ધોરણ',
    'upload.subject': 'વિષય',
    'upload.subject_desc': 'શૈક્ષણિક વિષય શ્રેણી',
    'upload.expiry_date': 'સમાપ્તિ તારીખ',
    'upload.expiry_date_desc': 'આ તારીખ પછી ફ્લિપબુક ઉપલબ્ધ રહેશે નહીં.',
    'upload.description': 'વર્ણન',
    'upload.description_desc': 'સંક્ષિપ્ત સારાંશ જે સંભવિત માતાપિતા અને શાળાઓને એક્સેસ વિનંતી કરતા પહેલા બતાવવામાં આવે છે',
    'upload.description_placeholder': 'પુસ્તકની સામગ્રી, લક્ષિત વાચકો અને મુખ્ય વિષયોનું વર્ણન કરો...',
    'upload.access_control': 'એક્સેસ નિયંત્રણ',
    'upload.access_notes': 'એક્સેસ નોંધો',
    'upload.access_notes_desc': 'આ પુસ્તક સુધી કોની પહોંચ હોવી જોઈએ તે અંગેની આંતરિક નોંધો (જાહેર વપરાશકર્તાઓને બતાવવામાં આવતી નથી)',
    'upload.approval_banner_title': '2-સ્તરીય મંજૂરી જરૂરી',
    'upload.approval_banner_desc': 'મંજૂરી માટે સબમિટ કરવાથી આ પુસ્તક તમામ લેવલ 1 મંજૂરકર્તાઓને મોકલવામાં આવે છે. કોઈપણ એક લેવલ 1 મંજૂરી પછી, તે આપમેળે તમામ લેવલ 2 મંજૂરકર્તાઓને મોકલવામાં આવે છે. લેવલ 2 મંજૂર કર્યા પછી, પુસ્તક પ્રકાશિત થાય છે.',
    'upload.submitted_notice': 'સબમિટ કરેલા પુસ્તકો બધા લેવલ 1 મંજૂરકર્તાઓ પાસે જાય છે, પછી લેવલ 2 પાસે',
    'upload.live_notice': 'વર્તમાન પ્રકાશિત સંસ્કરણ ત્યાં સુધી લાઈવ રહેશે જ્યાં સુધી લેવલ 2 નવી PDF ને મંજૂરી ન આપે',
    'upload.save_notice': 'મેટાડેટા ગમે ત્યારે સાચવો. પ્રકાશિત પુસ્તક પર PDF બદલવા માટે મંજૂરી જરૂરી છે.',

    'col.book_title': 'પુસ્તકનું શીર્ષક',
    'col.grade': 'ધોરણ',
    'col.subject': 'વિષય',
    'col.status': 'સ્થિતિ',
    'col.pages': 'પાના',
    'col.uploaded': 'અપલોડ તારીખ',
    'col.uploaded_by': 'દ્વારા અપલોડ',
    'col.expiry': 'સમાપ્તિ તારીખ',
    'col.views': 'વ્યૂઝ',
    'col.link': 'લિંક',
    'col.name': 'નામ',
    'col.email': 'ઇમેઇલ',
    'col.role': 'ભૂમિકા',
    'col.organization': 'સંસ્થા',
    'col.permissions': 'પરવાનગીઓ',
    'col.created_date': 'બનાવ્યાની તારીખ',
    'col.last_login': 'છેલ્લું લૉગિન',
    'col.ip_address': 'આઇપી સરનામું',
    'col.device': 'ડિવાઇસ',
    'col.action': 'ક્રિયા',
    'col.resource': 'સંસાધન',
    'col.timestamp': 'સમય',
    'col.details': 'વિગતો',
    'col.order': 'ક્રમ',
    'col.requested_by': 'વિનંતીકર્તા',
    'col.sent_at': 'મોકલેલ સમય',
    'col.level': 'સ્તર',
    'col.approval': 'મંજૂરી',

    'dashboard.title': 'ડેશબોર્ડ',
    'dashboard.total_books': 'કુલ પુસ્તકો',
    'dashboard.total_users': 'કુલ વપરાશકર્તાઓ',
    'dashboard.total_views': 'કુલ પુસ્તક વ્યૂઝ',
    'dashboard.active_users': 'સક્રિય વપરાશકર્તાઓ',
    'dashboard.avg_read_time': 'સરેરાશ વાંચન સમય',
    'dashboard.active_flipbooks': 'સક્રિય ફ્લિપબુક્સ',
    'dashboard.expired_today': 'આજે સમાપ્ત',
    'dashboard.otp_generated': 'ઓટીપી વિનંતીઓ (24 કલાક)',
    'dashboard.processing_queue': 'પ્રોસેસિંગ કતાર',
    'dashboard.storage_used': 'પીડીએફ સંગ્રહ વપરાશ',
    'dashboard.published_books': 'પ્રકાશિત પુસ્તકો',
    'dashboard.draft_books': 'ડ્રાફ્ટ પુસ્તકો',
    'dashboard.expired_books': 'સમાપ્ત પુસ્તકો',
    'dashboard.recent_activity': 'તાજેતરની પ્રવૃત્તિ',
    'dashboard.top_performing': 'ટોચના પુસ્તકો',
    'dashboard.views_trend': 'વ્યૂઝ ટ્રેન્ડ',
    'dashboard.books_by_grade': 'ધોરણ મુજબ પુસ્તકો',
    'dashboard.books_by_subject': 'વિષય મુજબ પુસ્તકો',
    'dashboard.trends_distribution': 'વલણો અને વિતરણ',
    'dashboard.otp_trend': 'ઓટીપી વિનંતી વલણ',
    'dashboard.otp_trend_sub': 'છેલ્લા 14 દિવસ · પ્રકાશિત ફ્લિપબુક એક્સેસ',
    'dashboard.books_by_grade_sub': 'બધા ધોરણોમાં વિતરણ',
    'dashboard.latest_events': 'લાઇબ્રેરીની તાજેતરની ઘટનાઓ',
    'dashboard.today': 'આજે',
    'dashboard.distribution_subjects': 'વિષયોમાં વિતરણ',
    'dashboard.analytics_heading': 'એનાલિટિક્સ — પુસ્તક વ્યૂઝ, એક્સેસ ઓટીપી અને વપરાશકર્તા માહિતી',
    'dashboard.monthly_activity_trend': 'માસિક પ્રવૃત્તિ વલણ',
    'dashboard.views_by_subject': 'વિષય મુજબ વ્યૂઝ',
    'dashboard.books_by_grade_group': 'ધોરણ જૂથ મુજબ પુસ્તકો',
    'dashboard.unique_otp_users': 'અનન્ય ઓટીપી વપરાશકર્તાઓ',
    'dashboard.this_month': 'આ મહિને',
    'dashboard.no_recent_activity': 'હજુ સુધી કોઈ તાજેતરની પ્રવૃત્તિ નથી',
    'dashboard.no_subject_data': 'કોઈ વિષય ડેટા ઉપલબ્ધ નથી',
    'dashboard.no_grade_data': 'કોઈ ધોરણ ડેટા ઉપલબ્ધ નથી',
    'dashboard.no_books_yet': 'હજુ સુધી કોઈ પુસ્તકો નથી',
    'dashboard.page_title': 'ડેશબોર્ડ અને એનાલિટિક્સ',
    'dashboard.main_subtitle': 'લાઇબ્રેરી વિહંગાવલોકન, પ્રવૃત્તિ મેટ્રિક્સ અને વપરાશ વિશ્લેષણ',
    'dashboard.needs_renewal': 'નવીકરણ જરૂરી',
    'dashboard.none_today': 'આજે કંઈ નથી',
    'dashboard.vs_previous_24h': 'પાછલા 24 કલાકની સરખામણીમાં',
    'dashboard.pdf_processing_pending': 'પીડીએફ પ્રોસેસિંગ બાકી',
    'dashboard.queue_clear': 'કતાર ખાલી',
    'dashboard.from_uploaded_books': 'અપલોડ કરેલી પુસ્તક ફાઇલોમાંથી',
    'dashboard.all_time': 'સમગ્ર સમય',
    'dashboard.last_7_days': 'છેલ્લા 7 દિવસ',
    'dashboard.per_session': 'પ્રતિ સત્ર',
    'dashboard.monthly_activity_sub': 'પુસ્તક વ્યૂઝ, એક્સેસ ઓટીપી અને સક્રિય વપરાશકર્તાઓ',
    'dashboard.grades_count': 'ધોરણ',
    'dashboard.grade_single': 'ધોરણ',
    'dashboard.otp_requests': 'ઓટીપી વિનંતીઓ',
    'dashboard.books_count': 'પુસ્તકો',
    'dashboard.chart_views': 'વ્યૂઝ',
    'dashboard.chart_otps': 'ઓટીપી',
    'dashboard.chart_users': 'વપરાશકર્તાઓ',
    'dashboard.chart_share': 'હિસ્સો',
    'dashboard.action_required': 'ક્રિયા',
    'dashboard.activity_upload': 'પુસ્તક અપલોડ થયું',
    'dashboard.activity_otp': 'એક્સેસ ઓટીપી',
    'dashboard.activity_expired': 'પુસ્તક સમાપ્ત',
    'dashboard.activity_view': 'પુસ્તક વ્યૂ',
    'header.global_overview_desc': 'ગ્લોબલ વિહંગાવલોકન અને એકત્રિત ડેટા',
    'header.all_assigned_orgs_desc': 'બધી સોંપાયેલ સંસ્થાઓ અને એકત્રિત ડેટા',
    'date.just_now': 'હમણાં',
    'date.yesterday': 'ગઈકાલે',
    'date.m_ago': 'મિનિટ પહેલા',
    'date.h_ago': 'કલાક પહેલા',
    'date.d_ago': 'દિવસ પહેલા',


    'history.visitors': 'મુલાકાતીઓ',
    'history.view_events': 'વ્યૂ ઇવેન્ટ્સ',
    'history.read_sessions': 'વાંચન સત્રો',
    'history.otp_history': 'ઓટીપી ઇતિહાસ',
    'history.reader_user': 'વાંચક / વપરાશકર્તા',
    'history.total_read_time': 'કુલ વાંચન સમય',
    'history.first_access': 'પ્રથમ એક્સેસ',
    'history.last_access': 'છેલ્લો એક્સેસ',
    'history.latest_ip': 'નવીનતમ આઇપી',
    'history.books_accessed': 'એક્સેસ કરેલ પુસ્તકો',

    'settings.title': 'સેટિંગ્સ',
    'settings.subtitle': 'તમારી વ્યક્તિગત પસંદગીઓ અને સિસ્ટમ રૂપરેખાંકન સંચાલિત કરો',
    'settings.tab_general': 'સામાન્ય સેટિંગ્સ',
    'settings.tab_notifications': 'સૂચના પસંદગીઓ',
    'settings.tab_security': 'સુરક્ષા સેટિંગ્સ',
    'settings.tab_flipbook': 'ફ્લિપબુક સેટિંગ્સ',
    'settings.tab_email': 'ઇમેઇલ સેટિંગ્સ',
    'settings.interface_language': 'ઇન્ટરફેસ ભાષા',
    'settings.interface_language_desc': 'એપ્લિકેશન ઇન્ટરફેસ માટે ભાષા પસંદ કરો',
    'settings.timezone': 'સમય ક્ષેત્ર (ટાઇમઝોન)',
    'settings.timezone_desc': 'સમય અને તારીખો તમારા પસંદ કરેલા સમય ક્ષેત્રમાં દેખાશે',
    'settings.admin_email': 'એડમિન ઇમેઇલ',
    'settings.admin_email_desc': 'સિસ્ટમ સૂચનાઓ માટે પ્રાથમિક સંપર્ક ઇમેઇલ',
    'settings.organization_name': 'સંસ્થાનું નામ',
    'settings.save_success': 'સેટિંગ્સ સફળતાપૂર્વક સાચવવામાં આવી',

    'reader.loading_flipbook': 'ફ્લિપબુક લોડ થઈ રહી છે…',
    'reader.loading_page': 'પાનું લોડ થઈ રહ્યું છે...',
    'reader.read_book': 'પુસ્તક વાંચો',
    'reader.thumbnails': 'થંબનેલ્સ',
    'reader.search': 'પુસ્તકમાં શોધો',
    'reader.zoom_in': 'ઝૂમ ઇન',
    'reader.zoom_out': 'ઝૂમ આઉટ',
    'reader.fullscreen': 'ફુલસ્ક્રીન',
    'reader.exit_fullscreen': 'ફુલસ્ક્રીનમાંથી બહાર નીકળો',
    'reader.sound_on': 'અવાજ ચાલુ',
    'reader.sound_off': 'અવાજ બંધ',
    'reader.auto_play': 'ઓટો પ્લે',
    'reader.pause': 'રોકો',
    'reader.bookmarks': 'બુકમાર્ક્સ',
    'reader.add_bookmark': 'આ પાનું બુકમાર્ક કરો',
    'reader.remove_bookmark': 'બુકમાર્ક દૂર કરો',
    'reader.page_of': 'માંથી',
    'reader.protected_document': 'સુરક્ષિત દસ્તાવેજ',

    // Session Expiry
    'session.expired_title': 'સત્ર સમાપ્ત થઈ ગયું',
    'session.expired_desc': 'નિષ્ક્રિયતાને કારણે તમારું સત્ર સમાપ્ત થઈ ગયું છે. ચાલુ રાખવા માટે કૃપા કરીને ફરીથી સાઇન ઇન કરો.',
    'session.sign_in_again': 'ફરીથી સાઇન ઇન કરો',
  },
  mr: {
    'nav.dashboard': 'डॅशबोर्ड',
    'nav.flipbook_library': 'फ्लिपबुक लायब्ररी',
    'nav.access_history': 'वापर इतिहास',
    'nav.books_approval': 'पुस्तके मंजुरी',
    'nav.organizations': 'संस्था',
    'nav.user_management': 'वापरकर्ता व्यवस्थापन',
    'nav.access_logs': 'प्रवेश नोंदी',
    'nav.masters': 'मास्टर्स',
    'nav.subject_master': 'विषय मास्टर',
    'nav.grade_master': 'इयत्ता मास्टर',
    'nav.settings': 'सेटिंग्ज',

    'library.subtitle': 'सर्व डिजिटल पुस्तकांचे व्यवस्थापन आणि अवलोकन करा',
    'dashboard.subtitle': 'डिजिटल पुस्तके, वाचक आणि विश्लेषणाचा आढावा',
    'access_history.subtitle': 'वाचक सत्र नोंदी आणि प्रवेश इव्हेंट्स पहा',
    'books_approval.subtitle': 'सादर केलेल्या पुस्तक मंजुऱ्यांचे पुनरावलोकन करा',
    'organizations.subtitle': 'संस्था, थीम्स आणि परवानग्या व्यवस्थापित करा',
    'user_management.subtitle': 'वापरकर्ता खाती आणि भूमिका व्यवस्थापित करा',
    'access_logs.subtitle': 'संस्थांमधील वापरकर्ता क्रियाकलापांचे परीक्षण करा',
    'masters.subjects_subtitle': 'फ्लिपबुक्ससाठी शैक्षणिक विषय व्यवस्थापित करा',
    'masters.grades_subtitle': 'शैक्षणिक इयत्ता आणि मानके व्यवस्थापित करा',
    'masters.add_subject': 'विषय जोडा',
    'masters.edit_subject': 'विषय संपादित करा',
    'masters.create_subject': 'विषय तयार करा',
    'masters.update_subject': 'विषय अद्यतनित करा',
    'masters.delete_subject_title': 'विषय हटवायचा?',
    'masters.delete_subject_desc': 'हे कायमस्वरूपी हटवेल',
    'masters.delete_subject_hint': 'पुस्तकांना नियुक्त केलेले विषय हटवले जाऊ शकत नाहीत; त्याऐवजी ते निष्क्रिय करा.',
    'masters.subject_name': 'विषयाचे नाव',
    'masters.enter_subject_name': 'विषयाचे नाव प्रविष्ट करा',
    'masters.subject_active_desc': 'पुस्तकासाठी विषय निवडताना उपलब्ध',
    'masters.subject_inactive_desc': 'पुस्तक विषय निवडीपासून लपवले',
    'masters.no_subjects_found': 'कोणतेही विषय आढळले नाहीत',
    'masters.loading_subjects': 'विषय लोड होत आहेत...',
    'masters.try_different_search': 'कृपया वेगळा शोध शब्द वापरून पहा.',
    'masters.add_first_subject': 'सुरू करण्यासाठी आपला पहिला विषय जोडा.',
    'masters.subject_name_required': 'विषयाचे नाव आवश्यक आहे',
    'masters.subject_created_success': 'विषय यशस्वीरित्या तयार केला',
    'masters.subject_updated_success': 'विषय यशस्वीरित्या अद्यतनित केला',
    'masters.subject_deleted_success': 'विषय यशस्वीरित्या हटवला',
    'masters.failed_load_subjects': 'विषय लोड करण्यात अयशस्वी',
    'masters.failed_save_subject': 'विषय जतन करण्यात अयशस्वी',
    'masters.failed_delete_subject': 'विषय हटवण्यात अयशस्वी',
    'masters.add_grade': 'इयत्ता जोडा',
    'masters.edit_grade': 'इयत्ता संपादित करा',
    'masters.create_grade': 'इयत्ता तयार करा',
    'masters.update_grade': 'इयत्ता अद्यतनित करा',
    'masters.delete_grade_title': 'इयत्ता हटवायची?',
    'masters.delete_grade_desc': 'हे कायमस्वरूपी हटवेल',
    'masters.delete_grade_hint': 'पुस्तकांना नियुक्त केलेल्या इयत्ता हटवल्या जाऊ शकत नाहीत; त्याऐवजी त्या निष्क्रिय करा.',
    'masters.grade_name': 'इयत्तेचे नाव',
    'masters.enter_grade_name': 'इयत्तेचे नाव प्रविष्ट करा',
    'masters.grade_active_desc': 'पुस्तकासाठी इयत्ता निवडताना उपलब्ध',
    'masters.grade_inactive_desc': 'पुस्तक इयत्ता निवडीपासून लपवले',
    'masters.no_grades_found': 'कोणतीही इयत्ता आढळली नाही',
    'masters.loading_grades': 'इयत्ता लोड होत आहेत...',
    'masters.add_first_grade': 'सुरू करण्यासाठी आपली पहिली इयत्ता जोडा.',
    'masters.grade_name_required': 'इयत्तेचे नाव आवश्यक आहे',
    'masters.grade_created_success': 'इयत्ता यशस्वीरित्या तयार केली',
    'masters.grade_updated_success': 'इयत्ता यशस्वीरित्या अद्यतनित केली',
    'masters.grade_deleted_success': 'इयत्ता यशस्वीरित्या हटवली',
    'masters.failed_load_grades': 'इयत्ता लोड करण्यात अयशस्वी',
    'masters.failed_save_grade': 'इयत्ता जतन करण्यात अयशस्वी',
    'masters.failed_delete_grade': 'इयत्ता हटवण्यात अयशस्वी',
    'masters.order_already_used': 'क्रम आधीच वापरलेला आहे. कृपया वेगळा क्रम निवडा.',
    'filter.search_subjects': 'विषय शोधा...',
    'filter.search_grades': 'इयत्ता शोधा...',
    'action.deleting': 'हटवत आहे...',

    // Roles
    'role.super_admin': 'सुपर ॲडमिन',
    'role.admin': 'ॲडमिन',
    'role.admins': 'ॲडमिन',
    'role.school_admin': 'शाळा ॲडमिन',
    'role.approver_l1': 'मंजूरकर्ता L1',
    'role.approver_l2': 'मंजूरकर्ता L2',
    'role.publish_approver_l1': 'प्रकाशन मंजूरकर्ता स्तर 1',
    'role.publish_approver_l2': 'प्रकाशन मंजूरकर्ता स्तर 2',
    'role.delete_approver_l1': 'हटवणे मंजूरकर्ता स्तर 1',
    'role.delete_approver_l2': 'हटवणे मंजूरकर्ता स्तर 2',
    'role.content_creator': 'सामग्री निर्माता',
    'role.reader': 'वाचक',
    'role.student': 'विद्यार्थी',
    'role.teacher': 'शिक्षक',

    // User Management Modals & Tables
    'user_management.admins': 'ॲडमिन',
    'user_management.delete_user': 'वापरकर्ता हटवा',
    'user_management.delete_user_desc': 'आपण खरोखर हटवू इच्छिता',
    'user_management.cannot_undone': 'ही कृती पूर्ववत केली जाऊ शकत नाही.',
    'user_management.add_new_user': 'नवीन वापरकर्ता जोडा',
    'user_management.edit_user': 'वापरकर्ता संपादित करा',
    'user_management.create_user_desc': 'नवीन खाते तयार करा आणि मॉड्यूल प्रवेश नियुक्त करा',
    'user_management.edit_user_desc': 'वापरकर्ता तपशील आणि परवानग्या अद्यतनित करा',
    'user_management.basic_info': 'मूलभूत माहिती',
    'user_management.full_name': 'पूर्ण नाव',
    'user_management.email_address': 'ईमेल पत्ता',
    'user_management.password': 'पासवर्ड',
    'user_management.new_password_keep': 'नवीन पासवर्ड (ठेवण्यासाठी रिक्त ठेवा)',
    'user_management.grade_optional': 'इयत्ता / वर्ग (पर्यायी)',
    'user_management.not_applicable': 'लागू नाही',
    'user_management.organizations_assignment': 'संस्था असाइनमेंट',
    'user_management.module_access': 'मॉड्यूल प्रवेश',
    'user_management.module_access_desc': 'भूमिकेनुसार डीफॉल्ट — आवश्यकतेनुसार सानुकूलित करा',
    'user_management.force_login': 'फोर्स लॉगिन',
    'user_management.otp_history': 'OTP इतिहास',
    'user_management.all_orgs': 'सर्व संस्था',
    'user_management.none': 'काहीही नाही',

    // Access Log Actions & Groups
    'log.group_user': 'वापरकर्ता',
    'log.group_books': 'पुस्तके',
    'log.group_other': 'इतर',
    'log.no_logs_found': 'कोणतेही लॉग आढळले नाहीत.',
    'log.force_login_by': 'द्वारे फोर्स लॉगिन',
    'log.performed_by': 'द्वारे केले',
    'log.target': 'लक्ष्य',
    'log.filter_by_action': 'कृतीनुसार फिल्टर करा',
    'log.action_login': 'लॉगिन',
    'log.action_logout': 'लॉग आउट',
    'log.action_generate_login_otp': 'लॉगिन OTP जनरेट करा',
    'log.action_verify_login_otp': 'लॉगिन OTP सत्यापित करा',
    'log.action_generate_reset_otp': 'रीसेट OTP जनरेट करा',
    'log.action_verify_reset_otp': 'रीसेट OTP सत्यापित करा',
    'log.action_reset_password': 'पासवर्ड रीसेट करा',
    'log.action_view_book': 'पुस्तक पहा',
    'log.action_generate_otp': 'OTP जनरेट करा',
    'log.action_verify_otp': 'OTP सत्यापित करा',
    'log.action_upload_book': 'पुस्तक अपलोड करा',
    'log.action_update_book': 'पुस्तक अद्यतनित करा',
    'log.action_publish_book': 'पुस्तक प्रकाशित करा',
    'log.action_delete_book': 'पुस्तक हटवा',
    'log.action_toggle_otp': 'OTP टॉगल करा',
    'log.action_add_access_user': 'एक्सेस वापरकर्ता जोडा',
    'log.action_remove_access_user': 'एक्सेस वापरकर्ता काढा',
    'log.action_create_user': 'वापरकर्ता तयार करा',
    'log.action_update_user': 'वापरकर्ता अद्यतनित करा',
    'log.action_delete_user': 'वापरकर्ता हटवा',
    'log.action_activate_version': 'आवृत्ती सक्रिय करा',
    'log.action_create_qr': 'QR तयार करा',
    'log.action_toggle_qr': 'QR टॉगल करा',
    'log.action_delete_qr': 'QR हटवा',
    'log.action_scan_qr': 'QR स्कॅन करा',

    'header.search_placeholder': 'पुस्तके, विषय, इयत्ता शोधा...',
    'header.switch_organization': 'संस्था बदला',
    'header.all_organizations': 'सर्व संस्था',
    'header.global_overview': 'ग्लोबल विहंगावलोकन',
    'header.all_assigned_orgs': 'सर्व नियुक्त संस्था',
    'header.profile': 'माझे प्रोफाइल',
    'header.logout': 'लॉग आउट',
    'header.super_admin': 'सुपर ॲडमिन',
    'header.admin': 'ॲडमिन',
    'header.notifications': 'सूचना',
    'notifications.new': 'नवीन',
    'notifications.all': 'सर्व',
    'notifications.unread': 'न वाचलेले',
    'notifications.approvals': 'मंजुऱ्या',
    'notifications.books': 'पुस्तके',
    'notifications.alerts': 'इशारे',
    'notifications.mark_all_read': 'सर्व वाचलेले म्हणून चिन्हांकित करा',
    'notifications.clear_read': 'वाचलेले साफ करा',
    'notifications.view_approvals': 'सर्व मंजुऱ्या पहा',
    'notifications.empty': 'कोणतीही सूचना आढळली नाही',
    'notifications.caught_up': 'सर्व काही अद्ययावत आहे!',
    'notifications.loading': 'सूचना लोड होत आहेत...',

    'action.search': 'शोधा',
    'action.filter': 'फिल्टर',
    'action.reset': 'रीसेट',
    'action.clear_all': 'सर्व साफ करा',
    'action.clear_filters': 'फिल्टर्स साफ करा',
    'action.save': 'बदल जतन करा',
    'action.saving': 'जतन करत आहे...',
    'action.cancel': 'रद्द करा',
    'action.edit': 'संपादित करा',
    'action.delete': 'हटवा',
    'action.actions': 'कृती',
    'action.upload': 'अपलोड',
    'action.upload_book': 'पुस्तक अपलोड करा',
    'action.add_user': 'वापरकर्ता जोडा',
    'action.add_organization': 'संस्था जोडा',
    'action.create_organization': 'संस्था तयार करा',
    'action.add_subject': 'विषय जोडा',
    'action.add_grade': 'इयत्ता जोडा',
    'action.create': 'तयार करा',
    'action.status': 'स्थिती',
    'action.back': 'मागे',
    'action.confirm': 'पुष्टी करा',
    'action.close': 'बंद करा',
    'action.send': 'पाठवा',
    'action.sending': 'पाठवत आहे...',
    'action.copy_link': 'दुवा कॉपी करा',
    'action.copy': 'कॉपी',
    'action.copied': 'कॉपी केले!',
    'action.view': 'पहा',
    'action.preview': 'पूर्वावलोकन',
    'action.approve': 'मंजूर करा',
    'action.approving': 'मंजूर करत आहे...',
    'action.send_back': 'परत पाठवा',
    'action.reject': 'नाकारा',
    'action.history': 'इतिहास',
    'action.versions': 'आवृत्त्या',
    'action.version_history': 'आवृत्ती इतिहास',
    'action.replace': 'बदला',
    'action.replace_book': 'पुस्तक बदला',
    'action.qr_code': 'क्यूआर कोड',
    'action.download_qr': 'क्यूआर कोड डाउनलोड करा',
    'action.download_pdf': 'पीडीएफ डाउनलोड करा',
    'action.refresh': 'रीफ्रेश',
    'action.export': 'निर्यात करा',
    'action.export_csv': 'सीएसव्ही निर्यात करा',
    'action.import': 'आयात करा',
    'action.view_all': 'सर्व पहा',
    'action.show_more': 'अधिक पहा',
    'action.collapse': 'संकोचित करा',
    'action.expand': 'विस्तारित करा',
    'action.submit_l2': 'पातळी २ साठी सादर करा',
    'action.publish': 'प्रकाशित करा',
    'action.publishing': 'प्रकाशित करत आहे...',
    'action.activate': 'सक्रिय करा',
    'action.deactivate': 'निष्क्रिय करा',
    'action.archive': 'संग्रहित करा',
    'action.edit_book': 'पुस्तक संपादित करा',
    'action.delete_book': 'पुस्तक हटवा',
    'action.request_delete': 'हटवण्याची विनंती करा',
    'action.no_books_found': 'कोणतीही पुस्तके आढळली नाहीत',
    'action.try_adjusting_filters': 'कृपया आपला शोध किंवा फिल्टर बदला',
    'action.upload_first_book': 'आपले पहिले पुस्तक अपलोड करा',
    'action.select_all': 'सर्व निवडा',
    'action.clear_selection': 'निवड साफ करा',
    'action.by': 'द्वारे',

    'pagination.showing': 'दाखवत आहे',
    'pagination.of': 'पैकी',
    'pagination.rows_per_page': 'प्रति पृष्ठ पंक्ती',
    'pagination.previous_page': 'मागील पृष्ठ',
    'pagination.next_page': 'पुढील पृष्ठ',
    'pagination.books': 'पुस्तके',
    'pagination.logs': 'लॉग',
    'pagination.records': 'नोंदी',
    'pagination.entries': 'नोंदी',
    'pagination.items': 'आयटम',
    'pagination.prev': 'मागील',
    'pagination.next': 'पुढील',

    'filter.all_statuses': 'सर्व स्थिती',
    'filter.all_grades': 'सर्व इयत्ता',
    'filter.all_subjects': 'सर्व विषय',
    'filter.all_roles': 'सर्व भूमिका',
    'filter.all_actions': 'सर्व कृती',
    'filter.search_books': 'शीर्षक, विषय, अपलोडर शोधा...',
    'filter.search_users': 'नाव, ईमेल, भूमिका शोधा...',
    'filter.search_logs': 'लॉग्स, वापरकर्ते शोधा...',
    'filter.search_organizations': 'संस्था शोधा...',
    'filter.search_reviewers': 'समीक्षक शोधा...',
    'filter.search_approvals': 'शीर्षक, विनंतीकर्ता, इयत्ता शोधा...',

    'status.active': 'सक्रिय',
    'status.inactive': 'निष्क्रिय',
    'status.draft': 'मसुदा',
    'status.published': 'प्रकाशित',
    'status.processing': 'प्रक्रिया सुरू',
    'status.pending_approval': 'मंजुरी प्रलंबित',
    'status.pending_l1': 'L1 मंजुरी प्रलंबित',
    'status.pending': 'प्रलंबित',
    'status.ready_for_l2': 'L2 साठी तयार',
    'status.awaiting_l2': 'L2 मंजुरी प्रतीक्षेत',
    'status.pending_l2': 'L2 मंजुरी प्रलंबित',
    'status.approved': 'मंजूर',
    'status.pending_publish': 'प्रकाशन प्रलंबित',
    'status.archived': 'संग्रहित',
    'status.expired': 'कालबाह्य',
    'status.rejected': 'परत पाठवले',
    'status.cancelled': 'रद्द केले',
    'status.success': 'यशस्वी',
    'status.failed': 'अयशस्वी',
    'status.all': 'सर्व',

    // Approvals, Reviews & Reviewers
    'approval.total_requests': 'एकूण विनंत्या',
    'approval.book_approval': 'पुस्तक मंजुरी',
    'approval.delete_requests': 'हटवण्याच्या विनंत्या',
    'approval.level_1': 'स्तर 1',
    'approval.level_2': 'स्तर 2',
    'approval.approval': 'मंजुरी',
    'approval.reviewers_l1': 'स्तर 1 समीक्षक',
    'approval.reviewers_l2': 'स्तर 2 समीक्षक',
    'approval.delete_reviewers_l1': 'स्तर 1 हटवणे समीक्षक',
    'approval.delete_reviewers_l2': 'स्तर 2 हटवणे समीक्षक',
    'approval.replacement_reviewers_l1': 'स्तर 1 पुनर्स्थिती समीक्षक',
    'approval.replacement_reviewers_l2': 'स्तर 2 पुनर्स्थिती समीक्षक',
    'approval.publishers': 'प्रकाशक',
    'approval.responsible_persons': 'जबाबदार व्यक्ती',
    'approval.selected': 'निवडलेले',
    'approval.no_reviewers_found': 'कोणतेही समीक्षक आढळले नाहीत',
    'approval.no_matches': 'कोणतेही जुळत नाही',
    'approval.select_l1_desc': 'स्तर 1 मंजूरकर्ते निवडा. कोणतीही एक मंजुरी हे पुस्तक स्तर 2 वर नेईल.',
    'approval.select_l2_desc': 'स्तर 2 मंजूरकर्ते निवडा. कोणतीही एक मंजुरी हे पुस्तक स्वयंचलितपणे प्रकाशित करेल.',
    'approval.sent_back_for_improvement': 'सुधारणेसाठी परत पाठवले',
    'approval.no_feedback_note': 'कोणतीही टीप दिली नाही. PDF बदला किंवा तपशील संपादित करा, त्यानंतर ते स्वयंचलितपणे पुन्हा नियुक्त केले जाईल.',
    'approval.delete_pending': 'हटवणे प्रलंबित',
    'approval.ready_for_l2_delete': 'L2 हटवण्यासाठी तयार',
    'approval.replacement_pending': 'पुनर्स्थिती प्रलंबित',
    'approval.replacement': 'पुनर्स्थिती',
    'approval.no_approvals': 'कोणतीही प्रलंबित मंजुरी नाही',
    'approval.no_delete_requests': 'कोणतीही हटवण्याची विनंती नाही',
    'approval.review_book': 'पुस्तकाचे पुनरावलोकन करा',
    'approval.review_book_subtitle': 'फ्लिपबुक पहा, नंतर मंजूर किंवा नाकारा',
    'approval.loading_book': 'पुस्तक लोड होत आहे...',
    'approval.request_not_found': 'मंजुरी विनंती आढळली नाही',
    'approval.why_approved': 'मंजुरीचे कारण',
    'approval.send_back_for_improvement': 'सुधारणेसाठी परत पाठवा',
    'approval.pdf_not_available': 'PDF उपलब्ध नाही',
    'approval.pdf_not_available_desc': 'या पुस्तकात अद्याप पाहण्यायोग्य PDF नाही.',
    'approval.book_preview': 'पुस्तक पूर्वावलोकन',
    'approval.replacement_preview_hint': 'पुनर्स्थिती PDF — वर्तमान प्रकाशित आवृत्ती मंजूर होईपर्यंत लाइव्ह राहील',
    'approval.loading_preview': 'पूर्वावलोकन लोड होत आहे...',
    'approval.book_not_found': 'पुस्तक आढळले नाही',
    'approval.note_from_sender': 'प्रेषकाची टीप',
    'approval.publish_now_desc': 'हे पुस्तक आधीच स्तर 2 मंजूर आहे. प्रकाशित केल्याने ते लगेच लायब्ररीमध्ये उपलब्ध होईल.',
    'action.send_delete_l2': 'स्तर 2 हटवण्यासाठी पाठवा',
    'action.send_for': 'साठी पाठवा',
    'action.back_to_list': 'यादीवर परत जा',

    // Grades & Standards
    'grade.nursery': 'नर्सरी',
    'grade.lkg': 'एलकेजी',
    'grade.ukg': 'यूकेजी',
    'grade.grade_prefix': 'इयत्ता',

    // Common Subjects
    'subject.mathematics': 'गणित',
    'subject.economics': 'अर्थशास्त्र',
    'subject.science': 'विज्ञान',
    'subject.english': 'इंग्रजी',
    'subject.hindi': 'हिंदी',
    'subject.gujarati': 'गुजराती',
    'subject.marathi': 'मराठी',
    'subject.physics': 'भौतिकशास्त्र',
    'subject.chemistry': 'रसायनशास्त्र',
    'subject.biology': 'जीवशास्त्र',
    'subject.history': 'इतिहास',
    'subject.geography': 'भूगोल',
    'subject.computer_science': 'संगणक शास्त्र',

    // Organizations Modals & Actions
    'org.create_organization': 'नवीन संस्था तयार करा',
    'org.edit_organization': 'संस्था संपादित करा',
    'org.configure_desc': 'संस्था तपशील, रंग आणि लोगो कॉन्फिगर करा',
    'org.activate_organization': 'संस्था सक्रिय करा',
    'org.deactivate_organization': 'संस्था निष्क्रिय करा',

    // Book Upload & Edit
    'action.submit_for_approval': 'मंजुरीसाठी सबमिट करा',
    'action.save_changes': 'बदल जतन करा',
    'upload.page_title': 'नवीन पुस्तक अपलोड करा',
    'upload.edit_page_title': 'पुस्तक संपादित करा',
    'upload.subtitle': 'PDF ला परस्परसंवादी HTML5 फ्लिपबुकमध्ये रूपांतरित करा',
    'upload.edit_subtitle': 'पुस्तकाचे तपशील अपडेट करा किंवा PDF फ्लिपबुक स्रोत बदला',
    'upload.pdf_source_file': 'PDF स्रोत फाइल',
    'upload.optional_replace': 'पर्यायी — फक्त बदलण्यासाठी अपलोड करा',
    'upload.drop_pdf': 'तुमची PDF येथे टाका, किंवा',
    'upload.browse': 'ब्राउझ करा',
    'upload.pdf_limit': 'फक्त PDF फाइल्स — प्रति अपलोड कमाल {size} MB',
    'upload.thumbnail_title': 'पुस्तक लघुप्रतिमा',
    'upload.drop_image': 'तुमची इमेज येथे टाका, किंवा',
    'upload.image_limit': 'JPG, PNG, किंवा WebP — कमाल 2 MB · PDF पृष्ठ 1 वापरण्यासाठी रिक्त ठेवा',
    'upload.current_thumbnail': 'फाइलमधील सध्याची लघुप्रतिमा',
    'upload.replace_image': 'इमेज बदला',
    'upload.replace_pdf': 'PDF बदला',
    'upload.org_title': 'संस्था',
    'upload.org_edit_desc': 'पुस्तक तयार झाल्यानंतर संस्था बदलता येत नाही.',
    'upload.org_desc': 'हे पुस्तक कोणत्या संस्थेचे आहे ते निवडा.',
    'upload.book_info': 'पुस्तकाची माहिती',
    'upload.book_name': 'पुस्तकाचे नाव',
    'upload.book_name_desc': 'फ्लिपबुक व्ह्यूअरमध्ये दिसेल तसे पूर्ण अधिकृत शीर्षक वापरा',
    'upload.book_name_placeholder': 'उदा. Mathematics Foundations — Grade 5, Term 1',
    'upload.publisher': 'प्रकाशक',
    'upload.publisher_desc': 'या पुस्तकासाठी प्रकाशन संस्था किंवा सामग्री स्रोत',
    'upload.grade': 'इयत्ता',
    'upload.grade_desc': 'या पुस्तकासाठी लक्ष्यित इयत्ता',
    'upload.subject': 'विषय',
    'upload.subject_desc': 'शैक्षणिक विषय श्रेणी',
    'upload.expiry_date': 'समाप्ती तारीख',
    'upload.expiry_date_desc': 'या तारखेनंतर फ्लिपबुक अनुपलब्ध होईल.',
    'upload.description': 'वर्णन',
    'upload.description_desc': 'संक्षिप्त सारांश जो संभाव्य पालक आणि शाळांना प्रवेश मागण्यापूर्वी दाखवला जातो',
    'upload.description_placeholder': 'पुस्तकातील सामग्री, लक्ष्यित वाचक आणि मुख्य विषयांचे वर्णन करा...',
    'upload.access_control': 'प्रवेश नियंत्रण',
    'upload.access_notes': 'प्रवेश टिपा',
    'upload.access_notes_desc': 'या पुस्तकाचा कोणाला प्रवेश असावा याबद्दल अंतर्गत टिपा (सार्वजनिक वापरकर्त्यांना दाखवले जात नाही)',
    'upload.approval_banner_title': '2-स्तरीय मंजुरी आवश्यक',
    'upload.approval_banner_desc': 'मंजुरीसाठी सबमिट केल्यावर हे पुस्तक सर्व स्तर 1 मंजुरीदारांकडे पाठवले जाते. कोणत्याही एका स्तर 1 मंजुरीनंतर, ते आपोआप सर्व स्तर 2 मंजुरीदारांकडे पाठवले जाते. स्तर 2 मंजूर झाल्यावर, पुस्तक प्रकाशित केले जाते.',
    'upload.submitted_notice': 'सबमिट केलेली पुस्तके सर्व स्तर 1 च्या मंजुरीदारांकडे जातात, नंतर स्तर 2 कडे',
    'upload.live_notice': 'स्तर 2 नवीन PDF मंजूर करेपर्यंत सध्याची आवृत्ती थेट राहील',
    'upload.save_notice': 'मेटाडेटा कधीही जतन करा. प्रकाशित पुस्तकावरील PDF बदलण्यासाठी मंजुरी आवश्यक आहे.',

    'col.book_title': 'पुस्तकाचे शीर्षक',
    'col.grade': 'इयत्ता',
    'col.subject': 'विषय',
    'col.status': 'स्थिती',
    'col.pages': 'पृष्ठे',
    'col.uploaded': 'अपलोड तारीख',
    'col.uploaded_by': 'अपलोडकर्ता',
    'col.expiry': 'समाप्ती तारीख',
    'col.views': 'दृश्ये',
    'col.link': 'दुवा',
    'col.name': 'नाव',
    'col.email': 'ईमेल',
    'col.role': 'भूमिका',
    'col.organization': 'संस्था',
    'col.permissions': 'परवानग्या',
    'col.created_date': 'तयार केल्याची तारीख',
    'col.last_login': 'शेवटचे लॉगिन',
    'col.ip_address': 'आयपी पत्ता',
    'col.device': 'डिव्हाइस',
    'col.action': 'कृती',
    'col.resource': 'संसाधन',
    'col.timestamp': 'वेळ',
    'col.details': 'तपशील',
    'col.order': 'क्रम',
    'col.requested_by': 'विनंतीकर्ता',
    'col.sent_at': 'पाठवलेली वेळ',
    'col.level': 'पातळी',
    'col.approval': 'मंजुरी',

    'dashboard.title': 'डॅशबोर्ड',
    'dashboard.total_books': 'एकूण पुस्तके',
    'dashboard.total_users': 'एकूण वापरकर्ते',
    'dashboard.total_views': 'एकूण पुस्तक दृश्ये',
    'dashboard.active_users': 'सक्रिय वापरकर्ते',
    'dashboard.avg_read_time': 'सरासरी वाचन वेळ',
    'dashboard.active_flipbooks': 'सक्रिय फ्लिपबुक्स',
    'dashboard.expired_today': 'आज कालबाह्य',
    'dashboard.otp_generated': 'ओटीपी विनंत्या (२४ तास)',
    'dashboard.processing_queue': 'प्रक्रिया रांग',
    'dashboard.storage_used': 'पीडीएफ स्टोरेज वापर',
    'dashboard.published_books': 'प्रकाशित पुस्तके',
    'dashboard.draft_books': 'मसुदा पुस्तके',
    'dashboard.expired_books': 'कालबाह्य पुस्तके',
    'dashboard.recent_activity': 'अलीकडील क्रियाकलाप',
    'dashboard.top_performing': 'शीर्ष कामगिरी पुस्तके',
    'dashboard.views_trend': 'दृश्य कल',
    'dashboard.books_by_grade': 'इयत्तानुसार पुस्तके',
    'dashboard.books_by_subject': 'विषयानुसार पुस्तके',
    'dashboard.trends_distribution': 'कल आणि वितरण',
    'dashboard.otp_trend': 'ओटीपी विनंती कल',
    'dashboard.otp_trend_sub': 'मागील १४ दिवस · प्रकाशित फ्लिपबुक प्रवेश',
    'dashboard.books_by_grade_sub': 'सर्व इयत्तांमध्ये वितरण',
    'dashboard.latest_events': 'लायब्ररीमधील ताज्या घडामोडी',
    'dashboard.today': 'आज',
    'dashboard.distribution_subjects': 'विषयांमध्ये वितरण',
    'dashboard.analytics_heading': 'विश्लेषण — पुस्तक दृश्ये, प्रवेश ओटीपी आणि वापरकर्ता माहिती',
    'dashboard.monthly_activity_trend': 'मासिक क्रियाकलाप कल',
    'dashboard.views_by_subject': 'विषयानुसार दृश्ये',
    'dashboard.books_by_grade_group': 'इयत्ता गटानुसार पुस्तके',
    'dashboard.unique_otp_users': 'अनन्य ओटीपी वापरकर्ते',
    'dashboard.this_month': 'या महिन्यात',
    'dashboard.no_recent_activity': 'अजून कोणताही अलीकडील क्रियाकलाप नाही',
    'dashboard.no_subject_data': 'कोणताही विषय डेटा उपलब्ध नाही',
    'dashboard.no_grade_data': 'कोणताही इयत्ता डेटा उपलब्ध नाही',
    'dashboard.no_books_yet': 'अजून कोणतीही पुस्तके नाहीत',
    'dashboard.page_title': 'डॅशबोर्ड आणि विश्लेषण',
    'dashboard.main_subtitle': 'ग्रंथालय विहंगावलोकन, क्रियाकलाप मेट्रिक्स आणि वापर विश्लेषण',
    'dashboard.needs_renewal': 'नूतनीकरण आवश्यक',
    'dashboard.none_today': 'आज काहीही नाही',
    'dashboard.vs_previous_24h': 'मागील 24 तासांच्या तुलनेत',
    'dashboard.pdf_processing_pending': 'पीडीएफ प्रक्रिया प्रलंबित',
    'dashboard.queue_clear': 'रांग मोकळी',
    'dashboard.from_uploaded_books': 'अपलोड केलेल्या पुस्तक फायलींमधून',
    'dashboard.all_time': 'सर्व वेळ',
    'dashboard.last_7_days': 'मागील 7 दिवस',
    'dashboard.per_session': 'प्रति सत्र',
    'dashboard.monthly_activity_sub': 'पुस्तक दृश्ये, ॲक्सेस ओटीपी आणि सक्रिय वापरकर्ते',
    'dashboard.grades_count': 'इयत्ता',
    'dashboard.grade_single': 'इयत्ता',
    'dashboard.otp_requests': 'ओटीपी विनंत्या',
    'dashboard.books_count': 'पुस्तके',
    'dashboard.chart_views': 'दृश्ये',
    'dashboard.chart_otps': 'ओटीपी',
    'dashboard.chart_users': 'वापरकर्ते',
    'dashboard.chart_share': 'वाटा',
    'dashboard.action_required': 'कृती',
    'dashboard.activity_upload': 'पुस्तक अपलोड केले',
    'dashboard.activity_otp': 'ॲक्सेस ओटीपी',
    'dashboard.activity_expired': 'पुस्तक कालबाह्य',
    'dashboard.activity_view': 'पुस्तक दृश्य',
    'header.global_overview_desc': 'ग्लोबल विहंगावलोकन आणि एकत्रित डेटा',
    'header.all_assigned_orgs_desc': 'सर्व नियुक्त संस्था आणि एकत्रित डेटा',
    'date.just_now': 'आत्ताच',
    'date.yesterday': 'काल',
    'date.m_ago': 'मिनिटांपूर्वी',
    'date.h_ago': 'तासांपूर्वी',
    'date.d_ago': 'दिवसांपूर्वी',


    'history.visitors': 'भेट देणारे',
    'history.view_events': 'दृश्य इव्हेंट्स',
    'history.read_sessions': 'वाचन सत्रे',
    'history.otp_history': 'ओटीपी इतिहास',
    'history.reader_user': 'वाचक / वापरकर्ता',
    'history.total_read_time': 'एकूण वाचन वेळ',
    'history.first_access': 'पहिला प्रवेश',
    'history.last_access': 'शेवटचा प्रवेश',
    'history.latest_ip': 'नवीनतम आयपी',
    'history.books_accessed': 'प्रवेश केलेली पुस्तके',

    'settings.title': 'सेटिंग्ज',
    'settings.subtitle': 'आपल्या वैयक्तिक पसंती आणि प्रणाली संरचना व्यवस्थापित करा',
    'settings.tab_general': 'सामान्य सेटिंग्ज',
    'settings.tab_notifications': 'सूचना प्राधान्ये',
    'settings.tab_security': 'सुरक्षा सेटिंग्ज',
    'settings.tab_flipbook': 'फ्लिपबुक सेटिंग्ज',
    'settings.tab_email': 'ईमेल सेटिंग्ज',
    'settings.interface_language': 'इंटरफेस भाषा',
    'settings.interface_language_desc': 'ॲप्लिकेशन इंटरफेससाठी भाषा निवडा',
    'settings.timezone': 'वेळ क्षेत्र (टाइमझोन)',
    'settings.timezone_desc': 'वेळ आणि तारखा आपण निवडलेल्या वेळ क्षेत्रात दिसतील',
    'settings.admin_email': 'ॲडमिन ईमेल',
    'settings.admin_email_desc': 'प्रणाली सूचनांसाठी प्राथमिक संपर्क ईमेल',
    'settings.organization_name': 'संस्थेचे नाव',
    'settings.save_success': 'सेटिंग्ज यशस्वीरित्या जतन केल्या',

    'reader.loading_flipbook': 'फ्लिपबुक लोड होत आहे…',
    'reader.loading_page': 'पृष्ठ लोड होत आहे...',
    'reader.read_book': 'पुस्तक वाचा',
    'reader.thumbnails': 'थंबनेल्स',
    'reader.search': 'पुस्तकात शोधा',
    'reader.zoom_in': 'झूम इन',
    'reader.zoom_out': 'झूम आउट',
    'reader.fullscreen': 'फुलस्क्रीन',
    'reader.exit_fullscreen': 'फुलस्क्रीनमधून बाहेर पडा',
    'reader.sound_on': 'आवाज चालू',
    'reader.sound_off': 'आवाज बंद',
    'reader.auto_play': 'ऑटो प्ले',
    'reader.pause': 'थांबवा',
    'reader.bookmarks': 'बुकमार्क',
    'reader.add_bookmark': 'हे पृष्ठ बुकमार्क करा',
    'reader.remove_bookmark': 'बुकमार्क काढा',
    'reader.page_of': 'पैकी',
    'reader.protected_document': 'संरक्षित दस्तऐवज',

    // Session Expiry
    'session.expired_title': 'सत्र संपले',
    'session.expired_desc': 'निष्क्रियतेमुळे आपले सत्र संपले आहे. पुढे चालू ठेवण्यासाठी कृपया पुन्हा साइन इन करा.',
    'session.sign_in_again': 'पुन्हा साइन इन करा',
  },
  es: {
    'nav.dashboard': 'Panel',
    'nav.flipbook_library': 'Biblioteca Flipbook',
    'nav.access_history': 'Historial de Acceso',
    'nav.books_approval': 'Aprobación de Libros',
    'nav.organizations': 'Organizaciones',
    'nav.user_management': 'Gestión de Usuarios',
    'nav.access_logs': 'Registros de Acceso',
    'nav.masters': 'Maestros',
    'nav.subject_master': 'Maestro de Materias',
    'nav.grade_master': 'Maestro de Grados',
    'nav.settings': 'Configuración',

    'library.subtitle': 'Administra y visualiza todos los libros digitales',
    'dashboard.subtitle': 'Resumen de libros digitales, lectores y estadísticas',
    'access_history.subtitle': 'Ver registros de sesiones de lectores',
    'books_approval.subtitle': 'Revisar y procesar aprobaciones de libros',
    'organizations.subtitle': 'Administrar organizaciones y permisos',
    'user_management.subtitle': 'Administrar usuarios y roles',
    'access_logs.subtitle': 'Monitorear actividades de usuarios',
    'masters.subjects_subtitle': 'Administrar materias académicas',
    'masters.grades_subtitle': 'Administrar grados y clases',
    'masters.add_subject': 'Agregar Materia',
    'masters.edit_subject': 'Editar Materia',
    'masters.create_subject': 'Crear Materia',
    'masters.update_subject': 'Actualizar Materia',
    'masters.delete_subject_title': '¿Eliminar Materia?',
    'masters.delete_subject_desc': 'Esto eliminará permanentemente',
    'masters.delete_subject_hint': 'Las materias asignadas a libros no se pueden eliminar; desactívelas en su lugar.',
    'masters.subject_name': 'Nombre de la Materia',
    'masters.enter_subject_name': 'Ingrese el nombre de la materia',
    'masters.subject_active_desc': 'Disponible al seleccionar materia para un libro',
    'masters.subject_inactive_desc': 'Oculto de la selección de materias',
    'masters.no_subjects_found': 'No se encontraron materias',
    'masters.loading_subjects': 'Cargando materias...',
    'masters.try_different_search': 'Intente con otro término de búsqueda.',
    'masters.add_first_subject': 'Agregue su primera materia para comenzar.',
    'masters.subject_name_required': 'El nombre de la materia es obligatorio',
    'masters.subject_created_success': 'Materia creada con éxito',
    'masters.subject_updated_success': 'Materia actualizada con éxito',
    'masters.subject_deleted_success': 'Materia eliminada con éxito',
    'masters.failed_load_subjects': 'Error al cargar materias',
    'masters.failed_save_subject': 'Error al guardar materia',
    'masters.failed_delete_subject': 'Error al eliminar materia',
    'masters.add_grade': 'Agregar Grado',
    'masters.edit_grade': 'Editar Grado',
    'masters.create_grade': 'Crear Grado',
    'masters.update_grade': 'Actualizar Grado',
    'masters.delete_grade_title': '¿Eliminar Grado?',
    'masters.delete_grade_desc': 'Esto eliminará permanentemente',
    'masters.delete_grade_hint': 'Los grados asignados a libros no se pueden eliminar; desactívelos en su lugar.',
    'masters.grade_name': 'Nombre del Grado',
    'masters.enter_grade_name': 'Ingrese el nombre del grado',
    'masters.grade_active_desc': 'Disponible al seleccionar grado para un libro',
    'masters.grade_inactive_desc': 'Oculto de la selección de grados',
    'masters.no_grades_found': 'No se encontraron grados',
    'masters.loading_grades': 'Cargando grados...',
    'masters.add_first_grade': 'Agregue su primer grado para comenzar.',
    'masters.grade_name_required': 'El nombre del grado es obligatorio',
    'masters.grade_created_success': 'Grado creado con éxito',
    'masters.grade_updated_success': 'Grado actualizado con éxito',
    'masters.grade_deleted_success': 'Grado eliminado con éxito',
    'masters.failed_load_grades': 'Error al cargar grados',
    'masters.failed_save_grade': 'Error al guardar grado',
    'masters.failed_delete_grade': 'Error al eliminar grado',
    'masters.order_already_used': 'El orden ya está en uso. Por favor elija un orden diferente.',
    'filter.search_subjects': 'Buscar materias...',
    'filter.search_grades': 'Buscar grados...',
    'action.deleting': 'Eliminando...',

    // Roles
    'role.super_admin': 'Super Administrador',
    'role.admin': 'Administrador',
    'role.admins': 'Administradores',
    'role.school_admin': 'Administrador Escolar',
    'role.approver_l1': 'Aprobador L1',
    'role.approver_l2': 'Aprobador L2',
    'role.publish_approver_l1': 'Aprobador de Publicación Nivel 1',
    'role.publish_approver_l2': 'Aprobador de Publicación Nivel 2',
    'role.delete_approver_l1': 'Aprobador de Eliminación Nivel 1',
    'role.delete_approver_l2': 'Aprobador de Eliminación Nivel 2',
    'role.content_creator': 'Creador de Contenido',
    'role.reader': 'Lector',
    'role.student': 'Estudiante',
    'role.teacher': 'Profesor',

    // User Management Modals & Tables
    'user_management.admins': 'Administradores',
    'user_management.delete_user': 'Eliminar usuario',
    'user_management.delete_user_desc': '¿Está seguro de que desea eliminar a',
    'user_management.cannot_undone': 'Esta acción no se puede deshacer.',
    'user_management.add_new_user': 'Agregar nuevo usuario',
    'user_management.edit_user': 'Editar usuario',
    'user_management.create_user_desc': 'Crear una nueva cuenta y asignar acceso',
    'user_management.edit_user_desc': 'Actualizar detalles y permisos del usuario',
    'user_management.basic_info': 'Información básica',
    'user_management.full_name': 'Nombre completo',
    'user_management.email_address': 'Correo electrónico',
    'user_management.password': 'Contraseña',
    'user_management.new_password_keep': 'Nueva contraseña (dejar en blanco para mantener)',
    'user_management.grade_optional': 'Grado / Clase (opcional)',
    'user_management.not_applicable': 'No aplicable',
    'user_management.organizations_assignment': 'Asignación de organizaciones',
    'user_management.module_access': 'Acceso a módulos',
    'user_management.module_access_desc': 'Predeterminado por rol — personalizar según sea necesario',
    'user_management.force_login': 'Inicio de sesión forzado',
    'user_management.otp_history': 'Historial de OTP',
    'user_management.all_orgs': 'Todas las org.',
    'user_management.none': 'Ninguno',

    // Access Log Actions & Groups
    'log.group_user': 'Usuario',
    'log.group_books': 'Libros',
    'log.group_other': 'Otro',
    'log.no_logs_found': 'No se encontraron registros.',
    'log.force_login_by': 'Inicio de sesión forzado por',
    'log.performed_by': 'Realizado por',
    'log.target': 'Objetivo',
    'log.filter_by_action': 'Filtrar por acción',
    'log.action_login': 'Iniciar sesión',
    'log.action_logout': 'Cerrar sesión',
    'log.action_generate_login_otp': 'Generar OTP de inicio',
    'log.action_verify_login_otp': 'Verificar OTP de inicio',
    'log.action_generate_reset_otp': 'Generar OTP de restablecimiento',
    'log.action_verify_reset_otp': 'Verificar OTP de restablecimiento',
    'log.action_reset_password': 'Restablecer contraseña',
    'log.action_view_book': 'Ver libro',
    'log.action_generate_otp': 'Generar OTP',
    'log.action_verify_otp': 'Verificar OTP',
    'log.action_upload_book': 'Subir libro',
    'log.action_update_book': 'Actualizar libro',
    'log.action_publish_book': 'Publicar libro',
    'log.action_delete_book': 'Eliminar libro',
    'log.action_toggle_otp': 'Alternar OTP',
    'log.action_add_access_user': 'Agregar usuario de acceso',
    'log.action_remove_access_user': 'Quitar usuario de acceso',
    'log.action_create_user': 'Crear usuario',
    'log.action_update_user': 'Actualizar usuario',
    'log.action_delete_user': 'Eliminar usuario',
    'log.action_activate_version': 'Activar versión',
    'log.action_create_qr': 'Crear QR',
    'log.action_toggle_qr': 'Alternar QR',
    'log.action_delete_qr': 'Eliminar QR',
    'log.action_scan_qr': 'Escanear QR',

    'header.search_placeholder': 'Buscar libros, materias, grados...',
    'header.switch_organization': 'Cambiar Organización',
    'header.all_organizations': 'Todas las Organizaciones',
    'header.global_overview': 'Resumen Global',
    'header.all_assigned_orgs': 'Todas las Organizaciones Asignadas',
    'header.profile': 'Mi Perfil',
    'header.logout': 'Cerrar Sesión',
    'header.super_admin': 'Super Administrador',
    'header.admin': 'Administrador',
    'header.notifications': 'Notificaciones',
    'notifications.new': 'Nuevas',
    'notifications.all': 'Todas',
    'notifications.unread': 'No leídas',
    'notifications.approvals': 'Aprobaciones',
    'notifications.books': 'Libros',
    'notifications.alerts': 'Alertas',
    'notifications.mark_all_read': 'Marcar todas como leídas',
    'notifications.clear_read': 'Borrar leídas',
    'notifications.view_approvals': 'Ver todas las aprobaciones',
    'notifications.empty': 'No se encontraron notificaciones',
    'notifications.caught_up': '¡Estás al día!',
    'notifications.loading': 'Cargando notificaciones...',

    'action.search': 'Buscar',
    'action.filter': 'Filtrar',
    'action.reset': 'Restablecer',
    'action.clear_all': 'Borrar Todo',
    'action.clear_filters': 'Limpiar filtros',
    'action.save': 'Guardar Cambios',
    'action.saving': 'Guardando...',
    'action.cancel': 'Cancelar',
    'action.edit': 'Editar',
    'action.delete': 'Eliminar',
    'action.actions': 'Acciones',
    'action.upload': 'Subir',
    'action.upload_book': 'Subir Libro',
    'action.add_user': 'Agregar Usuario',
    'action.add_organization': 'Agregar Organización',
    'action.create_organization': 'Crear Organización',
    'action.add_subject': 'Agregar Materia',
    'action.add_grade': 'Agregar Grado',
    'action.create': 'Crear',
    'action.status': 'Estado',
    'action.back': 'Atrás',
    'action.confirm': 'Confirmar',
    'action.close': 'Cerrar',
    'action.send': 'Enviar',
    'action.sending': 'Enviando...',
    'action.copy_link': 'Copiar Enlace',
    'action.copy': 'Copiar',
    'action.copied': '¡Copiado!',
    'action.view': 'Ver',
    'action.preview': 'Vista Previa',
    'action.approve': 'Aprobar',
    'action.approving': 'Aprobando...',
    'action.send_back': 'Devolver',
    'action.reject': 'Rechazar',
    'action.history': 'Historial',
    'action.versions': 'Versiones',
    'action.version_history': 'Historial de Versiones',
    'action.replace': 'Reemplazar',
    'action.replace_book': 'Reemplazar Libro',
    'action.qr_code': 'Código QR',
    'action.download_qr': 'Descargar Código QR',
    'action.download_pdf': 'Descargar PDF',
    'action.refresh': 'Actualizar',
    'action.export': 'Exportar',
    'action.export_csv': 'Exportar CSV',
    'action.import': 'Importar',
    'action.view_all': 'Ver Todo',
    'action.show_more': 'Mostrar Más',
    'action.collapse': 'Contraer',
    'action.expand': 'Expandir',
    'action.submit_l2': 'Enviar a Nivel 2',
    'action.publish': 'Publicar',
    'action.publishing': 'Publicando...',
    'action.activate': 'Activar',
    'action.deactivate': 'Desactivar',
    'action.archive': 'Archivar',
    'action.edit_book': 'Editar Libro',
    'action.delete_book': 'Eliminar Libro',
    'action.request_delete': 'Solicitar Eliminación',
    'action.no_books_found': 'No se encontraron libros',
    'action.try_adjusting_filters': 'Intente ajustar su búsqueda o filtros',
    'action.upload_first_book': 'Suba su primer libro',
    'action.select_all': 'Seleccionar todo',
    'action.clear_selection': 'Limpiar selección',
    'action.by': 'por',

    'pagination.showing': 'Mostrando',
    'pagination.of': 'de',
    'pagination.rows_per_page': 'Filas por página',
    'pagination.previous_page': 'Página anterior',
    'pagination.next_page': 'Página siguiente',
    'pagination.books': 'libros',
    'pagination.logs': 'registros',
    'pagination.records': 'registros',
    'pagination.entries': 'entradas',
    'pagination.items': 'elementos',
    'pagination.prev': 'Anterior',
    'pagination.next': 'Siguiente',

    'filter.all_statuses': 'Todos los Estados',
    'filter.all_grades': 'Todos los Grados',
    'filter.all_subjects': 'Todas las Materias',
    'filter.all_roles': 'Todos los Roles',
    'filter.all_actions': 'Todas las Acciones',
    'filter.search_books': 'Buscar título, materia, autor...',
    'filter.search_users': 'Buscar nombre, email, rol...',
    'filter.search_logs': 'Buscar registros...',
    'filter.search_organizations': 'Buscar organizaciones...',
    'filter.search_reviewers': 'Buscar revisores...',
    'filter.search_approvals': 'Buscar título, solicitante, grado...',

    'status.active': 'Activo',
    'status.inactive': 'Inactivo',
    'status.draft': 'Borrador',
    'status.published': 'Publicado',
    'status.processing': 'Procesando',
    'status.pending_approval': 'Pendiente de Aprobación',
    'status.pending_l1': 'Pendiente de Aprobación L1',
    'status.pending': 'Pendiente',
    'status.ready_for_l2': 'Listo para L2',
    'status.awaiting_l2': 'Esperando L2',
    'status.pending_l2': 'Pendiente de Aprobación L2',
    'status.approved': 'Aprobado',
    'status.pending_publish': 'Pendiente de Publicación',
    'status.archived': 'Archivado',
    'status.expired': 'Expirado',
    'status.rejected': 'Devuelto',
    'status.cancelled': 'Cancelado',
    'status.success': 'Éxito',
    'status.failed': 'Fallido',
    'status.all': 'Todos',

    // Approvals, Reviews & Reviewers
    'approval.total_requests': 'Solicitudes Totales',
    'approval.book_approval': 'Aprobación de Libros',
    'approval.delete_requests': 'Solicitudes de Eliminación',
    'approval.level_1': 'Nivel 1',
    'approval.level_2': 'Nivel 2',
    'approval.approval': 'Aprobación',
    'approval.reviewers_l1': 'Revisores de nivel 1',
    'approval.reviewers_l2': 'Revisores de nivel 2',
    'approval.delete_reviewers_l1': 'Revisores de eliminación nivel 1',
    'approval.delete_reviewers_l2': 'Revisores de eliminación nivel 2',
    'approval.replacement_reviewers_l1': 'Revisores de reemplazo nivel 1',
    'approval.replacement_reviewers_l2': 'Revisores de reemplazo nivel 2',
    'approval.publishers': 'Publicadores',
    'approval.responsible_persons': 'Personas responsables',
    'approval.selected': 'seleccionados',
    'approval.no_reviewers_found': 'No se encontraron revisores',
    'approval.no_matches': 'Sin coincidencias',
    'approval.select_l1_desc': 'Seleccione aprobadores de Nivel 1. Cualquier aprobación mueve este libro al Nivel 2.',
    'approval.select_l2_desc': 'Seleccione aprobadores de Nivel 2. Cualquier aprobación publica este libro automáticamente.',
    'approval.sent_back_for_improvement': 'Devuelto para mejorar',
    'approval.no_feedback_note': 'No se proporcionó ninguna nota de comentarios. Reemplace el PDF o edite los detalles, luego se reasignará automáticamente.',
    'approval.delete_pending': 'Eliminación pendiente',
    'approval.ready_for_l2_delete': 'Listo para eliminar L2',
    'approval.replacement_pending': 'Reemplazo pendiente',
    'approval.replacement': 'Reemplazo',
    'approval.no_approvals': 'Sin aprobaciones pendientes',
    'approval.no_delete_requests': 'Sin solicitudes de eliminación',
    'approval.review_book': 'Revisar Libro',
    'approval.review_book_subtitle': 'Ver el flipbook, luego aprobar o rechazar',
    'approval.loading_book': 'Cargando libro...',
    'approval.request_not_found': 'Solicitud de aprobación no encontrada',
    'approval.why_approved': 'Por qué fue aprobado',
    'approval.send_back_for_improvement': 'Devolver para mejorar',
    'approval.pdf_not_available': 'PDF no disponible',
    'approval.pdf_not_available_desc': 'Este libro aún no tiene un PDF visible.',
    'approval.book_preview': 'Vista Previa del Libro',
    'approval.replacement_preview_hint': 'PDF de reemplazo — la versión publicada actual sigue activa hasta su aprobación',
    'approval.loading_preview': 'Cargando vista previa...',
    'approval.book_not_found': 'Libro no encontrado',
    'approval.note_from_sender': 'Nota del remitente',
    'approval.publish_now_desc': 'Este libro ya está aprobado en Nivel 2. Publicarlo lo pondrá disponible en la biblioteca de inmediato.',
    'action.send_delete_l2': 'Enviar para eliminación de nivel 2',
    'action.send_for': 'Enviar para',
    'action.back_to_list': 'Volver a la lista',

    // Grades & Standards
    'grade.nursery': 'Guardería',
    'grade.lkg': 'LKG',
    'grade.ukg': 'UKG',
    'grade.grade_prefix': 'Grado',

    // Common Subjects
    'subject.mathematics': 'Matemáticas',
    'subject.economics': 'Economía',
    'subject.science': 'Ciencias',
    'subject.english': 'Inglés',
    'subject.hindi': 'Hindi',
    'subject.gujarati': 'Gujarati',
    'subject.marathi': 'Maratí',
    'subject.physics': 'Física',
    'subject.chemistry': 'Química',
    'subject.biology': 'Biología',
    'subject.history': 'Historia',
    'subject.geography': 'Geografía',
    'subject.computer_science': 'Informática',

    // Organizations Modals & Actions
    'org.create_organization': 'Crear Nueva Organización',
    'org.edit_organization': 'Editar Organización',
    'org.configure_desc': 'Configurar detalles, colores y logo de la organización',
    'org.activate_organization': 'Activar Organización',
    'org.deactivate_organization': 'Desactivar Organización',

    // Book Upload & Edit
    'action.submit_for_approval': 'Enviar para Aprobación',
    'action.save_changes': 'Guardar Cambios',
    'upload.page_title': 'Subir Nuevo Libro',
    'upload.edit_page_title': 'Editar Libro',
    'upload.subtitle': 'Convierte un PDF en un flipbook interactivo HTML5',
    'upload.edit_subtitle': 'Actualizar detalles del libro o reemplazar la fuente PDF',
    'upload.pdf_source_file': 'Archivo Fuente PDF',
    'upload.optional_replace': 'Opcional: subir solo para reemplazar',
    'upload.drop_pdf': 'Arrastra tu PDF aquí o',
    'upload.browse': 'examinar',
    'upload.pdf_limit': 'Solo archivos PDF — máx. {size} MB por subida',
    'upload.thumbnail_title': 'Miniatura del Libro',
    'upload.drop_image': 'Arrastra tu imagen aquí o',
    'upload.image_limit': 'JPG, PNG o WebP — máx. 2 MB · dejar vacío para usar la pág. 1 del PDF',
    'upload.current_thumbnail': 'Miniatura actual en archivo',
    'upload.replace_image': 'Reemplazar imagen',
    'upload.replace_pdf': 'Reemplazar PDF',
    'upload.org_title': 'Organización',
    'upload.org_edit_desc': 'La organización no se puede cambiar después de crear el libro.',
    'upload.org_desc': 'Seleccione a qué organización pertenece este libro.',
    'upload.book_info': 'Información del Libro',
    'upload.book_name': 'Nombre del Libro',
    'upload.book_name_desc': 'Utilice el título oficial completo tal como aparecerá en el visor de flipbook',
    'upload.book_name_placeholder': 'ej. Mathematics Foundations — Grade 5, Term 1',
    'upload.publisher': 'Editorial',
    'upload.publisher_desc': 'Editorial o fuente de contenido para este libro',
    'upload.grade': 'Grado',
    'upload.grade_desc': 'El grado objetivo para este libro',
    'upload.subject': 'Materia',
    'upload.subject_desc': 'Categoría de materia académica',
    'upload.expiry_date': 'Fecha de Caducidad',
    'upload.expiry_date_desc': 'El flipbook deja de ser accesible después de esta fecha.',
    'upload.description': 'Descripción',
    'upload.description_desc': 'Resumen breve mostrado a padres y escuelas antes de solicitar acceso',
    'upload.description_placeholder': 'Describa el contenido del libro, público objetivo y temas clave...',
    'upload.access_control': 'Control de Acceso',
    'upload.access_notes': 'Notas de Acceso',
    'upload.access_notes_desc': 'Notas internas sobre quién debe tener acceso (no visible para usuarios públicos)',
    'upload.approval_banner_title': 'Aprobación de 2 niveles requerida',
    'upload.approval_banner_desc': 'Enviar para aprobación envía este libro a todos los aprobadores de Nivel 1. Después de cualquier aprobación de Nivel 1, se envía automáticamente al Nivel 2. Una vez que el Nivel 2 aprueba, el libro se publica.',
    'upload.submitted_notice': 'Los libros enviados van a todos los aprobadores de Nivel 1 y luego de Nivel 2',
    'upload.live_notice': 'La versión publicada actual permanece activa hasta que el Nivel 2 apruebe el nuevo PDF',
    'upload.save_notice': 'Guarde metadatos en cualquier momento. Reemplazar el PDF en un libro publicado requiere aprobación.',

    'col.book_title': 'Título del Libro',
    'col.grade': 'Grado',
    'col.subject': 'Materia',
    'col.status': 'Estado',
    'col.pages': 'Páginas',
    'col.uploaded': 'Subido',
    'col.uploaded_by': 'Subido Por',
    'col.expiry': 'Vencimiento',
    'col.views': 'Vistas',
    'col.link': 'Enlace',
    'col.name': 'Nombre',
    'col.email': 'Correo',
    'col.role': 'Rol',
    'col.organization': 'Organización',
    'col.permissions': 'Permisos',
    'col.created_date': 'Fecha de Creación',
    'col.last_login': 'Último Acceso',
    'col.ip_address': 'Dirección IP',
    'col.device': 'Dispositivo',
    'col.action': 'Acción',
    'col.resource': 'Recurso',
    'col.timestamp': 'Marca de Tiempo',
    'col.details': 'Detalles',
    'col.order': 'Orden',
    'col.requested_by': 'Solicitado Por',
    'col.sent_at': 'Enviado el',
    'col.level': 'Nivel',
    'col.approval': 'Aprobación',

    'dashboard.title': 'Panel de Control',
    'dashboard.total_books': 'Total de Libros',
    'dashboard.total_users': 'Total de Usuarios',
    'dashboard.total_views': 'Total de Vistas de Libros',
    'dashboard.active_users': 'Usuarios Activos',
    'dashboard.avg_read_time': 'Tiempo Promedio de Lectura',
    'dashboard.active_flipbooks': 'Flipbooks Activos',
    'dashboard.expired_today': 'Expirados Hoy',
    'dashboard.otp_generated': 'Solicitudes OTP (24h)',
    'dashboard.processing_queue': 'Cola de Procesamiento',
    'dashboard.storage_used': 'Almacenamiento PDF Usado',
    'dashboard.published_books': 'Libros Publicados',
    'dashboard.draft_books': 'Libros en Borrador',
    'dashboard.expired_books': 'Libros Expirados',
    'dashboard.recent_activity': 'Actividad Reciente',
    'dashboard.top_performing': 'Libros Más Leídos',
    'dashboard.views_trend': 'Tendencia de Vistas',
    'dashboard.books_by_grade': 'Libros por Grado',
    'dashboard.books_by_subject': 'Libros por Materia',
    'dashboard.trends_distribution': 'Tendencias y Distribución',
    'dashboard.otp_trend': 'Tendencia de Solicitudes OTP',
    'dashboard.otp_trend_sub': 'Últimos 14 días · acceso a flipbooks',
    'dashboard.books_by_grade_sub': 'Distribución en todos los grados',
    'dashboard.latest_events': 'Últimos eventos en la biblioteca',
    'dashboard.today': 'Hoy',
    'dashboard.distribution_subjects': 'Distribución por materias',
    'dashboard.analytics_heading': 'Analítica — Vistas, OTPs y usuarios',
    'dashboard.monthly_activity_trend': 'Tendencia Mensual de Actividad',
    'dashboard.views_by_subject': 'Vistas por Materia',
    'dashboard.books_by_grade_group': 'Libros por Grupo de Grados',
    'dashboard.unique_otp_users': 'Usuarios Únicos de OTP',
    'dashboard.this_month': 'este mes',
    'dashboard.no_recent_activity': 'No hay actividad reciente',
    'dashboard.no_subject_data': 'Sin datos de materias',
    'dashboard.no_grade_data': 'Sin datos de grados',
    'dashboard.no_books_yet': 'No hay libros todavía',
    'dashboard.page_title': 'Panel y Analítica',
    'dashboard.main_subtitle': 'Resumen de biblioteca, métricas de actividad y analítica de uso',
    'dashboard.needs_renewal': 'Requiere renovación',
    'dashboard.none_today': 'Ninguno hoy',
    'dashboard.vs_previous_24h': 'vs últimas 24h',
    'dashboard.pdf_processing_pending': 'Procesamiento de PDF pendiente',
    'dashboard.queue_clear': 'Cola libre',
    'dashboard.from_uploaded_books': 'De archivos de libros subidos',
    'dashboard.all_time': 'Todo el tiempo',
    'dashboard.last_7_days': 'Últimos 7 días',
    'dashboard.per_session': 'Por sesión',
    'dashboard.monthly_activity_sub': 'Vistas de libros, OTP de acceso y usuarios activos',
    'dashboard.grades_count': 'grados',
    'dashboard.grade_single': 'grado',
    'dashboard.otp_requests': 'Solicitudes OTP',
    'dashboard.books_count': 'Libros',
    'dashboard.chart_views': 'Vistas',
    'dashboard.chart_otps': 'OTPs',
    'dashboard.chart_users': 'Usuarios',
    'dashboard.chart_share': 'Porcentaje',
    'dashboard.action_required': 'Acción',
    'dashboard.activity_upload': 'Libro subido',
    'dashboard.activity_otp': 'OTP de acceso',
    'dashboard.activity_expired': 'Libro expirado',
    'dashboard.activity_view': 'Vista de libro',
    'header.global_overview_desc': 'Resumen global y datos agregados',
    'header.all_assigned_orgs_desc': 'Todas las organizaciones asignadas y datos agregados',
    'date.just_now': 'Ahora mismo',
    'date.yesterday': 'Ayer',
    'date.m_ago': 'm antes',
    'date.h_ago': 'h antes',
    'date.d_ago': 'd antes',


    'history.visitors': 'Visitantes',
    'history.view_events': 'Eventos de Vista',
    'history.read_sessions': 'Sesiones de Lectura',
    'history.otp_history': 'Historial OTP',
    'history.reader_user': 'Lector / Usuario',
    'history.total_read_time': 'Tiempo Total de Lectura',
    'history.first_access': 'Primer Acceso',
    'history.last_access': 'Último Acceso',
    'history.latest_ip': 'Última IP',
    'history.books_accessed': 'Libros Accedidos',

    'settings.title': 'Configuración',
    'settings.subtitle': 'Administra tus preferencias y configuración del sistema',
    'settings.tab_general': 'Configuración General',
    'settings.tab_notifications': 'Preferencias de Notificación',
    'settings.tab_security': 'Configuración de Seguridad',
    'settings.tab_flipbook': 'Configuración de Flipbook',
    'settings.tab_email': 'Configuración de Correo',
    'settings.interface_language': 'Idioma de la Interfaz',
    'settings.interface_language_desc': 'Selecciona el idioma para la interfaz',
    'settings.timezone': 'Zona Horaria',
    'settings.timezone_desc': 'Las fechas y horas se mostrarán en la zona horaria seleccionada',
    'settings.admin_email': 'Correo del Administrador',
    'settings.admin_email_desc': 'Correo principal de contacto para notificaciones',
    'settings.organization_name': 'Nombre de la Organización',
    'settings.save_success': 'Configuración guardada correctamente',

    'reader.loading_flipbook': 'Cargando flipbook…',
    'reader.loading_page': 'Cargando página...',
    'reader.read_book': 'Leer Libro',
    'reader.thumbnails': 'Miniaturas',
    'reader.search': 'Buscar en el libro',
    'reader.zoom_in': 'Acercar',
    'reader.zoom_out': 'Alejar',
    'reader.fullscreen': 'Pantalla Completa',
    'reader.exit_fullscreen': 'Salir de Pantalla Completa',
    'reader.sound_on': 'Sonido Activado',
    'reader.sound_off': 'Sonido Desactivado',
    'reader.auto_play': 'Reproducción Automática',
    'reader.pause': 'Pausa',
    'reader.bookmarks': 'Marcadores',
    'reader.add_bookmark': 'Marcar esta página',
    'reader.remove_bookmark': 'Eliminar marcador',
    'reader.page_of': 'de',
    'reader.protected_document': 'Documento Protegido',

    // Session Expiry
    'session.expired_title': 'Sesión Expirada',
    'session.expired_desc': 'Su sesión ha expirado por inactividad. Inicie sesión nuevamente para continuar.',
    'session.sign_in_again': 'Iniciar sesión nuevamente',
  },
  fr: {
    'nav.dashboard': 'Tableau de bord',
    'nav.flipbook_library': 'Bibliothèque Flipbook',
    'nav.access_history': 'Historique des accès',
    'nav.books_approval': 'Approbation des livres',
    'nav.organizations': 'Organisations',
    'nav.user_management': 'Gestion des utilisateurs',
    'nav.access_logs': 'Journaux d\'accès',
    'nav.masters': 'Données de base',
    'nav.subject_master': 'Matières',
    'nav.grade_master': 'Classes',
    'nav.settings': 'Paramètres',

    'library.subtitle': 'Gérer et consulter tous les livres numériques',
    'dashboard.subtitle': 'Aperçu des livres numériques et statistiques',
    'access_history.subtitle': 'Consulter les journaux de sessions',
    'books_approval.subtitle': 'Examiner les approbations de livres',
    'organizations.subtitle': 'Gérer les organisations et permissions',
    'user_management.subtitle': 'Gérer les utilisateurs et rôles',
    'access_logs.subtitle': 'Surveiller les activités des utilisateurs',
    'masters.subjects_subtitle': 'Gérer les matières académiques',
    'masters.grades_subtitle': 'Gérer les niveaux et classes',
    'masters.add_subject': 'Ajouter une matière',
    'masters.edit_subject': 'Modifier la matière',
    'masters.create_subject': 'Créer une matière',
    'masters.update_subject': 'Mettre à jour la matière',
    'masters.delete_subject_title': 'Supprimer la matière ?',
    'masters.delete_subject_desc': 'Cela supprimera définitivement',
    'masters.delete_subject_hint': 'Les matières associées à des livres ne peuvent pas être supprimées ; désactivez-les.',
    'masters.subject_name': 'Nom de la matière',
    'masters.enter_subject_name': 'Entrez le nom de la matière',
    'masters.subject_active_desc': 'Disponible lors de la sélection d’une matière pour un livre',
    'masters.subject_inactive_desc': 'Masqué de la sélection des matières',
    'masters.no_subjects_found': 'Aucune matière trouvée',
    'masters.loading_subjects': 'Chargement des matières...',
    'masters.try_different_search': 'Essayez un autre terme de recherche.',
    'masters.add_first_subject': 'Ajoutez votre première matière pour commencer.',
    'masters.subject_name_required': 'Le nom de la matière est obligatoire',
    'masters.subject_created_success': 'Matière créée avec succès',
    'masters.subject_updated_success': 'Matière mise à jour avec succès',
    'masters.subject_deleted_success': 'Matière supprimée avec succès',
    'masters.failed_load_subjects': 'Échec du chargement des matières',
    'masters.failed_save_subject': 'Échec de l’enregistrement de la matière',
    'masters.failed_delete_subject': 'Échec de la suppression de la matière',
    'masters.add_grade': 'Ajouter un niveau',
    'masters.edit_grade': 'Modifier le niveau',
    'masters.create_grade': 'Créer un niveau',
    'masters.update_grade': 'Mettre à jour le niveau',
    'masters.delete_grade_title': 'Supprimer le niveau ?',
    'masters.delete_grade_desc': 'Cela supprimera définitivement',
    'masters.delete_grade_hint': 'Les niveaux associés à des livres ne peuvent pas être supprimés ; désactivez-les.',
    'masters.grade_name': 'Nom du niveau',
    'masters.enter_grade_name': 'Entrez le nom du niveau',
    'masters.grade_active_desc': 'Disponible lors de la sélection d’un niveau pour un livre',
    'masters.grade_inactive_desc': 'Masqué de la sélection des niveaux',
    'masters.no_grades_found': 'Aucun niveau trouvé',
    'masters.loading_grades': 'Chargement des niveaux...',
    'masters.add_first_grade': 'Ajoutez votre premier niveau pour commencer.',
    'masters.grade_name_required': 'Le nom du niveau est obligatoire',
    'masters.grade_created_success': 'Niveau créé avec succès',
    'masters.grade_updated_success': 'Niveau mis à jour avec succès',
    'masters.grade_deleted_success': 'Niveau supprimé avec succès',
    'masters.failed_load_grades': 'Échec du chargement des niveaux',
    'masters.failed_save_grade': 'Échec de l’enregistrement du niveau',
    'masters.failed_delete_grade': 'Échec de la suppression du niveau',
    'masters.order_already_used': 'L’ordre est déjà utilisé. Veuillez choisir un ordre différent.',
    'filter.search_subjects': 'Rechercher des matières...',
    'filter.search_grades': 'Rechercher des niveaux...',
    'action.deleting': 'Suppression...',

    // Roles
    'role.super_admin': 'Super Administrateur',
    'role.admin': 'Administrateur',
    'role.admins': 'Administrateurs',
    'role.school_admin': 'Administrateur Scolaire',
    'role.approver_l1': 'Approbateur L1',
    'role.approver_l2': 'Approbateur L2',
    'role.publish_approver_l1': 'Approbateur de Publication Niveau 1',
    'role.publish_approver_l2': 'Approbateur de Publication Niveau 2',
    'role.delete_approver_l1': 'Approbateur de Suppression Niveau 1',
    'role.delete_approver_l2': 'Approbateur de Suppression Niveau 2',
    'role.content_creator': 'Créateur de Contenu',
    'role.reader': 'Lecteur',
    'role.student': 'Élève',
    'role.teacher': 'Enseignant',

    // User Management Modals & Tables
    'user_management.admins': 'Administrateurs',
    'user_management.delete_user': 'Supprimer l’utilisateur',
    'user_management.delete_user_desc': 'Êtes-vous sûr de vouloir supprimer',
    'user_management.cannot_undone': 'Cette action est irréversible.',
    'user_management.add_new_user': 'Ajouter un utilisateur',
    'user_management.edit_user': 'Modifier l’utilisateur',
    'user_management.create_user_desc': 'Créer un compte et attribuer des accès',
    'user_management.edit_user_desc': 'Mettre à jour les détails et permissions',
    'user_management.basic_info': 'Informations de base',
    'user_management.full_name': 'Nom complet',
    'user_management.email_address': 'Adresse e-mail',
    'user_management.password': 'Mot de passe',
    'user_management.new_password_keep': 'Nouveau mot de passe (laisser vide pour conserver)',
    'user_management.grade_optional': 'Niveau / Classe (optionnel)',
    'user_management.not_applicable': 'Non applicable',
    'user_management.organizations_assignment': 'Attribution d’organisations',
    'user_management.module_access': 'Accès aux modules',
    'user_management.module_access_desc': 'Par défaut selon le rôle — personnalisable',
    'user_management.force_login': 'Connexion forcée',
    'user_management.otp_history': 'Historique OTP',
    'user_management.all_orgs': 'Toutes les org.',
    'user_management.none': 'Aucun',

    // Access Log Actions & Groups
    'log.group_user': 'Utilisateur',
    'log.group_books': 'Livres',
    'log.group_other': 'Autre',
    'log.no_logs_found': 'Aucun journal trouvé.',
    'log.force_login_by': 'Connexion forcée par',
    'log.performed_by': 'Effectué par',
    'log.target': 'Cible',
    'log.filter_by_action': 'Filtrer par action',
    'log.action_login': 'Connexion',
    'log.action_logout': 'Déconnexion',
    'log.action_generate_login_otp': 'Générer OTP de connexion',
    'log.action_verify_login_otp': 'Vérifier OTP de connexion',
    'log.action_generate_reset_otp': 'Générer OTP de réinitialisation',
    'log.action_verify_reset_otp': 'Vérifier OTP de réinitialisation',
    'log.action_reset_password': 'Réinitialiser le mot de passe',
    'log.action_view_book': 'Voir le livre',
    'log.action_generate_otp': 'Générer OTP',
    'log.action_verify_otp': 'Vérifier OTP',
    'log.action_upload_book': 'Téléverser un livre',
    'log.action_update_book': 'Mettre à jour le livre',
    'log.action_publish_book': 'Publier le livre',
    'log.action_delete_book': 'Supprimer le livre',
    'log.action_toggle_otp': 'Basculer OTP',
    'log.action_add_access_user': 'Ajouter utilisateur d’accès',
    'log.action_remove_access_user': 'Supprimer utilisateur d’accès',
    'log.action_create_user': 'Créer un utilisateur',
    'log.action_update_user': 'Mettre à jour l’utilisateur',
    'log.action_delete_user': 'Supprimer l’utilisateur',
    'log.action_activate_version': 'Activer la version',
    'log.action_create_qr': 'Créer QR',
    'log.action_toggle_qr': 'Basculer QR',
    'log.action_delete_qr': 'Supprimer QR',
    'log.action_scan_qr': 'Scanner QR',

    'header.search_placeholder': 'Rechercher des livres, matières...',
    'header.switch_organization': 'Changer d\'organisation',
    'header.all_organizations': 'Toutes les organisations',
    'header.global_overview': 'Vue Globale',
    'header.all_assigned_orgs': 'Toutes les organisations assignées',
    'header.profile': 'Mon profil',
    'header.logout': 'Déconnexion',
    'header.super_admin': 'Super Administrateur',
    'header.admin': 'Administrateur',
    'header.notifications': 'Notifications',
    'notifications.new': 'Nouveau',
    'notifications.all': 'Toutes',
    'notifications.unread': 'Non lues',
    'notifications.approvals': 'Approbations',
    'notifications.books': 'Livres',
    'notifications.alerts': 'Alertes',
    'notifications.mark_all_read': 'Tout marquer comme lu',
    'notifications.clear_read': 'Effacer les notifications lues',
    'notifications.view_approvals': 'Voir toutes les approbations',
    'notifications.empty': 'Aucune notification trouvée',
    'notifications.caught_up': 'Vous êtes à jour !',
    'notifications.loading': 'Chargement des notifications...',

    'action.search': 'Rechercher',
    'action.filter': 'Filtrer',
    'action.reset': 'Réinitialiser',
    'action.clear_all': 'Tout effacer',
    'action.clear_filters': 'Effacer les filtres',
    'action.save': 'Enregistrer les modifications',
    'action.saving': 'Enregistrement...',
    'action.cancel': 'Annuler',
    'action.edit': 'Modifier',
    'action.delete': 'Supprimer',
    'action.actions': 'Actions',
    'action.upload': 'Téléverser',
    'action.upload_book': 'Téléverser un livre',
    'action.add_user': 'Ajouter un utilisateur',
    'action.add_organization': 'Ajouter une organisation',
    'action.create_organization': 'Créer une organisation',
    'action.add_subject': 'Ajouter une matière',
    'action.add_grade': 'Ajouter une classe',
    'action.create': 'Créer',
    'action.status': 'Statut',
    'action.back': 'Retour',
    'action.confirm': 'Confirmer',
    'action.close': 'Fermer',
    'action.send': 'Envoyer',
    'action.sending': 'Envoi en cours...',
    'action.copy_link': 'Copier le lien',
    'action.copy': 'Copier',
    'action.copied': 'Copié !',
    'action.view': 'Voir',
    'action.preview': 'Aperçu',
    'action.approve': 'Approuver',
    'action.approving': 'Approbation en cours...',
    'action.send_back': 'Renvoyer',
    'action.reject': 'Rejeter',
    'action.history': 'Historique',
    'action.versions': 'Versions',
    'action.version_history': 'Historique des versions',
    'action.replace': 'Remplacer',
    'action.replace_book': 'Remplacer le livre',
    'action.qr_code': 'Code QR',
    'action.download_qr': 'Télécharger le code QR',
    'action.download_pdf': 'Télécharger le PDF',
    'action.refresh': 'Actualiser',
    'action.export': 'Exporter',
    'action.export_csv': 'Exporter CSV',
    'action.import': 'Importer',
    'action.view_all': 'Tout voir',
    'action.show_more': 'Afficher plus',
    'action.collapse': 'Réduire',
    'action.expand': 'Agrandir',
    'action.submit_l2': 'Soumettre au niveau 2',
    'action.publish': 'Publier',
    'action.publishing': 'Publication...',
    'action.activate': 'Activer',
    'action.deactivate': 'Désactiver',
    'action.archive': 'Archiver',
    'action.edit_book': 'Modifier le livre',
    'action.delete_book': 'Supprimer le livre',
    'action.request_delete': 'Demander la suppression',
    'action.no_books_found': 'Aucun livre trouvé',
    'action.try_adjusting_filters': 'Essayez d\'ajuster vos critères',
    'action.upload_first_book': 'Téléversez votre premier livre',
    'action.select_all': 'Tout sélectionner',
    'action.clear_selection': 'Effacer la sélection',
    'action.by': 'par',

    'pagination.showing': 'Affichage de',
    'pagination.of': 'sur',
    'pagination.rows_per_page': 'Lignes par page',
    'pagination.previous_page': 'Page précédente',
    'pagination.next_page': 'Page suivante',
    'pagination.books': 'livres',
    'pagination.logs': 'journaux',
    'pagination.records': 'enregistrements',
    'pagination.entries': 'entrées',
    'pagination.items': 'éléments',
    'pagination.prev': 'Préc',
    'pagination.next': 'Suiv',

    'filter.all_statuses': 'Tous les statuts',
    'filter.all_grades': 'Toutes les classes',
    'filter.all_subjects': 'Toutes les matières',
    'filter.all_roles': 'Tous les rôles',
    'filter.all_actions': 'Toutes les actions',
    'filter.search_books': 'Rechercher titre, matière...',
    'filter.search_users': 'Rechercher nom, email...',
    'filter.search_logs': 'Rechercher journaux...',
    'filter.search_organizations': 'Rechercher organisations...',
    'filter.search_reviewers': 'Rechercher réviseurs...',
    'filter.search_approvals': 'Rechercher titre, demandeur, classe...',

    'status.active': 'Actif',
    'status.inactive': 'Inactif',
    'status.draft': 'Brouillon',
    'status.published': 'Publié',
    'status.processing': 'En traitement',
    'status.pending_approval': 'En attente d\'approbation',
    'status.pending_l1': 'En attente d\'approbation L1',
    'status.pending': 'En attente',
    'status.ready_for_l2': 'Prêt pour L2',
    'status.awaiting_l2': 'En attente L2',
    'status.pending_l2': 'En attente d\'approbation L2',
    'status.approved': 'Approuvé',
    'status.pending_publish': 'En attente de publication',
    'status.archived': 'Archivé',
    'status.expired': 'Expiré',
    'status.rejected': 'Renvoyé',
    'status.cancelled': 'Annulé',
    'status.success': 'Succès',
    'status.failed': 'Échoué',
    'status.all': 'Tous',

    // Approvals, Reviews & Reviewers
    'approval.total_requests': 'Total des demandes',
    'approval.book_approval': 'Approbation des livres',
    'approval.delete_requests': 'Demandes de suppression',
    'approval.level_1': 'Niveau 1',
    'approval.level_2': 'Niveau 2',
    'approval.approval': 'Approbation',
    'approval.reviewers_l1': 'Réviseurs de niveau 1',
    'approval.reviewers_l2': 'Réviseurs de niveau 2',
    'approval.delete_reviewers_l1': 'Réviseurs de suppression niveau 1',
    'approval.delete_reviewers_l2': 'Réviseurs de suppression niveau 2',
    'approval.replacement_reviewers_l1': 'Réviseurs de remplacement niveau 1',
    'approval.replacement_reviewers_l2': 'Réviseurs de remplacement niveau 2',
    'approval.publishers': 'Éditeurs',
    'approval.responsible_persons': 'Personnes responsables',
    'approval.selected': 'sélectionnés',
    'approval.no_reviewers_found': 'Aucun réviseur trouvé',
    'approval.no_matches': 'Aucune correspondance',
    'approval.select_l1_desc': 'Sélectionnez les approbateurs de niveau 1. Une seule approbation fait passer ce livre au niveau 2.',
    'approval.select_l2_desc': 'Sélectionnez les approbateurs de niveau 2. Une seule approbation publie ce livre automatiquement.',
    'approval.sent_back_for_improvement': 'Renvoyé pour amélioration',
    'approval.no_feedback_note': 'Aucune note de retour n’a été fournie. Remplacez le PDF ou modifiez les détails, il sera réassigné automatiquement.',
    'approval.delete_pending': 'Suppression en attente',
    'approval.ready_for_l2_delete': 'Prêt pour suppression L2',
    'approval.replacement_pending': 'Remplacement en attente',
    'approval.replacement': 'Remplacement',
    'approval.no_approvals': 'Aucune approbation en attente',
    'approval.no_delete_requests': 'Aucune demande de suppression',
    'approval.review_book': 'Examiner le livre',
    'approval.review_book_subtitle': 'Afficher le flipbook, puis approuver ou rejeter',
    'approval.loading_book': 'Chargement du livre...',
    'approval.request_not_found': 'Demande d\'approbation non trouvée',
    'approval.why_approved': 'Pourquoi approuvé',
    'approval.send_back_for_improvement': 'Renvoyer pour amélioration',
    'approval.pdf_not_available': 'PDF non disponible',
    'approval.pdf_not_available_desc': 'Ce livre n\'a pas encore de PDF visualisable.',
    'approval.book_preview': 'Aperçu du livre',
    'approval.replacement_preview_hint': 'PDF de remplacement — la version publiée reste en ligne jusqu\'à approbation',
    'approval.loading_preview': 'Chargement de l\'aperçu...',
    'approval.book_not_found': 'Livre non trouvé',
    'approval.note_from_sender': 'Note de l\'expéditeur',
    'approval.publish_now_desc': 'Ce livre est déjà approuvé au niveau 2. Le publier le rendra immédiatement disponible.',
    'action.send_delete_l2': 'Envoyer pour suppression de niveau 2',
    'action.send_for': 'Envoyer pour',
    'action.back_to_list': 'Retour à la liste',

    // Grades & Standards
    'grade.nursery': 'Maternelle',
    'grade.lkg': 'LKG',
    'grade.ukg': 'UKG',
    'grade.grade_prefix': 'Niveau',

    // Common Subjects
    'subject.mathematics': 'Mathématiques',
    'subject.economics': 'Économie',
    'subject.science': 'Sciences',
    'subject.english': 'Anglais',
    'subject.hindi': 'Hindi',
    'subject.gujarati': 'Gujarati',
    'subject.marathi': 'Marathi',
    'subject.physics': 'Physique',
    'subject.chemistry': 'Chimie',
    'subject.biology': 'Biologie',
    'subject.history': 'Histoire',
    'subject.geography': 'Géographie',
    'subject.computer_science': 'Informatique',

    // Organizations Modals & Actions
    'org.create_organization': 'Créer une nouvelle organisation',
    'org.edit_organization': 'Modifier l’organisation',
    'org.configure_desc': 'Configurer les détails, les couleurs et le logo de l’organisation',
    'org.activate_organization': 'Activer l’organisation',
    'org.deactivate_organization': 'Désactiver l’organisation',

    // Book Upload & Edit
    'action.submit_for_approval': 'Soumettre pour Approbation',
    'action.save_changes': 'Enregistrer les Modifications',
    'upload.page_title': 'Téléverser un Nouveau Livre',
    'upload.edit_page_title': 'Modifier le Livre',
    'upload.subtitle': 'Convertir un PDF en un flipbook HTML5 interactif',
    'upload.edit_subtitle': 'Mettre à jour les détails du livre ou remplacer le fichier source PDF',
    'upload.pdf_source_file': 'Fichier Source PDF',
    'upload.optional_replace': 'Optionnel — téléverser uniquement pour remplacer',
    'upload.drop_pdf': 'Déposez votre PDF ici, ou',
    'upload.browse': 'parcourir',
    'upload.pdf_limit': 'Fichiers PDF uniquement — {size} Mo max par téléversement',
    'upload.thumbnail_title': 'Vignette du Livre',
    'upload.drop_image': 'Déposez votre image ici, ou',
    'upload.image_limit': 'JPG, PNG ou WebP — 2 Mo max · laisser vide pour utiliser la page 1 du PDF',
    'upload.current_thumbnail': 'Vignette actuelle enregistrée',
    'upload.replace_image': 'Remplacer l’image',
    'upload.replace_pdf': 'Remplacer le PDF',
    'upload.org_title': 'Organisation',
    'upload.org_edit_desc': 'L’organisation ne peut plus être modifiée après la création du livre.',
    'upload.org_desc': 'Sélectionnez l’organisation à laquelle ce livre appartient.',
    'upload.book_info': 'Informations sur le Livre',
    'upload.book_name': 'Nom du Livre',
    'upload.book_name_desc': 'Utilisez le titre officiel complet tel qu’il apparaîtra dans la visionneuse',
    'upload.book_name_placeholder': 'ex. Mathematics Foundations — Grade 5, Term 1',
    'upload.publisher': 'Éditeur',
    'upload.publisher_desc': 'Maison d’édition ou source de contenu pour ce livre',
    'upload.grade': 'Niveau / Classe',
    'upload.grade_desc': 'Le niveau visé pour ce livre',
    'upload.subject': 'Matière',
    'upload.subject_desc': 'Catégorie de matière académique',
    'upload.expiry_date': 'Date d’Expiration',
    'upload.expiry_date_desc': 'Le flipbook ne sera plus accessible après cette date.',
    'upload.description': 'Description',
    'upload.description_desc': 'Bref résumé affiché aux parents et établissements avant de demander l’accès',
    'upload.description_placeholder': 'Décrivez le contenu du livre, le public visé et les thèmes clés...',
    'upload.access_control': 'Contrôle d’Accès',
    'upload.access_notes': 'Notes d’Accès',
    'upload.access_notes_desc': 'Notes internes sur les personnes autorisées (non affiché publiquement)',
    'upload.approval_banner_title': 'Approbation à 2 niveaux requise',
    'upload.approval_banner_desc': 'Soumettre pour approbation envoie ce livre à tous les approbateurs de niveau 1. Après une approbation de niveau 1, il est envoyé automatiquement au niveau 2. Une fois approuvé au niveau 2, le livre est publié.',
    'upload.submitted_notice': 'Les livres soumis vont à tous les approbateurs de niveau 1, puis de niveau 2',
    'upload.live_notice': 'La version publiée actuelle reste en ligne jusqu’à ce que le niveau 2 approuve le nouveau PDF',
    'upload.save_notice': 'Enregistrez les métadonnées à tout moment. Le remplacement du PDF nécessite une approbation.',

    'col.book_title': 'Titre du livre',
    'col.grade': 'Classe',
    'col.subject': 'Matière',
    'col.status': 'Statut',
    'col.pages': 'Pages',
    'col.uploaded': 'Téléversé le',
    'col.uploaded_by': 'Téléversé par',
    'col.expiry': 'Expiration',
    'col.views': 'Vues',
    'col.link': 'Lien',
    'col.name': 'Nom',
    'col.email': 'E-mail',
    'col.role': 'Rôle',
    'col.organization': 'Organisation',
    'col.permissions': 'Permissions',
    'col.created_date': 'Date de création',
    'col.last_login': 'Dernière connexion',
    'col.ip_address': 'Adresse IP',
    'col.device': 'Appareil',
    'col.action': 'Action',
    'col.resource': 'Ressource',
    'col.timestamp': 'Horodatage',
    'col.details': 'Détails',
    'col.order': 'Ordre',
    'col.requested_by': 'Demandé par',
    'col.sent_at': 'Envoyé à',
    'col.level': 'Niveau',
    'col.approval': 'Approbation',

    'dashboard.title': 'Tableau de bord',
    'dashboard.total_books': 'Total des livres',
    'dashboard.total_users': 'Total des utilisateurs',
    'dashboard.total_views': 'Total des vues de livres',
    'dashboard.active_users': 'Utilisateurs actifs',
    'dashboard.avg_read_time': 'Temps de lecture moyen',
    'dashboard.active_flipbooks': 'Flipbooks actifs',
    'dashboard.expired_today': 'Expirés aujourd\'hui',
    'dashboard.otp_generated': 'Demandes OTP (24h)',
    'dashboard.processing_queue': 'File de traitement',
    'dashboard.storage_used': 'Stockage PDF utilisé',
    'dashboard.published_books': 'Livres publiés',
    'dashboard.draft_books': 'Livres brouillons',
    'dashboard.expired_books': 'Livres expirés',
    'dashboard.recent_activity': 'Activité récente',
    'dashboard.top_performing': 'Livres les plus lus',
    'dashboard.views_trend': 'Tendance des vues',
    'dashboard.books_by_grade': 'Livres par classe',
    'dashboard.books_by_subject': 'Livres par matière',
    'dashboard.trends_distribution': 'Tendances et répartition',
    'dashboard.otp_trend': 'Tendance des demandes OTP',
    'dashboard.otp_trend_sub': '14 derniers jours · accès flipbook',
    'dashboard.books_by_grade_sub': 'Répartition par classes',
    'dashboard.latest_events': 'Derniers événements',
    'dashboard.today': 'Aujourd\'hui',
    'dashboard.distribution_subjects': 'Répartition par matières',
    'dashboard.analytics_heading': 'Analytique — Vues, OTP et utilisateurs',
    'dashboard.monthly_activity_trend': 'Tendance mensuelle d\'activité',
    'dashboard.views_by_subject': 'Vues par matière',
    'dashboard.books_by_grade_group': 'Livres par groupe de classes',
    'dashboard.unique_otp_users': 'Utilisateurs uniques OTP',
    'dashboard.this_month': 'ce mois-ci',
    'dashboard.no_recent_activity': 'Aucune activité récente',
    'dashboard.no_subject_data': 'Aucune donnée de matière',
    'dashboard.no_grade_data': 'Aucune donnée de classe',
    'dashboard.no_books_yet': 'Aucun livre pour le moment',
    'dashboard.page_title': 'Tableau de bord et analytique',
    'dashboard.main_subtitle': 'Aperçu de la bibliothèque, métriques d’activité et analyses d’utilisation',
    'dashboard.needs_renewal': 'Renouvellement requis',
    'dashboard.none_today': 'Aucun aujourd’hui',
    'dashboard.vs_previous_24h': 'vs dernières 24h',
    'dashboard.pdf_processing_pending': 'Traitement PDF en attente',
    'dashboard.queue_clear': 'File d’attente vide',
    'dashboard.from_uploaded_books': 'À partir des fichiers téléchargés',
    'dashboard.all_time': 'Tout le temps',
    'dashboard.last_7_days': '7 derniers jours',
    'dashboard.per_session': 'Par session',
    'dashboard.monthly_activity_sub': 'Vues de livres, OTP d’accès et utilisateurs actifs',
    'dashboard.grades_count': 'niveaux',
    'dashboard.grade_single': 'niveau',
    'dashboard.otp_requests': 'Demandes OTP',
    'dashboard.books_count': 'Livres',
    'dashboard.chart_views': 'Vues',
    'dashboard.chart_otps': 'OTPs',
    'dashboard.chart_users': 'Utilisateurs',
    'dashboard.chart_share': 'Part',
    'dashboard.action_required': 'Action',
    'dashboard.activity_upload': 'Livre téléversé',
    'dashboard.activity_otp': 'OTP d’accès',
    'dashboard.activity_expired': 'Livre expiré',
    'dashboard.activity_view': 'Vue du livre',
    'header.global_overview_desc': 'Aperçu global et données agrégées',
    'header.all_assigned_orgs_desc': 'Toutes les organisations assignées et données agrégées',
    'date.just_now': 'À l’instant',
    'date.yesterday': 'Hier',
    'date.m_ago': 'min',
    'date.h_ago': 'h',
    'date.d_ago': 'j',


    'history.visitors': 'Visiteurs',
    'history.view_events': 'Événements de vue',
    'history.read_sessions': 'Sessions de lecture',
    'history.otp_history': 'Historique OTP',
    'history.reader_user': 'Lecteur / Utilisateur',
    'history.total_read_time': 'Temps total de lecture',
    'history.first_access': 'Premier accès',
    'history.last_access': 'Dernier accès',
    'history.latest_ip': 'Dernière IP',
    'history.books_accessed': 'Livres consultés',

    'settings.title': 'Paramètres',
    'settings.subtitle': 'Gérez vos préférences et la configuration du système',
    'settings.tab_general': 'Paramètres généraux',
    'settings.tab_notifications': 'Préférences de notification',
    'settings.tab_security': 'Paramètres de sécurité',
    'settings.tab_flipbook': 'Paramètres Flipbook',
    'settings.tab_email': 'Paramètres d\'e-mail',
    'settings.interface_language': 'Langue de l\'interface',
    'settings.interface_language_desc': 'Sélectionnez la langue d\'affichage de l\'interface',
    'settings.timezone': 'Fuseau horaire',
    'settings.timezone_desc': 'Les dates et heures s\'afficheront dans votre fuseau horaire',
    'settings.admin_email': 'E-mail de l\'administrateur',
    'settings.admin_email_desc': 'E-mail de contact principal pour les notifications',
    'settings.organization_name': 'Nom de l\'organisation',
    'settings.save_success': 'Paramètres enregistrés avec succès',

    'reader.loading_flipbook': 'Chargement du flipbook…',
    'reader.loading_page': 'Chargement de la page...',
    'reader.read_book': 'Lire le livre',
    'reader.thumbnails': 'Vignettes',
    'reader.search': 'Rechercher dans le livre',
    'reader.zoom_in': 'Zoom avant',
    'reader.zoom_out': 'Zoom arrière',
    'reader.fullscreen': 'Plein écran',
    'reader.exit_fullscreen': 'Quitter le plein écran',
    'reader.sound_on': 'Son activé',
    'reader.sound_off': 'Son désactivé',
    'reader.auto_play': 'Lecture automatique',
    'reader.pause': 'Pause',
    'reader.bookmarks': 'Signets',
    'reader.add_bookmark': 'Ajouter aux signets',
    'reader.remove_bookmark': 'Supprimer le signet',
    'reader.page_of': 'sur',
    'reader.protected_document': 'Document protégé',

    // Session Expiry
    'session.expired_title': 'Session Expirée',
    'session.expired_desc': 'Votre session a expiré en raison de votre inactivité. Veuillez vous reconnecter pour continuer.',
    'session.sign_in_again': 'Se reconnecter',
  },
  de: {
    'nav.dashboard': 'Dashboard',
    'nav.flipbook_library': 'Flipbook-Bibliothek',
    'nav.access_history': 'Zugriffsverlauf',
    'nav.books_approval': 'Buchgenehmigung',
    'nav.organizations': 'Organisationen',
    'nav.user_management': 'Benutzerverwaltung',
    'nav.access_logs': 'Zugriffsprotokolle',
    'nav.masters': 'Stammdaten',
    'nav.subject_master': 'Fächer',
    'nav.grade_master': 'Klassenstufen',
    'nav.settings': 'Einstellungen',

    'library.subtitle': 'Alle digitalen Bücher verwalten und ansehen',
    'dashboard.subtitle': 'Übersicht über digitale Bücher und Statistiken',
    'access_history.subtitle': 'Lesersitzungsprotokolle einsehen',
    'books_approval.subtitle': 'Eingereichte Buchgenehmigungen bearbeiten',
    'organizations.subtitle': 'Organisationen und Berechtigungen verwalten',
    'user_management.subtitle': 'Benutzerkonten und Rollen verwalten',
    'access_logs.subtitle': 'Benutzeraktivitäten überwachen',
    'masters.subjects_subtitle': 'Akademische Fächer verwalten',
    'masters.grades_subtitle': 'Klassenstufen verwalten',
    'masters.add_subject': 'Fach hinzufügen',
    'masters.edit_subject': 'Fach bearbeiten',
    'masters.create_subject': 'Fach erstellen',
    'masters.update_subject': 'Fach aktualisieren',
    'masters.delete_subject_title': 'Fach löschen?',
    'masters.delete_subject_desc': 'Dadurch wird dauerhaft gelöscht:',
    'masters.delete_subject_hint': 'Büchern zugewiesene Fächer können nicht gelöscht werden; deaktivieren Sie sie stattdessen.',
    'masters.subject_name': 'Fachname',
    'masters.enter_subject_name': 'Fachnamen eingeben',
    'masters.subject_active_desc': 'Verfügbar bei der Fachauswahl für ein Buch',
    'masters.subject_inactive_desc': 'In der Fachauswahl ausgeblendet',
    'masters.no_subjects_found': 'Keine Fächer gefunden',
    'masters.loading_subjects': 'Fächer werden geladen...',
    'masters.try_different_search': 'Versuchen Sie einen anderen Suchbegriff.',
    'masters.add_first_subject': 'Fügen Sie Ihr erstes Fach hinzu.',
    'masters.subject_name_required': 'Fachname ist erforderlich',
    'masters.subject_created_success': 'Fach erfolgreich erstellt',
    'masters.subject_updated_success': 'Fach erfolgreich aktualisiert',
    'masters.subject_deleted_success': 'Fach erfolgreich gelöscht',
    'masters.failed_load_subjects': 'Fächer konnten nicht geladen werden',
    'masters.failed_save_subject': 'Fach konnte nicht gespeichert werden',
    'masters.failed_delete_subject': 'Fach konnte nicht gelöscht werden',
    'masters.add_grade': 'Klasse hinzufügen',
    'masters.edit_grade': 'Klasse bearbeiten',
    'masters.create_grade': 'Klasse erstellen',
    'masters.update_grade': 'Klasse aktualisieren',
    'masters.delete_grade_title': 'Klasse löschen?',
    'masters.delete_grade_desc': 'Dadurch wird dauerhaft gelöscht:',
    'masters.delete_grade_hint': 'Büchern zugewiesene Klassen können nicht gelöscht werden; deaktivieren Sie sie stattdessen.',
    'masters.grade_name': 'Klassenname',
    'masters.enter_grade_name': 'Klassennamen eingeben',
    'masters.grade_active_desc': 'Verfügbar bei der Klassenauswahl für ein Buch',
    'masters.grade_inactive_desc': 'In der Klassenauswahl ausgeblendet',
    'masters.no_grades_found': 'Keine Klassen gefunden',
    'masters.loading_grades': 'Klassen werden geladen...',
    'masters.add_first_grade': 'Fügen Sie Ihre erste Klasse hinzu.',
    'masters.grade_name_required': 'Klassenname ist erforderlich',
    'masters.grade_created_success': 'Klasse erfolgreich erstellt',
    'masters.grade_updated_success': 'Klasse erfolgreich aktualisiert',
    'masters.grade_deleted_success': 'Klasse erfolgreich gelöscht',
    'masters.failed_load_grades': 'Klassen konnten nicht geladen werden',
    'masters.failed_save_grade': 'Klasse konnte nicht gespeichert werden',
    'masters.failed_delete_grade': 'Klasse konnte nicht gelöscht werden',
    'masters.order_already_used': 'Reihenfolge wird bereits verwendet. Bitte wählen Sie eine andere.',
    'filter.search_subjects': 'Fächer suchen...',
    'filter.search_grades': 'Klassen suchen...',
    'action.deleting': 'Wird gelöscht...',

    // Roles
    'role.super_admin': 'Super-Administrator',
    'role.admin': 'Administrator',
    'role.admins': 'Administratoren',
    'role.school_admin': 'Schuladministrator',
    'role.approver_l1': 'Prüfer L1',
    'role.approver_l2': 'Prüfer L2',
    'role.publish_approver_l1': 'Veröffentlichungsprüfer Stufe 1',
    'role.publish_approver_l2': 'Veröffentlichungsprüfer Stufe 2',
    'role.delete_approver_l1': 'Löschungsprüfer Stufe 1',
    'role.delete_approver_l2': 'Löschungsprüfer Stufe 2',
    'role.content_creator': 'Inhaltsersteller',
    'role.reader': 'Leser',
    'role.student': 'Schüler',
    'role.teacher': 'Lehrer',

    // User Management Modals & Tables
    'user_management.admins': 'Administratoren',
    'user_management.delete_user': 'Benutzer löschen',
    'user_management.delete_user_desc': 'Möchten Sie wirklich löschen',
    'user_management.cannot_undone': 'Diese Aktion kann nicht rückgängig gemacht werden.',
    'user_management.add_new_user': 'Neuen Benutzer hinzufügen',
    'user_management.edit_user': 'Benutzer bearbeiten',
    'user_management.create_user_desc': 'Neues Benutzerkonto erstellen und Zugriff zuweisen',
    'user_management.edit_user_desc': 'Benutzerdetails und Berechtigungen aktualisieren',
    'user_management.basic_info': 'Basisinformationen',
    'user_management.full_name': 'Vollständiger Name',
    'user_management.email_address': 'E-Mail-Adresse',
    'user_management.password': 'Passwort',
    'user_management.new_password_keep': 'Neues Passwort (leer lassen zum Beibehalten)',
    'user_management.grade_optional': 'Klasse (optional)',
    'user_management.not_applicable': 'Nicht zutreffend',
    'user_management.organizations_assignment': 'Organisationszuweisung',
    'user_management.module_access': 'Modulzugriff',
    'user_management.module_access_desc': 'Standard nach Rolle — nach Bedarf anpassen',
    'user_management.force_login': 'Erzwungene Anmeldung',
    'user_management.otp_history': 'OTP-Verlauf',
    'user_management.all_orgs': 'Alle Org.',
    'user_management.none': 'Keine',

    // Access Log Actions & Groups
    'log.group_user': 'Benutzer',
    'log.group_books': 'Bücher',
    'log.group_other': 'Andere',
    'log.no_logs_found': 'Keine Protokolle gefunden.',
    'log.force_login_by': 'Erzwungene Anmeldung durch',
    'log.performed_by': 'Durchgeführt von',
    'log.target': 'Ziel',
    'log.filter_by_action': 'Nach Aktion filtern',
    'log.action_login': 'Anmelden',
    'log.action_logout': 'Abmelden',
    'log.action_generate_login_otp': 'Anmelde-OTP generieren',
    'log.action_verify_login_otp': 'Anmelde-OTP verifizieren',
    'log.action_generate_reset_otp': 'Reset-OTP generieren',
    'log.action_verify_reset_otp': 'Reset-OTP verifizieren',
    'log.action_reset_password': 'Passwort zurücksetzen',
    'log.action_view_book': 'Buch ansehen',
    'log.action_generate_otp': 'OTP generieren',
    'log.action_verify_otp': 'OTP verifizieren',
    'log.action_upload_book': 'Buch hochladen',
    'log.action_update_book': 'Buch aktualisieren',
    'log.action_publish_book': 'Buch veröffentlichen',
    'log.action_delete_book': 'Buch löschen',
    'log.action_toggle_otp': 'OTP umschalten',
    'log.action_add_access_user': 'Zugriffsbenutzer hinzufügen',
    'log.action_remove_access_user': 'Zugriffsbenutzer entfernen',
    'log.action_create_user': 'Benutzer erstellen',
    'log.action_update_user': 'Benutzer aktualisieren',
    'log.action_delete_user': 'Benutzer löschen',
    'log.action_activate_version': 'Version aktivieren',
    'log.action_create_qr': 'QR erstellen',
    'log.action_toggle_qr': 'QR umschalten',
    'log.action_delete_qr': 'QR löschen',
    'log.action_scan_qr': 'QR scannen',

    'header.search_placeholder': 'Bücher, Fächer suchen...',
    'header.switch_organization': 'Organisation wechseln',
    'header.all_organizations': 'Alle Organisationen',
    'header.global_overview': 'Globale Übersicht',
    'header.all_assigned_orgs': 'Alle zugewiesenen Organisationen',
    'header.profile': 'Mein Profil',
    'header.logout': 'Abmelden',
    'header.super_admin': 'Super-Administrator',
    'header.admin': 'Administrator',
    'header.notifications': 'Benachrichtigungen',
    'notifications.new': 'Neu',
    'notifications.all': 'Alle',
    'notifications.unread': 'Ungelesen',
    'notifications.approvals': 'Genehmigungen',
    'notifications.books': 'Bücher',
    'notifications.alerts': 'Warnungen',
    'notifications.mark_all_read': 'Alle als gelesen markieren',
    'notifications.clear_read': 'Gelesene löschen',
    'notifications.view_approvals': 'Alle Genehmigungen anzeigen',
    'notifications.empty': 'Keine Benachrichtigungen gefunden',
    'notifications.caught_up': 'Alles auf dem neuesten Stand!',
    'notifications.loading': 'Benachrichtigungen werden geladen...',

    'action.search': 'Suchen',
    'action.filter': 'Filtern',
    'action.reset': 'Zurücksetzen',
    'action.clear_all': 'Alles löschen',
    'action.clear_filters': 'Filter zurücksetzen',
    'action.save': 'Änderungen speichern',
    'action.saving': 'Wird gespeichert...',
    'action.cancel': 'Abbrechen',
    'action.edit': 'Bearbeiten',
    'action.delete': 'Löschen',
    'action.actions': 'Aktionen',
    'action.upload': 'Hochladen',
    'action.upload_book': 'Buch hochladen',
    'action.add_user': 'Benutzer hinzufügen',
    'action.add_organization': 'Organisation hinzufügen',
    'action.create_organization': 'Organisation erstellen',
    'action.add_subject': 'Fach hinzufügen',
    'action.add_grade': 'Klassenstufe hinzufügen',
    'action.create': 'Erstellen',
    'action.status': 'Status',
    'action.back': 'Zurück',
    'action.confirm': 'Bestätigen',
    'action.close': 'Schließen',
    'action.send': 'Senden',
    'action.sending': 'Wird gesendet...',
    'action.copy_link': 'Link kopieren',
    'action.copy': 'Kopieren',
    'action.copied': 'Kopiert!',
    'action.view': 'Ansehen',
    'action.preview': 'Vorschau',
    'action.approve': 'Genehmigen',
    'action.approving': 'Wird genehmigt...',
    'action.send_back': 'Zurücksenden',
    'action.reject': 'Ablehnen',
    'action.history': 'Verlauf',
    'action.versions': 'Versionen',
    'action.version_history': 'Versionsverlauf',
    'action.replace': 'Ersetzen',
    'action.replace_book': 'Buch ersetzen',
    'action.qr_code': 'QR-Code',
    'action.download_qr': 'QR-Code herunterladen',
    'action.download_pdf': 'PDF herunterladen',
    'action.refresh': 'Aktualisieren',
    'action.export': 'Exportieren',
    'action.export_csv': 'CSV exportieren',
    'action.import': 'Importieren',
    'action.view_all': 'Alle anzeigen',
    'action.show_more': 'Mehr anzeigen',
    'action.collapse': 'Einklappen',
    'action.expand': 'Ausklappen',
    'action.submit_l2': 'Für Stufe 2 einreichen',
    'action.publish': 'Veröffentlichen',
    'action.publishing': 'Wird veröffentlicht...',
    'action.activate': 'Aktivieren',
    'action.deactivate': 'Deaktivieren',
    'action.archive': 'Archivieren',
    'action.edit_book': 'Buch bearbeiten',
    'action.delete_book': 'Buch löschen',
    'action.request_delete': 'Löschung beantragen',
    'action.no_books_found': 'Keine Bücher gefunden',
    'action.try_adjusting_filters': 'Passen Sie Ihre Such- oder Filterkriterien an',
    'action.upload_first_book': 'Laden Sie Ihr erstes Buch hoch',
    'action.select_all': 'Alle auswählen',
    'action.clear_selection': 'Auswahl aufheben',
    'action.by': 'von',

    'pagination.showing': 'Zeige',
    'pagination.of': 'von',
    'pagination.rows_per_page': 'Zeilen pro Seite',
    'pagination.previous_page': 'Vorherige Seite',
    'pagination.next_page': 'Nächste Seite',
    'pagination.books': 'Bücher',
    'pagination.logs': 'Protokolle',
    'pagination.records': 'Datensätze',
    'pagination.entries': 'Einträge',
    'pagination.items': 'Elemente',
    'pagination.prev': 'Zurück',
    'pagination.next': 'Weiter',

    'filter.all_statuses': 'Alle Status',
    'filter.all_grades': 'Alle Klassen',
    'filter.all_subjects': 'Alle Fächer',
    'filter.all_roles': 'Alle Rollen',
    'filter.all_actions': 'Alle Aktionen',
    'filter.search_books': 'Titel, Fach suchen...',
    'filter.search_users': 'Name, E-Mail suchen...',
    'filter.search_logs': 'Protokolle suchen...',
    'filter.search_organizations': 'Organisationen suchen...',
    'filter.search_reviewers': 'Prüfer suchen...',
    'filter.search_approvals': 'Titel, Antragsteller suchen...',

    'status.active': 'Aktiv',
    'status.inactive': 'Inaktiv',
    'status.draft': 'Entwurf',
    'status.published': 'Veröffentlicht',
    'status.processing': 'In Bearbeitung',
    'status.pending_approval': 'Ausstehende Genehmigung',
    'status.pending_l1': 'Ausstehende Genehmigung L1',
    'status.pending': 'Ausstehend',
    'status.ready_for_l2': 'Bereit für L2',
    'status.awaiting_l2': 'Wartet auf L2',
    'status.pending_l2': 'Ausstehende Genehmigung L2',
    'status.approved': 'Genehmigt',
    'status.pending_publish': 'Ausstehende Veröffentlichung',
    'status.archived': 'Archiviert',
    'status.expired': 'Abgelaufen',
    'status.rejected': 'Zurückgesendet',
    'status.cancelled': 'Abgebrochen',
    'status.success': 'Erfolg',
    'status.failed': 'Fehlgeschlagen',
    'status.all': 'Alle',

    // Approvals, Reviews & Reviewers
    'approval.total_requests': 'Anfragen gesamt',
    'approval.book_approval': 'Buchgenehmigung',
    'approval.delete_requests': 'Löschanfragen',
    'approval.level_1': 'Stufe 1',
    'approval.level_2': 'Stufe 2',
    'approval.approval': 'Genehmigung',
    'approval.reviewers_l1': 'Prüfer der Stufe 1',
    'approval.reviewers_l2': 'Prüfer der Stufe 2',
    'approval.delete_reviewers_l1': 'Löschungsprüfer Stufe 1',
    'approval.delete_reviewers_l2': 'Löschungsprüfer Stufe 2',
    'approval.replacement_reviewers_l1': 'Ersatzprüfer Stufe 1',
    'approval.replacement_reviewers_l2': 'Ersatzprüfer Stufe 2',
    'approval.publishers': 'Verleger',
    'approval.responsible_persons': 'Verantwortliche',
    'approval.selected': 'ausgewählt',
    'approval.no_reviewers_found': 'Keine Prüfer gefunden',
    'approval.no_matches': 'Keine Treffer',
    'approval.select_l1_desc': 'Wählen Sie Prüfer der Stufe 1. Eine einzige Genehmigung verschiebt dieses Buch auf Stufe 2.',
    'approval.select_l2_desc': 'Wählen Sie Prüfer der Stufe 2. Eine einzige Genehmigung veröffentlicht dieses Buch automatisch.',
    'approval.sent_back_for_improvement': 'Zur Überarbeitung zurückgesendet',
    'approval.no_feedback_note': 'Keine Feedback-Notiz bereitgestellt. Ersetzen Sie das PDF oder bearbeiten Sie die Details, dann wird es automatisch neu zugewiesen.',
    'approval.delete_pending': 'Löschung ausstehend',
    'approval.ready_for_l2_delete': 'Bereit für L2-Löschung',
    'approval.replacement_pending': 'Ersatz ausstehend',
    'approval.replacement': 'Ersatz',
    'approval.no_approvals': 'Keine ausstehenden Genehmigungen',
    'approval.no_delete_requests': 'Keine Löschanfragen',
    'approval.review_book': 'Buch überprüfen',
    'approval.review_book_subtitle': 'Flipbook ansehen, dann genehmigen oder ablehnen',
    'approval.loading_book': 'Buch wird geladen...',
    'approval.request_not_found': 'Genehmigungsanfrage nicht gefunden',
    'approval.why_approved': 'Warum genehmigt',
    'approval.send_back_for_improvement': 'Zur Überarbeitung zurücksenden',
    'approval.pdf_not_available': 'PDF nicht verfügbar',
    'approval.pdf_not_available_desc': 'Dieses Buch hat noch kein anzeigbares PDF.',
    'approval.book_preview': 'Buchvorschau',
    'approval.replacement_preview_hint': 'Ersatz-PDF — aktuell veröffentlichte Version bleibt bis zur Genehmigung online',
    'approval.loading_preview': 'Vorschau wird geladen...',
    'approval.book_not_found': 'Buch nicht gefunden',
    'approval.note_from_sender': 'Notiz vom Absender',
    'approval.publish_now_desc': 'Dieses Buch ist bereits für Stufe 2 genehmigt. Durch die Veröffentlichung ist es sofort in der Bibliothek verfügbar.',
    'action.send_delete_l2': 'Zur L2-Löschung senden',
    'action.send_for': 'Senden für',
    'action.back_to_list': 'Zurück zur Liste',

    // Grades & Standards
    'grade.nursery': 'Kindergarten',
    'grade.lkg': 'LKG',
    'grade.ukg': 'UKG',
    'grade.grade_prefix': 'Klasse',

    // Common Subjects
    'subject.mathematics': 'Mathematik',
    'subject.economics': 'Wirtschaft',
    'subject.science': 'Wissenschaft',
    'subject.english': 'Englisch',
    'subject.hindi': 'Hindi',
    'subject.gujarati': 'Gujarati',
    'subject.marathi': 'Marathi',
    'subject.physics': 'Physik',
    'subject.chemistry': 'Chemie',
    'subject.biology': 'Biologie',
    'subject.history': 'Geschichte',
    'subject.geography': 'Geografie',
    'subject.computer_science': 'Informatik',

    // Organizations Modals & Actions
    'org.create_organization': 'Neue Organisation erstellen',
    'org.edit_organization': 'Organisation bearbeiten',
    'org.configure_desc': 'Organisationsdetails, Farben und Logo konfigurieren',
    'org.activate_organization': 'Organisation aktivieren',
    'org.deactivate_organization': 'Organisation deaktivieren',

    // Book Upload & Edit
    'action.submit_for_approval': 'Zur Genehmigung Einreichen',
    'action.save_changes': 'Änderungen Speichern',
    'upload.page_title': 'Neues Buch Hochladen',
    'upload.edit_page_title': 'Buch Bearbeiten',
    'upload.subtitle': 'Konvertieren Sie eine PDF in ein interaktives HTML5-Flipbook',
    'upload.edit_subtitle': 'Buchdetails aktualisieren oder PDF-Quelle ersetzen',
    'upload.pdf_source_file': 'PDF-Quelldatei',
    'upload.optional_replace': 'Optional — nur zum Ersetzen hochladen',
    'upload.drop_pdf': 'Ziehen Sie Ihre PDF hierher oder',
    'upload.browse': 'durchsuchen',
    'upload.pdf_limit': 'Nur PDF-Dateien — max. {size} MB pro Upload',
    'upload.thumbnail_title': 'Buch-Miniaturansicht',
    'upload.drop_image': 'Ziehen Sie Ihr Bild hierher oder',
    'upload.image_limit': 'JPG, PNG oder WebP — max. 2 MB · leer lassen für PDF-Seite 1',
    'upload.current_thumbnail': 'Aktuelle Miniaturansicht in Datei',
    'upload.replace_image': 'Bild ersetzen',
    'upload.replace_pdf': 'PDF ersetzen',
    'upload.org_title': 'Organisation',
    'upload.org_edit_desc': 'Organisation kann nach der Erstellung nicht mehr geändert werden.',
    'upload.org_desc': 'Wählen Sie die Organisation für dieses Buch aus.',
    'upload.book_info': 'Buchinformationen',
    'upload.book_name': 'Buchname',
    'upload.book_name_desc': 'Verwenden Sie den vollständigen offiziellen Titel wie im Flipbook-Viewer',
    'upload.book_name_placeholder': 'z.B. Mathematics Foundations — Grade 5, Term 1',
    'upload.publisher': 'Verlag',
    'upload.publisher_desc': 'Verlag oder Inhaltsquelle für dieses Buch',
    'upload.grade': 'Klasse / Stufe',
    'upload.grade_desc': 'Zielklasse für dieses Buch',
    'upload.subject': 'Fach',
    'upload.subject_desc': 'Kategorie des Schulfachs',
    'upload.expiry_date': 'Ablaufdatum',
    'upload.expiry_date_desc': 'Das Flipbook ist nach diesem Datum nicht mehr zugänglich.',
    'upload.description': 'Beschreibung',
    'upload.description_desc': 'Kurze Zusammenfassung vor der Zugriffsanfrage',
    'upload.description_placeholder': 'Beschreiben Sie Buchinhalt, Zielgruppe und Hauptthemen...',
    'upload.access_control': 'Zugriffskontrolle',
    'upload.access_notes': 'Zugriffsnotizen',
    'upload.access_notes_desc': 'Interne Notizen zu Zugriffsberechtigungen (nicht öffentlich sichtbar)',
    'upload.approval_banner_title': '2-Stufen-Genehmigung erforderlich',
    'upload.approval_banner_desc': 'Durch das Einreichen zur Genehmigung wird dieses Buch an alle Level-1-Genehmiger gesendet. Nach einer Level-1-Genehmigung geht es an Level 2. Sobald Level 2 genehmigt, wird das Buch veröffentlicht.',
    'upload.submitted_notice': 'Eingereichte Bücher gehen an alle Level-1-Genehmiger, dann Level 2',
    'upload.live_notice': 'Die veröffentlichte Version bleibt live, bis Level 2 das neue PDF genehmigt',
    'upload.save_notice': 'Metadaten jederzeit speichern. Das Ersetzen des PDFs erfordert Genehmigung.',

    'col.book_title': 'Buchtitel',
    'col.grade': 'Klasse',
    'col.subject': 'Fach',
    'col.status': 'Status',
    'col.pages': 'Seiten',
    'col.uploaded': 'Hochgeladen am',
    'col.uploaded_by': 'Hochgeladen von',
    'col.expiry': 'Ablaufdatum',
    'col.views': 'Aufrufe',
    'col.link': 'Link',
    'col.name': 'Name',
    'col.email': 'E-Mail',
    'col.role': 'Rolle',
    'col.organization': 'Organisation',
    'col.permissions': 'Berechtigungen',
    'col.created_date': 'Erstellungsdatum',
    'col.last_login': 'Letzter Login',
    'col.ip_address': 'IP-Adresse',
    'col.device': 'Gerät',
    'col.action': 'Aktion',
    'col.resource': 'Ressource',
    'col.timestamp': 'Zeitstempel',
    'col.details': 'Details',
    'col.order': 'Reihenfolge',
    'col.requested_by': 'Beantragt von',
    'col.sent_at': 'Gesendet am',
    'col.level': 'Stufe',
    'col.approval': 'Genehmigung',

    'dashboard.title': 'Dashboard',
    'dashboard.total_books': 'Bücher insgesamt',
    'dashboard.total_users': 'Benutzer insgesamt',
    'dashboard.total_views': 'Buchaufrufe insgesamt',
    'dashboard.active_users': 'Aktive Benutzer',
    'dashboard.avg_read_time': 'Durchschn. Lesezeit',
    'dashboard.active_flipbooks': 'Aktive Flipbooks',
    'dashboard.expired_today': 'Heute abgelaufen',
    'dashboard.otp_generated': 'OTP-Anfragen (24 Std.)',
    'dashboard.processing_queue': 'Verarbeitungswarteschlange',
    'dashboard.storage_used': 'Verwendeter PDF-Speicher',
    'dashboard.published_books': 'Veröffentlichte Bücher',
    'dashboard.draft_books': 'Entwürfe',
    'dashboard.expired_books': 'Abgelaufene Bücher',
    'dashboard.recent_activity': 'Letzte Aktivität',
    'dashboard.top_performing': 'Beliebteste Bücher',
    'dashboard.views_trend': 'Aufruftrend',
    'dashboard.books_by_grade': 'Bücher nach Klasse',
    'dashboard.books_by_subject': 'Bücher nach Fach',
    'dashboard.trends_distribution': 'Trends & Verteilung',
    'dashboard.otp_trend': 'OTP-Anfragetrend',
    'dashboard.otp_trend_sub': 'Letzte 14 Tage · Flipbook-Zugriff',
    'dashboard.books_by_grade_sub': 'Verteilung über alle Klassen',
    'dashboard.latest_events': 'Neueste Bibliotheksereignisse',
    'dashboard.today': 'Heute',
    'dashboard.distribution_subjects': 'Verteilung nach Fächern',
    'dashboard.analytics_heading': 'Analysen — Aufrufe, OTPs & Benutzer',
    'dashboard.monthly_activity_trend': 'Monatlicher Aktivitätstrend',
    'dashboard.views_by_subject': 'Aufrufe nach Fach',
    'dashboard.books_by_grade_group': 'Bücher nach Klassengruppen',
    'dashboard.unique_otp_users': 'Eindeutige OTP-Benutzer',
    'dashboard.this_month': 'diesen Monat',
    'dashboard.no_recent_activity': 'Noch keine aktuellen Aktivitäten',
    'dashboard.no_subject_data': 'Keine Fachdaten vorhanden',
    'dashboard.no_grade_data': 'Keine Klassendaten vorhanden',
    'dashboard.no_books_yet': 'Noch keine Bücher vorhanden',
    'dashboard.page_title': 'Dashboard & Analysen',
    'dashboard.main_subtitle': 'Bibliotheksübersicht, Aktivitätsmetriken und Nutzungsanalysen',
    'dashboard.needs_renewal': 'Erneuerung erforderlich',
    'dashboard.none_today': 'Heute keine',
    'dashboard.vs_previous_24h': 'ggü. letzten 24 Std.',
    'dashboard.pdf_processing_pending': 'PDF-Verarbeitung ausstehend',
    'dashboard.queue_clear': 'Warteschlange leer',
    'dashboard.from_uploaded_books': 'Aus hochgeladenen Buchdateien',
    'dashboard.all_time': 'Gesamtzeit',
    'dashboard.last_7_days': 'Letzte 7 Tage',
    'dashboard.per_session': 'Pro Sitzung',
    'dashboard.monthly_activity_sub': 'Buchansichten, Zugriffs-OTPs und aktive Benutzer',
    'dashboard.grades_count': 'Klassen',
    'dashboard.grade_single': 'Klasse',
    'dashboard.otp_requests': 'OTP-Anfragen',
    'dashboard.books_count': 'Bücher',
    'dashboard.chart_views': 'Ansichten',
    'dashboard.chart_otps': 'OTPs',
    'dashboard.chart_users': 'Benutzer',
    'dashboard.chart_share': 'Anteil',
    'dashboard.action_required': 'Aktion',
    'dashboard.activity_upload': 'Buch hochgeladen',
    'dashboard.activity_otp': 'Zugriffs-OTP',
    'dashboard.activity_expired': 'Buch abgelaufen',
    'dashboard.activity_view': 'Buchansicht',
    'header.global_overview_desc': 'Globale Übersicht und aggregierte Daten',
    'header.all_assigned_orgs_desc': 'Alle zugewiesenen Organisationen und aggregierte Daten',
    'date.just_now': 'Gerade eben',
    'date.yesterday': 'Gestern',
    'date.m_ago': 'Min. her',
    'date.h_ago': 'Std. her',
    'date.d_ago': 'Tage her',


    'history.visitors': 'Besucher',
    'history.view_events': 'Aufruf-Ereignisse',
    'history.read_sessions': 'Lesesitzungen',
    'history.otp_history': 'OTP-Verlauf',
    'history.reader_user': 'Leser / Benutzer',
    'history.total_read_time': 'Gesamte Lesezeit',
    'history.first_access': 'Erster Zugriff',
    'history.last_access': 'Letzter Zugriff',
    'history.latest_ip': 'Neueste IP',
    'history.books_accessed': 'Aufgerufene Bücher',

    'settings.title': 'Einstellungen',
    'settings.subtitle': 'Verwalten Sie Ihre Einstellungen und Systemeinstellungen',
    'settings.tab_general': 'Allgemeine Einstellungen',
    'settings.tab_notifications': 'Benachrichtigungseinstellungen',
    'settings.tab_security': 'Sicherheitseinstellungen',
    'settings.tab_flipbook': 'Flipbook-Einstellungen',
    'settings.tab_email': 'E-Mail-Einstellungen',
    'settings.interface_language': 'Benutzeroberflächensprache',
    'settings.interface_language_desc': 'Wählen Sie die Anzeigesprache für die Benutzeroberfläche',
    'settings.timezone': 'Zeitzone',
    'settings.timezone_desc': 'Datums- und Zeitangaben werden in der gewählten Zeitzone angezeigt',
    'settings.admin_email': 'Administrator-E-Mail',
    'settings.admin_email_desc': 'Hauptkontakt-E-Mail für Systembenachrichtigungen',
    'settings.organization_name': 'Organisationsname',
    'settings.save_success': 'Einstellungen erfolgreich gespeichert',

    'reader.loading_flipbook': 'Flipbook wird geladen…',
    'reader.loading_page': 'Seite wird geladen...',
    'reader.read_book': 'Buch lesen',
    'reader.thumbnails': 'Vorschaubilder',
    'reader.search': 'Im Buch suchen',
    'reader.zoom_in': 'Vergrößern',
    'reader.zoom_out': 'Verkleinern',
    'reader.fullscreen': 'Vollbild',
    'reader.exit_fullscreen': 'Vollbild beenden',
    'reader.sound_on': 'Ton an',
    'reader.sound_off': 'Ton aus',
    'reader.auto_play': 'Automatische Wiedergabe',
    'reader.pause': 'Pause',
    'reader.bookmarks': 'Lesezeichen',
    'reader.add_bookmark': 'Lesezeichen setzen',
    'reader.remove_bookmark': 'Lesezeichen entfernen',
    'reader.page_of': 'von',
    'reader.protected_document': 'Geschütztes Dokument',

    // Session Expiry
    'session.expired_title': 'Sitzung abgelaufen',
    'session.expired_desc': 'Ihre Sitzung ist aufgrund von Inaktivität abgelaufen. Bitte melden Sie sich erneut an, um fortzufahren.',
    'session.sign_in_again': 'Erneut anmelden',
  },
  ar: {
    'nav.dashboard': 'لوحة التحكم',
    'nav.flipbook_library': 'مكتبة الكتب التفاعلية',
    'nav.access_history': 'سجل الوصول',
    'nav.books_approval': 'الموافقة على الكتب',
    'nav.organizations': 'المؤسسات',
    'nav.user_management': 'إدارة المستخدمين',
    'nav.access_logs': 'سجلات الدخول',
    'nav.masters': 'البيانات الرئيسية',
    'nav.subject_master': 'المواد الدراسية',
    'nav.grade_master': 'الصفوف الدراسية',
    'nav.settings': 'الإعدادات',

    'library.subtitle': 'إدارة وعرض جميع الكتب الرقمية',
    'dashboard.subtitle': 'نظرة عامة على الكتب الرقمية وتفاعل القراء',
    'access_history.subtitle': 'عرض سجلات جلسات القراء وأحداث الوصول',
    'books_approval.subtitle': 'مراجعة ومعالجة موافقات الكتب المقدمة',
    'organizations.subtitle': 'إدارة المؤسسات والسمات والصلاحيات',
    'user_management.subtitle': 'إدارة حسابات المستخدمين والأدوار',
    'access_logs.subtitle': 'مراقبة وتدقيق أنشطة المستخدمين',
    'masters.subjects_subtitle': 'إدارة المواد الدراسية للكتب',
    'masters.grades_subtitle': 'إدارة الصفوف والمراحل الدراسية',
    'masters.add_subject': 'إضافة مادة',
    'masters.edit_subject': 'تعديل المادة',
    'masters.create_subject': 'إنشاء مادة',
    'masters.update_subject': 'تحديث المادة',
    'masters.delete_subject_title': 'حذف المادة؟',
    'masters.delete_subject_desc': 'سيؤدي هذا إلى حذف نهائي لـ',
    'masters.delete_subject_hint': 'لا يمكن حذف المواد المرتبطة بكتب؛ يرجى تعطيلها بدلاً من ذلك.',
    'masters.subject_name': 'اسم المادة',
    'masters.enter_subject_name': 'أدخل اسم المادة',
    'masters.subject_active_desc': 'متاح عند اختيار مادة لكتاب',
    'masters.subject_inactive_desc': 'مخفي من اختيار مادة الكتاب',
    'masters.no_subjects_found': 'لم يتم العثور على مواد',
    'masters.loading_subjects': 'جارٍ تحميل المواد...',
    'masters.try_different_search': 'يرجى تجربة مصطلح بحث مختلف.',
    'masters.add_first_subject': 'أضف مادتك الأولى للبدء.',
    'masters.subject_name_required': 'اسم المادة مطلوب',
    'masters.subject_created_success': 'تم إنشاء المادة بنجاح',
    'masters.subject_updated_success': 'تم تحديث المادة بنجاح',
    'masters.subject_deleted_success': 'تم حذف المادة بنجاح',
    'masters.failed_load_subjects': 'فشل تحميل المواد',
    'masters.failed_save_subject': 'فشل حفظ المادة',
    'masters.failed_delete_subject': 'فشل حذف المادة',
    'masters.add_grade': 'إضافة صف',
    'masters.edit_grade': 'تعديل الصف',
    'masters.create_grade': 'إنشاء صف',
    'masters.update_grade': 'تحديث الصف',
    'masters.delete_grade_title': 'حذف الصف؟',
    'masters.delete_grade_desc': 'سيؤدي هذا إلى حذف نهائي لـ',
    'masters.delete_grade_hint': 'لا يمكن حذف الصفوف المرتبطة بكتب؛ يرجى تعطيلها بدلاً من ذلك.',
    'masters.grade_name': 'اسم الصف',
    'masters.enter_grade_name': 'أدخل اسم الصف',
    'masters.grade_active_desc': 'متاح عند اختيار صف لكتاب',
    'masters.grade_inactive_desc': 'مخفي من اختيار صف الكتاب',
    'masters.no_grades_found': 'لم يتم العثور على صفوف',
    'masters.loading_grades': 'جارٍ تحميل الصفوف...',
    'masters.add_first_grade': 'أضف صفك الأول للبدء.',
    'masters.grade_name_required': 'اسم الصف مطلوب',
    'masters.grade_created_success': 'تم إنشاء الصف بنجاح',
    'masters.grade_updated_success': 'تم تحديث الصف بنجاح',
    'masters.grade_deleted_success': 'تم حذف الصف بنجاح',
    'masters.failed_load_grades': 'فشل تحميل الصفوف',
    'masters.failed_save_grade': 'فشل حفظ الصف',
    'masters.failed_delete_grade': 'فشل حذف الصف',
    'masters.order_already_used': 'الترتيب مستخدم بالفعل. يرجى اختيار ترتيب آخر.',
    'filter.search_subjects': 'البحث عن المواد...',
    'filter.search_grades': 'البحث عن الصفوف...',
    'action.deleting': 'جارٍ الحذف...',

    // Roles
    'role.super_admin': 'المسؤول العام',
    'role.admin': 'مسؤول',
    'role.admins': 'المسؤولون',
    'role.school_admin': 'مسؤول المدرسة',
    'role.approver_l1': 'المعتمد L1',
    'role.approver_l2': 'المعتمد L2',
    'role.publish_approver_l1': 'معتمد النشر المستوى 1',
    'role.publish_approver_l2': 'معتمد النشر المستوى 2',
    'role.delete_approver_l1': 'معتمد الحذف المستوى 1',
    'role.delete_approver_l2': 'معتمد الحذف المستوى 2',
    'role.content_creator': 'منشئ المحتوى',
    'role.reader': 'قارئ',
    'role.student': 'طالب',
    'role.teacher': 'معلم',

    // User Management Modals & Tables
    'user_management.admins': 'المسؤولون',
    'user_management.delete_user': 'حذف المستخدم',
    'user_management.delete_user_desc': 'هل أنت متأكد من رغبتك في حذف',
    'user_management.cannot_undone': 'لا يمكن التراجع عن هذا الإجراء.',
    'user_management.add_new_user': 'إضافة مستخدم جديد',
    'user_management.edit_user': 'تعديل المستخدم',
    'user_management.create_user_desc': 'إنشاء حساب مستخدم جديد وتعيين صلاحيات الوصول',
    'user_management.edit_user_desc': 'تحديث بيانات المستخدم وصلاحيات الوصول',
    'user_management.basic_info': 'المعلومات الأساسية',
    'user_management.full_name': 'الاسم الكامل',
    'user_management.email_address': 'البريد الإلكتروني',
    'user_management.password': 'كلمة المرور',
    'user_management.new_password_keep': 'كلمة المرور الجديدة (اتركه فارغاً للاحتفاظ بالحالية)',
    'user_management.grade_optional': 'الصف / الفصل (اختياري)',
    'user_management.not_applicable': 'لا ينطبق',
    'user_management.organizations_assignment': 'تعيين المؤسسات',
    'user_management.module_access': 'الوصول إلى الوحدات',
    'user_management.module_access_desc': 'الافتراضيات حسب الدور — التخصيص حسب الحاجة',
    'user_management.force_login': 'تسجيل دخول مفروض',
    'user_management.otp_history': 'سجل OTP',
    'user_management.all_orgs': 'جميع المؤسسات',
    'user_management.none': 'لا يوجد',

    // Access Log Actions & Groups
    'log.group_user': 'المستخدم',
    'log.group_books': 'الكتب',
    'log.group_other': 'أخرى',
    'log.no_logs_found': 'لم يتم العثور على سجلات.',
    'log.force_login_by': 'تسجيل دخول مفروض بواسطة',
    'log.performed_by': 'تم بواسطة',
    'log.target': 'الهدف',
    'log.filter_by_action': 'تصفية حسب الإجراء',
    'log.action_login': 'تسجيل الدخول',
    'log.action_logout': 'تسجيل الخروج',
    'log.action_generate_login_otp': 'إنشاء رمز الدخول',
    'log.action_verify_login_otp': 'التحقق من رمز الدخول',
    'log.action_generate_reset_otp': 'إنشاء رمز الاستعادة',
    'log.action_verify_reset_otp': 'التحقق من رمز الاستعادة',
    'log.action_reset_password': 'إعادة تعيين كلمة المرور',
    'log.action_view_book': 'عرض الكتاب',
    'log.action_generate_otp': 'إنشاء OTP',
    'log.action_verify_otp': 'التحقق من OTP',
    'log.action_upload_book': 'رفع الكتاب',
    'log.action_update_book': 'تحديث الكتاب',
    'log.action_publish_book': 'نشر الكتاب',
    'log.action_delete_book': 'حذف الكتاب',
    'log.action_toggle_otp': 'تبديل OTP',
    'log.action_add_access_user': 'إضافة مستخدم وصول',
    'log.action_remove_access_user': 'إزالة مستخدم وصول',
    'log.action_create_user': 'إنشاء مستخدم',
    'log.action_update_user': 'تحديث مستخدم',
    'log.action_delete_user': 'حذف مستخدم',
    'log.action_activate_version': 'تنشيط الإصدار',
    'log.action_create_qr': 'إنشاء QR',
    'log.action_toggle_qr': 'تبديل QR',
    'log.action_delete_qr': 'حذف QR',
    'log.action_scan_qr': 'مسح QR',

    'header.search_placeholder': 'البحث عن كتب، مواد، صفوف...',
    'header.switch_organization': 'تبديل المؤسسة',
    'header.all_organizations': 'جميع المؤسسات',
    'header.global_overview': 'نظرة عامة شاملة',
    'header.all_assigned_orgs': 'جميع المؤسسات المعينة',
    'header.profile': 'ملفي الشخصي',
    'header.logout': 'تسجيل الخروج',
    'header.super_admin': 'المسؤول العام',
    'header.admin': 'مسؤول',
    'header.notifications': 'الإشعارات',
    'notifications.new': 'جديد',
    'notifications.all': 'الكل',
    'notifications.unread': 'غير مقروء',
    'notifications.approvals': 'الموافقات',
    'notifications.books': 'الكتب',
    'notifications.alerts': 'التنبيهات',
    'notifications.mark_all_read': 'تحديد الكل كمقروء',
    'notifications.clear_read': 'مسح المقروء',
    'notifications.view_approvals': 'عرض جميع الموافقات',
    'notifications.empty': 'لا توجد إشعارات',
    'notifications.caught_up': 'أنت على اطلاع بكل جديد!',
    'notifications.loading': 'جاري تحميل الإشعارات...',

    'action.search': 'بحث',
    'action.filter': 'تصفية',
    'action.reset': 'إعادة ضبط',
    'action.clear_all': 'مسح الكل',
    'action.clear_filters': 'مسح الفلاتر',
    'action.save': 'حفظ التغييرات',
    'action.saving': 'جارٍ الحفظ...',
    'action.cancel': 'إلغاء',
    'action.edit': 'تعديل',
    'action.delete': 'حذف',
    'action.actions': 'إجراءات',
    'action.upload': 'رفع',
    'action.upload_book': 'رفع كتاب',
    'action.add_user': 'إضافة مستخدم',
    'action.add_organization': 'إضافة مؤسسة',
    'action.create_organization': 'إنشاء مؤسسة',
    'action.add_subject': 'إضافة مادة',
    'action.add_grade': 'إضافة صف',
    'action.create': 'إنشاء',
    'action.status': 'الحالة',
    'action.back': 'رجوع',
    'action.confirm': 'تأكيد',
    'action.close': 'إغلاق',
    'action.send': 'إرسال',
    'action.sending': 'جارٍ الإرسال...',
    'action.copy_link': 'نسخ الرابط',
    'action.copy': 'نسخ',
    'action.copied': 'تم النسخ!',
    'action.view': 'عرض',
    'action.preview': 'معاينة',
    'action.approve': 'موافقة',
    'action.approving': 'جارٍ الموافقة...',
    'action.send_back': 'إعادة للتعديل',
    'action.reject': 'رفض',
    'action.history': 'السجل',
    'action.versions': 'الإصدارات',
    'action.version_history': 'سجل الإصدارات',
    'action.replace': 'استبدال',
    'action.replace_book': 'استبدال الكتاب',
    'action.qr_code': 'رمز QR',
    'action.download_qr': 'تحميل رمز QR',
    'action.download_pdf': 'تحميل PDF',
    'action.refresh': 'تحديث',
    'action.export': 'تصدير',
    'action.export_csv': 'تصدير CSV',
    'action.import': 'استيراد',
    'action.view_all': 'عرض الكل',
    'action.show_more': 'إظهار المزيد',
    'action.collapse': 'طي',
    'action.expand': 'توسيع',
    'action.submit_l2': 'تقديم للمستوى الثاني',
    'action.publish': 'نشر',
    'action.publishing': 'جارٍ النشر...',
    'action.activate': 'تنشيط',
    'action.deactivate': 'تعطيل',
    'action.archive': 'أرشفة',
    'action.edit_book': 'تعديل الكتاب',
    'action.delete_book': 'حذف الكتاب',
    'action.request_delete': 'طلب الحذف',
    'action.no_books_found': 'لم يتم العثور على كتب',
    'action.try_adjusting_filters': 'يرجى تعديل معايير البحث أو التصفية',
    'action.upload_first_book': 'ارفع كتابك الأول',
    'action.select_all': 'تحديد الكل',
    'action.clear_selection': 'مسح التحديد',
    'action.by': 'بواسطة',

    'pagination.showing': 'عرض',
    'pagination.of': 'من',
    'pagination.rows_per_page': 'صفوف لكل صفحة',
    'pagination.previous_page': 'الصفحة السابقة',
    'pagination.next_page': 'الصفحة التالية',
    'pagination.books': 'كتب',
    'pagination.logs': 'سجلات',
    'pagination.records': 'سجلات',
    'pagination.entries': 'مدخلات',
    'pagination.items': 'عناصر',
    'pagination.prev': 'السابق',
    'pagination.next': 'التالي',

    'filter.all_statuses': 'جميع الحالات',
    'filter.all_grades': 'جميع الصفوف',
    'filter.all_subjects': 'جميع المواد',
    'filter.all_roles': 'جميع الأدوار',
    'filter.all_actions': 'جميع الإجراءات',
    'filter.search_books': 'البحث بالاسم، المادة...',
    'filter.search_users': 'البحث بالاسم، البريد...',
    'filter.search_logs': 'البحث في السجلات...',
    'filter.search_organizations': 'البحث عن المؤسسات...',
    'filter.search_reviewers': 'البحث عن المراجعين...',
    'filter.search_approvals': 'البحث بالاسم، مقدم الطلب...',

    'status.active': 'نشط',
    'status.inactive': 'غير نشط',
    'status.draft': 'مسودة',
    'status.published': 'تم النشر',
    'status.processing': 'قيد المعالجة',
    'status.pending_approval': 'قيد الموافقة',
    'status.pending_l1': 'المستوى 1 قيد الموافقة',
    'status.pending': 'معلق',
    'status.ready_for_l2': 'جاهز للمستوى 2',
    'status.awaiting_l2': 'بانتظار موافقة المستوى الثاني',
    'status.pending_l2': 'المستوى 2 قيد الموافقة',
    'status.approved': 'تمت الموافقة',
    'status.pending_publish': 'قيد النشر',
    'status.archived': 'مؤرشف',
    'status.expired': 'منتهي الصلاحية',
    'status.rejected': 'معاد للتعديل',
    'status.cancelled': 'ملغى',
    'status.success': 'ناجح',
    'status.failed': 'فاشل',
    'status.all': 'الكل',

    // Approvals, Reviews & Reviewers
    'approval.total_requests': 'إجمالي الطلبات',
    'approval.book_approval': 'موافقة الكتب',
    'approval.delete_requests': 'طلبات الحذف',
    'approval.level_1': 'المستوى 1',
    'approval.level_2': 'المستوى 2',
    'approval.approval': 'موافقة',
    'approval.reviewers_l1': 'مراجعو المستوى 1',
    'approval.reviewers_l2': 'مراجعو المستوى 2',
    'approval.delete_reviewers_l1': 'مراجعو الحذف المستوى 1',
    'approval.delete_reviewers_l2': 'مراجعو الحذف المستوى 2',
    'approval.replacement_reviewers_l1': 'مراجعو الاستبدال المستوى 1',
    'approval.replacement_reviewers_l2': 'مراجعو الاستبدال المستوى 2',
    'approval.publishers': 'الناشرون',
    'approval.responsible_persons': 'المسؤولون',
    'approval.selected': 'محدد',
    'approval.no_reviewers_found': 'لم يتم العثور على مراجعين',
    'approval.no_matches': 'لا توجد تطابقات',
    'approval.select_l1_desc': 'حدد معتمدي المستوى 1. أي موافقة واحدة تنقل هذا الكتاب إلى المستوى 2.',
    'approval.select_l2_desc': 'حدد معتمدي المستوى 2. أي موافقة واحدة تنشر هذا الكتاب تلقائياً.',
    'approval.sent_back_for_improvement': 'أعيد للتحسين',
    'approval.no_feedback_note': 'لم يتم تقديم أي ملاحظات. استبدل ملف PDF أو عدل التفاصيل، ثم ستتم إعادة تعيينه تلقائياً.',
    'approval.delete_pending': 'الحذف معلق',
    'approval.ready_for_l2_delete': 'جاهز لحذف المستوى 2',
    'approval.replacement_pending': 'الاستبدال معلق',
    'approval.replacement': 'استبدال',
    'approval.no_approvals': 'لا توجد موافقات معلقة',
    'approval.no_delete_requests': 'لا توجد طلبات حذف',
    'approval.review_book': 'مراجعة الكتاب',
    'approval.review_book_subtitle': 'عرض الكتاب التفاعلي، ثم الموافقة أو الرفض',
    'approval.loading_book': 'جارٍ تحميل الكتاب...',
    'approval.request_not_found': 'طلب الموافقة غير موجود',
    'approval.why_approved': 'سبب الموافقة',
    'approval.send_back_for_improvement': 'إعادة للتحسين',
    'approval.pdf_not_available': 'ملف PDF غير متوفر',
    'approval.pdf_not_available_desc': 'هذا الكتاب لا يحتوي على ملف PDF قابل للعرض بعد.',
    'approval.book_preview': 'معاينة الكتاب',
    'approval.replacement_preview_hint': 'ملف PDF بديل — تبقى النسخة الحالية منشورة حتى تتم الموافقة',
    'approval.loading_preview': 'جارٍ تحميل المعاينة...',
    'approval.book_not_found': 'الكتاب غير موجود',
    'approval.note_from_sender': 'ملاحظة من المرسل',
    'approval.publish_now_desc': 'تمت الموافقة على هذا الكتاب للمستوى 2 مسبقاً. نشره سيجعله متاحاً في المكتبة فوراً.',
    'action.send_delete_l2': 'إرسال لحذف المستوى 2',
    'action.send_for': 'إرسال لـ',
    'action.back_to_list': 'العودة إلى القائمة',

    // Grades & Standards
    'grade.nursery': 'الحضانة',
    'grade.lkg': 'LKG',
    'grade.ukg': 'UKG',
    'grade.grade_prefix': 'الصف',

    // Common Subjects
    'subject.mathematics': 'الرياضيات',
    'subject.economics': 'الاقتصاد',
    'subject.science': 'العلوم',
    'subject.english': 'الإنجليزية',
    'subject.hindi': 'الهندية',
    'subject.gujarati': 'الغوجاراتية',
    'subject.marathi': 'الماراثية',
    'subject.physics': 'الفيزياء',
    'subject.chemistry': 'الكيمياء',
    'subject.biology': 'علم الأحياء',
    'subject.history': 'التاريخ',
    'subject.geography': 'الجغرافيا',
    'subject.computer_science': 'علوم الحاسوب',

    // Organizations Modals & Actions
    'org.create_organization': 'إنشاء مؤسسة جديدة',
    'org.edit_organization': 'تعديل المؤسسة',
    'org.configure_desc': 'تكوين تفاصيل المؤسسة والألوان والشعار',
    'org.activate_organization': 'تفعيل المؤسسة',
    'org.deactivate_organization': 'إلغاء تفعيل المؤسسة',

    // Book Upload & Edit
    'action.submit_for_approval': 'إرسال للموافقة',
    'action.save_changes': 'حفظ التغييرات',
    'upload.page_title': 'رفع كتاب جديد',
    'upload.edit_page_title': 'تعديل الكتاب',
    'upload.subtitle': 'تحويل ملف PDF إلى دفتر رسوم متحركة تفاعلي بتقنية HTML5',
    'upload.edit_subtitle': 'تحديث تفاصيل الكتاب أو استبدال ملف PDF المصدر',
    'upload.pdf_source_file': 'ملف PDF المصدر',
    'upload.optional_replace': 'اختياري — الرفع فقط للاستبدال',
    'upload.drop_pdf': 'أسقط ملف PDF هنا، أو',
    'upload.browse': 'تصفح',
    'upload.pdf_limit': 'ملفات PDF فقط — {size} ميجابايت كحد أقصى لكل عملية رفع',
    'upload.thumbnail_title': 'صورة مصغرة للكتاب',
    'upload.drop_image': 'أسقط صورتك هنا، أو',
    'upload.image_limit': 'JPG أو PNG أو WebP — حد أقصى 2 ميجابايت · اتركه فارغاً لاستخدام الصفحة الأولى من PDF',
    'upload.current_thumbnail': 'الصورة المصغرة الحالية في الملف',
    'upload.replace_image': 'استبدال الصورة',
    'upload.replace_pdf': 'استبدال ملف PDF',
    'upload.org_title': 'المؤسسة',
    'upload.org_edit_desc': 'لا يمكن تغيير المؤسسة بعد إنشاء الكتاب.',
    'upload.org_desc': 'حدد المؤسسة التي ينتمي إليها هذا الكتاب.',
    'upload.book_info': 'معلومات الكتاب',
    'upload.book_name': 'اسم الكتاب',
    'upload.book_name_desc': 'استخدم العنوان الرسمي الكامل كما سيظهر في عارض الكتب التفاعلية',
    'upload.book_name_placeholder': 'مثال: أسس الرياضيات — الصف 5',
    'upload.publisher': 'الناشر',
    'upload.publisher_desc': 'دار النشر أو مصدر المحتوى لهذا الكتاب',
    'upload.grade': 'الصف الدراسي',
    'upload.grade_desc': 'الصف المستهدف لهذا الكتاب',
    'upload.subject': 'المادة الدراسية',
    'upload.subject_desc': 'فئة المادة الأكاديمية',
    'upload.expiry_date': 'تاريخ الانتهاء',
    'upload.expiry_date_desc': 'يصبح الكتاب التفاعلي غير متاح بعد هذا التاريخ.',
    'upload.description': 'الوصف',
    'upload.description_desc': 'ملخص موجز يظهر لأولياء الأمور والمدارس قبل طلب الوصول',
    'upload.description_placeholder': 'صف محتوى الكتاب والجمهور المستهدف والمواضيع الرئيسية...',
    'upload.access_control': 'التحكم في الوصول',
    'upload.access_notes': 'ملاحظات الوصول',
    'upload.access_notes_desc': 'ملاحظات داخلية حول من ينبغي أن يكون له حق الوصول (لا تظهر للمستخدمين)',
    'upload.approval_banner_title': 'مطلوب موافقة على مستويين',
    'upload.approval_banner_desc': 'يؤدي الإرسال للموافقة إلى إرسال هذا الكتاب إلى جميع معتمدي المستوى 1. وبعد موافقة أي معتمد، يتم إرساله تلقائياً إلى المستوى 2. وبمجرد موافقة المستوى 2، يُنشر الكتاب.',
    'upload.submitted_notice': 'تذهب الكتب المقدمة إلى جميع معتمدي المستوى 1، ثم المستوى 2',
    'upload.live_notice': 'تظل النسخة المنشورة الحالية متاحة حتى يوافق المستوى 2 على ملف PDF الجديد',
    'upload.save_notice': 'احفظ البيانات الوصفية في أي وقت. يتطلب استبدال ملف PDF موافقة.',

    'col.book_title': 'عنوان الكتاب',
    'col.grade': 'الصف',
    'col.subject': 'المادة',
    'col.status': 'الحالة',
    'col.pages': 'الصفحات',
    'col.uploaded': 'تاريخ الرفع',
    'col.uploaded_by': 'تم الرفع بواسطة',
    'col.expiry': 'تاريخ الانتهاء',
    'col.views': 'المشاهدات',
    'col.link': 'الرابط',
    'col.name': 'الاسم',
    'col.email': 'البريد الإلكتروني',
    'col.role': 'الدور',
    'col.organization': 'المؤسسة',
    'col.permissions': 'الصلاحيات',
    'col.created_date': 'تاريخ الإنشاء',
    'col.last_login': 'آخر تسجيل دخول',
    'col.ip_address': 'عنوان IP',
    'col.device': 'الجهاز',
    'col.action': 'الإجراء',
    'col.resource': 'المورد',
    'col.timestamp': 'الوقت',
    'col.details': 'التفاصيل',
    'col.order': 'الترتيب',
    'col.requested_by': 'مقدم الطلب',
    'col.sent_at': 'أرسل في',
    'col.level': 'المستوى',
    'col.approval': 'الموافقة',

    'dashboard.title': 'لوحة التحكم',
    'dashboard.total_books': 'إجمالي الكتب',
    'dashboard.total_users': 'إجمالي المستخدمين',
    'dashboard.total_views': 'إجمالي مشاهدات الكتب',
    'dashboard.active_users': 'المستخدمون النشطون',
    'dashboard.avg_read_time': 'متوسط وقت القراءة',
    'dashboard.active_flipbooks': 'الكتب التفاعلية النشطة',
    'dashboard.expired_today': 'المنتهية اليوم',
    'dashboard.otp_generated': 'طلبات OTP (24 ساعة)',
    'dashboard.processing_queue': 'طابور المعالجة',
    'dashboard.storage_used': 'مساحة PDF المستخدمة',
    'dashboard.published_books': 'الكتب المنشورة',
    'dashboard.draft_books': 'المسودات',
    'dashboard.expired_books': 'الكتب المنتهية',
    'dashboard.recent_activity': 'النشاط الأخير',
    'dashboard.top_performing': 'الكتب الأكثر قراءة',
    'dashboard.views_trend': 'اتجاه المشاهدات',
    'dashboard.books_by_grade': 'الكتب حسب الصف',
    'dashboard.books_by_subject': 'الكتب حسب المادة',
    'dashboard.trends_distribution': 'الاتجاهات والتوزيع',
    'dashboard.otp_trend': 'اتجاه طلبات OTP',
    'dashboard.otp_trend_sub': 'آخر 14 يومًا · وصول للكتب',
    'dashboard.books_by_grade_sub': 'التوزيع على جميع الصفوف',
    'dashboard.latest_events': 'أحدث الأحداث في المكتبة',
    'dashboard.today': 'اليوم',
    'dashboard.distribution_subjects': 'التوزيع حسب المواد',
    'dashboard.analytics_heading': 'التحليلات — المشاهدات، OTP والمستخدمين',
    'dashboard.monthly_activity_trend': 'اتجاه النشاط الشهري',
    'dashboard.views_by_subject': 'المشاهدات حسب المادة',
    'dashboard.books_by_grade_group': 'الكتب حسب مجموعات الصفوف',
    'dashboard.unique_otp_users': 'مستخدمو OTP الفريدون',
    'dashboard.this_month': 'هذا الشهر',
    'dashboard.no_recent_activity': 'لا يوجد نشاط حديث حتى الآن',
    'dashboard.no_subject_data': 'لا توجد بيانات مواد',
    'dashboard.no_grade_data': 'لا توجد بيانات صفوف',
    'dashboard.no_books_yet': 'لا توجد كتب بعد',
    'dashboard.page_title': 'لوحة التحكم والتحليلات',
    'dashboard.main_subtitle': 'نظرة عامة على المكتبة ومقاييس النشاط وتحليلات الاستخدام',
    'dashboard.needs_renewal': 'بحاجة للتجديد',
    'dashboard.none_today': 'لا شيء اليوم',
    'dashboard.vs_previous_24h': 'مقارنة بآخر 24 ساعة',
    'dashboard.pdf_processing_pending': 'معالجة PDF معلقة',
    'dashboard.queue_clear': 'قائمة الانتظار فارغة',
    'dashboard.from_uploaded_books': 'من ملفات الكتب المرفوعة',
    'dashboard.all_time': 'كل الوقت',
    'dashboard.last_7_days': 'آخر 7 أيام',
    'dashboard.per_session': 'لكل جلسة',
    'dashboard.monthly_activity_sub': 'مشاهدات الكتب، ورموز OTP للوصول، والمستخدمون النشطون',
    'dashboard.grades_count': 'صفوف',
    'dashboard.grade_single': 'صف',
    'dashboard.otp_requests': 'طلبات OTP',
    'dashboard.books_count': 'كتب',
    'dashboard.chart_views': 'مشاهدات',
    'dashboard.chart_otps': 'OTPs',
    'dashboard.chart_users': 'مستخدمين',
    'dashboard.chart_share': 'حصة',
    'dashboard.action_required': 'إجراء',
    'dashboard.activity_upload': 'تم رفع الكتاب',
    'dashboard.activity_otp': 'رمز OTP للوصول',
    'dashboard.activity_expired': 'انتهت صلاحية الكتاب',
    'dashboard.activity_view': 'مشاهدة الكتاب',
    'header.global_overview_desc': 'نظرة عامة عالمية وبيانات مجمعة',
    'header.all_assigned_orgs_desc': 'جميع المؤسسات المعينة وبيانات مجمعة',
    'date.just_now': 'الآن',
    'date.yesterday': 'أمس',
    'date.m_ago': 'منذ دقيقة',
    'date.h_ago': 'منذ ساعة',
    'date.d_ago': 'منذ يوم',


    'history.visitors': 'الزوار',
    'history.view_events': 'أحداث العرض',
    'history.read_sessions': 'جلسات القراءة',
    'history.otp_history': 'سجل OTP',
    'history.reader_user': 'القارئ / المستخدم',
    'history.total_read_time': 'إجمالي وقت القراءة',
    'history.first_access': 'أول وصول',
    'history.last_access': 'آخر وصول',
    'history.latest_ip': 'آخر عنوان IP',
    'history.books_accessed': 'الكتب التي تم الوصول إليها',

    'settings.title': 'الإعدادات',
    'settings.subtitle': 'إدارة تفضيلاتك الشخصية وإعدادات النظام',
    'settings.tab_general': 'الإعدادات العامة',
    'settings.tab_notifications': 'تفضيلات الإشعارات',
    'settings.tab_security': 'إعدادات الأمان',
    'settings.tab_flipbook': 'إعدادات الكتاب التفاعلي',
    'settings.tab_email': 'إعدادات البريد الإلكتروني',
    'settings.interface_language': 'لغة الواجهة',
    'settings.interface_language_desc': 'اختر لغة العرض لواجهة التطبيق',
    'settings.timezone': 'المنطقة الزمنية',
    'settings.timezone_desc': 'ستظهر التواريخ والأوقات في منطقتك الزمنية المحددة',
    'settings.admin_email': 'بريد المسؤول',
    'settings.admin_email_desc': 'البريد الرئيسي لتلقي إشعارات النظام',
    'settings.organization_name': 'اسم المؤسسة',
    'settings.save_success': 'تم حفظ الإعدادات بنجاح',

    'reader.loading_flipbook': 'جارٍ تحميل الكتاب…',
    'reader.loading_page': 'جارٍ تحميل الصفحة...',
    'reader.read_book': 'قراءة الكتاب',
    'reader.thumbnails': 'المصغرات',
    'reader.search': 'بحث في الكتاب',
    'reader.zoom_in': 'تكبير',
    'reader.zoom_out': 'تصغير',
    'reader.fullscreen': 'ملء الشاشة',
    'reader.exit_fullscreen': 'إنهاء ملء الشاشة',
    'reader.sound_on': 'تشغيل الصوت',
    'reader.sound_off': 'كتم الصوت',
    'reader.auto_play': 'تشغيل تلقائي',
    'reader.pause': 'إيقاف مؤقت',
    'reader.bookmarks': 'الإشارات المرجعية',
    'reader.add_bookmark': 'إضافة إشارة مرجعية',
    'reader.remove_bookmark': 'إزالة الإشارة المرجعية',
    'reader.page_of': 'من',
    'reader.protected_document': 'مستند محمي',

    // Session Expiry
    'session.expired_title': 'انتهت الجلسة',
    'session.expired_desc': 'انتهت جلستك بسبب عدم النشاط. يرجى تسجيل الدخول مرة أخرى للمتابعة.',
    'session.sign_in_again': 'تسجيل الدخول مجدداً',
  },
};

/**
 * Get active user language code from localStorage or default to 'en'
 */
export function getUserLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && stored in translations) {
      return stored as SupportedLanguage;
    }
  } catch {
    /* safe fallback */
  }
  return DEFAULT_LANGUAGE;
}

/**
 * Save user language code and dispatch change event
 */
export function setUserLanguage(lang: string): void {
  if (typeof window === 'undefined') return;
  const normalized = (lang || '').trim().toLowerCase();
  const validLang: SupportedLanguage = normalized in translations ? (normalized as SupportedLanguage) : DEFAULT_LANGUAGE;

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, validLang);
    document.documentElement.lang = validLang;
    const isRtl = validLang === 'ar';
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    window.dispatchEvent(
      new CustomEvent(LANGUAGE_CHANGE_EVENT, { detail: { language: validLang } })
    );
  } catch {
    /* safe */
  }
}

/**
 * Translate a key into the active or specified language with English fallback
 */
export function translate(key: string, customLang?: SupportedLanguage | null): string {
  const lang = customLang || getUserLanguage();
  const dict = translations[lang] || translations.en;
  if (dict && key in dict) {
    return dict[key];
  }
  if (translations.en && key in translations.en) {
    return translations.en[key];
  }
  return key;
}

/** Short alias for translate */
export const t = translate;
